import UserService from "./UserService";
import UtilityService from "./UtilityService";
import Kichiri from "./KichiriService";

import moment from "moment";
import ClientService from "./ClientService";
import PlaidService from "./PlaidService";
import { authedFetch, readJsonAnswer, COULD_NOT_REACH_THE_SERVER } from "./apiFetch";
import { CANONICAL } from "../constants/CanonicalCodes";
import TAX_CATEGORIES from "../constants/Taxes";


export const PERIOD_BASIS = {
	FULL_YEAR: "full_year",
	FIRST_YEAR: "first_year",
	YEAR_END_CHANGE: "year_end_change",
	FINAL_PERIOD: "final_period",
	INTERIM: "interim",
};

export const CUSTOM_PERIOD_LENGTH = "custom";

export const PERIOD_LENGTH_OPTIONS = [
	{ value: 12, label: "12 months" },
	{ value: 6, label: "6 months" },
	{ value: 3, label: "3 months" },
	{ value: 1, label: "1 month" },
	{ value: CUSTOM_PERIOD_LENGTH, label: "Custom start date" },
];

const LONGEST_LEGAL_TAX_YEAR_IN_DAYS = 371;
const SHORTEST_PERIOD_TREATED_AS_A_FULL_YEAR_IN_DAYS = 350;
const LONGEST_MONTHLY_PERIOD_IN_DAYS = 31;
const REGISTRY_FILE_NAME_LIMIT = 255;
const REQUEST_FAILED_MESSAGE = "The server rejected the request. Check the API log — if this API points at a database without the latest migration, the fiscal period columns are missing.";
const LONGEST_QUARTERLY_PERIOD_IN_DAYS = 92;

// What a bank writes in front of nearly every line. They say how the money moved, never
// who took it, so two rows sharing only these are not the same merchant.
const MONEY_MOVEMENT_WORDS = [
	"interac", "purchase", "payment", "pymt", "transfer", "etransfer", "sent", "received",
	"cheque", "check", "chq", "deposit", "withdrawal", "eft", "pos", "debit", "credit",
	"pre", "auth", "authorized", "automatic", "misc", "miscellaneous", "transaction",
	"from", "to", "the", "and", "for", "of", "at", "on", "in", "by", "with",
];

const derivePeriodTypeFromLength = ({ isChosenInterim, daysInPeriod }) => {
	if (!isChosenInterim) return "annual";
	if (daysInPeriod <= LONGEST_MONTHLY_PERIOD_IN_DAYS) return "monthly";
	if (daysInPeriod <= LONGEST_QUARTERLY_PERIOD_IN_DAYS) return "quarterly";
	return "interim";
};

export const REVIEW_STATUS_FILTERS = {
	NOT_APPROVED: "not_approved",
	APPROVED: "approved",
	HAS_ISSUE: "has_issue",
};

export const REVIEW_STATUS_FILTER_OPTIONS = [
	{ value: "", label: "All statuses" },
	{ value: REVIEW_STATUS_FILTERS.NOT_APPROVED, label: "Not yet approved" },
	{ value: REVIEW_STATUS_FILTERS.APPROVED, label: "Approved" },
	{ value: REVIEW_STATUS_FILTERS.HAS_ISSUE, label: "Has an issue" },
];

export const ARCHIVE_REASONS = {
	DUPLICATE: "duplicate",
	NOT_A_TRANSACTION: "not_a_transaction",
	PARSE_ERROR: "parse_error",
	WRONG_ACCOUNT: "wrong_account",
	STATEMENT_REMOVED: "statement_removed",
	OTHER: "other",
};

export const ARCHIVE_REASON_OPTIONS = [
	{ value: ARCHIVE_REASONS.DUPLICATE, label: "Duplicate of another row" },
	{ value: ARCHIVE_REASONS.NOT_A_TRANSACTION, label: "Not a transaction (summary or header line)" },
	{ value: ARCHIVE_REASONS.PARSE_ERROR, label: "Read incorrectly from the statement" },
	{ value: ARCHIVE_REASONS.WRONG_ACCOUNT, label: "Belongs to another account or entity" },
	{ value: ARCHIVE_REASONS.OTHER, label: "Other" },
];

const ARCHIVE_REASON_LABELS = ARCHIVE_REASON_OPTIONS.reduce((labels, option) => {
	labels[option.value] = option.label;
	return labels;
}, {});

// Set by the tool rather than picked by a person, so it is not in the options a
// preparer chooses from — but the Archived tab still has to be able to name it.
ARCHIVE_REASON_LABELS[ARCHIVE_REASONS.STATEMENT_REMOVED] = "Its bank statement was removed";

const buildDuplicateKey = ({ transaction }) => {
	const date = transaction.date || "";
	const description = String(transaction.description || "").trim().toLowerCase();
	const amount = Number(transaction.amount);
	if (!date || !description || isNaN(amount)) return "";
	return [date, description, amount.toFixed(2)].join("|");
};

const SUSPENSE_ACCOUNT_PATTERN = /suspense/i;
const DEFAULT_SUSPENSE_ACCOUNT_NAME = "9999 - Suspense";
const OPENING_BALANCE_TOLERANCE = 0.005;
const COULD_NOT_READ_STATEMENT = "Couldn't read that statement — check the file and try again.";

export const ISSUE_STATUSES = [
	{ value: "pending", label: "Pending" },
	{ value: "in_progress", label: "In Progress" },
	{ value: "resolved", label: "Resolved" },
];
const DEFAULT_ISSUE_STATUS = "pending";

const DATE_FORMATS = [
	"YYYY-MM-DD", "YYYY/MM/DD", "YYYYMMDD",
	"DD-MM-YYYY", "D-M-YYYY", "DD/MM/YYYY", "D/M/YYYY", "DD.MM.YYYY", "D.M.YYYY", "DD MM YYYY",
	"MM-DD-YYYY", "M-D-YYYY", "MM/DD/YYYY", "M/D/YYYY", "MM.DD.YYYY", "M.D.YYYY",
	"DD-MM-YY", "D-M-YY", "DD/MM/YY", "D/M/YY", "DD.MM.YY",
	"MM-DD-YY", "M-D-YY", "MM/DD/YY", "M/D/YY", "MM.DD.YY", "YY-MM-DD", "YY/MM/DD",
	"DD-MMM-YYYY", "D-MMM-YYYY", "DD MMM YYYY", "D MMM YYYY",
	"MMM DD, YYYY", "MMM DD YYYY", "MMM D, YYYY", "MMM D YYYY",
	"DD-MMM-YY", "D-MMM-YY", "DD MMM YY", "MMM DD, YY", "MMM DD YY",
	"DD MMMM YYYY", "D MMMM YYYY", "MMMM DD, YYYY", "MMMM DD YYYY",
	"MMMM D, YYYY", "MMMM D YYYY", "DD MMMM YY", "MMMM DD, YY", "MMMM DD YY",
];

const AMOUNT_TOLERANCE = 0.01;
const DATE_TOLERANCE_DAYS = 3;
const CROSS_CURRENCY_TOLERANCE_RATE = 0.03;

const CURRENCY_NAME_HINTS = [
	{ code: "USD", hints: ["usd", "us dollar", "u.s. dollar", "us$", "$us", "usdollar"] },
	{ code: "EUR", hints: ["eur", "euro", "€"] },
	{ code: "GBP", hints: ["gbp", "sterling", "£"] },
];


function isUnclassified(classification) {
	if (classification === null || classification === undefined) return true;
	let value = UtilityService.toSearchText(classification);
	return value === "" || value === "unclassified" || value === "uncategorized";
}

function stillNeedsAnAccount(transaction) {
	const settledAsATransferLeg = !!transaction.mirrors_transaction_id;
	if (settledAsATransferLeg) return false;

	const hasNoAccountYet = isUnclassified(transaction.classification);
	return hasNoAccountYet;
}

function normalizeDate(dateString) {
	if (!dateString) return null;

	let trimmed = dateString.trim();
	if (!trimmed) return null;

	let parsed = moment(trimmed, DATE_FORMATS, true);
	if (parsed.isValid()) return parsed.format("YYYY-MM-DD");

	let fallback = moment(trimmed);
	if (fallback.isValid()) return fallback.format("YYYY-MM-DD");

	return null;
}

function normalizeAmount(amountString) {
	if (amountString === null || amountString === undefined || amountString === "") return NaN;

	let cleaned = String(amountString).trim().replace(/\s/g, "");

	let isParenthesisNegative = /^\(.*\)$/.test(cleaned);
	cleaned = cleaned.replace(/[()]/g, "");
	cleaned = cleaned.replace(/[$£€¥₹₩₪]/g, "");
	cleaned = cleaned.trim();

	if (!cleaned) return NaN;

	if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(cleaned)) {
		cleaned = cleaned.replace(/\./g, "").replace(",", ".");
	} else {
		cleaned = cleaned.replace(/,/g, "");
	}

	let value = parseFloat(cleaned);
	if (isNaN(value)) return NaN;

	return isParenthesisNegative ? -Math.abs(value) : value;
}


function extractTaxRateFromString(taxString) {
	let str = String(taxString);
	let match = str.match(/[\d.]+/);
	if (!match) return null;

	let rate = parseFloat(match[0]);
	if (!isFinite(rate) || isNaN(rate)) return null;

	return rate;
}

function findSuspenseAccount(classifications) {
	for (let classification of classifications) {
		if (SUSPENSE_ACCOUNT_PATTERN.test(classification.name)) return classification;

		let accountNumber = classification.name.split(" - ")[0]?.trim();
		if (/^9{3,}/.test(accountNumber)) return classification;
	}
	return null;
}

function cadEquivalentAmount({ transaction }) {
	const cadAmount = Number(transaction.amount) || 0;
	return cadAmount;
}

function transferLegsMatchInCad({ transactionA, transactionB, allowCrossCurrency = true }) {
	const cadA = cadEquivalentAmount({ transaction: transactionA });
	const cadB = cadEquivalentAmount({ transaction: transactionB });
	if (Math.sign(cadA) === Math.sign(cadB)) return false;

	const isCrossCurrency = (transactionA.currency || "CAD") !== (transactionB.currency || "CAD");
	if (isCrossCurrency && !allowCrossCurrency) return false;

	const largerCadValue = Math.max(Math.abs(cadA), Math.abs(cadB));
	const allowedDifference = isCrossCurrency ? largerCadValue * CROSS_CURRENCY_TOLERANCE_RATE : AMOUNT_TOLERANCE;

	const cadDifference = Math.abs(cadA + cadB);
	return cadDifference <= allowedDifference;
}

function transferAccountName({ transaction }) {
	return transaction.bankAccountName || transaction.accountName || "";
}

function isTransferPairCandidate({ transaction }) {
	if (transaction.notATransfer === true) return false;

	const accountName = transferAccountName({ transaction });
	if (!accountName) return false;

	const amount = Number(transaction.amount);
	if (!amount || isNaN(amount)) return false;

	return true;
}

function buildTransferPairSummary({ transactionA, transactionB }) {
	const outLeg = transactionA.amount < 0 ? transactionA : transactionB;
	const inLeg = transactionA.amount < 0 ? transactionB : transactionA;
	const isCrossCurrency = (outLeg.currency || "CAD") !== (inLeg.currency || "CAD");
	const isApplied = !!inLeg.mirrors_transaction_id || !!outLeg.mirrors_transaction_id;
	const status = isApplied ? "applied" : "proposed";
	const cadValue = Math.abs(cadEquivalentAmount({ transaction: outLeg }));
	return { outLeg, inLeg, cadValue, isCrossCurrency, status };
}

function datesAreWithinTolerance(dateA, dateB) {
	let dayDifference = Math.abs((new Date(dateA) - new Date(dateB)) / 86400000);
	return dayDifference <= DATE_TOLERANCE_DAYS;
}

function buildFullAccountDescription(accountNumber, accountDescription) {
	return `${accountNumber} - ${accountDescription}`;
}

function normalizeConfidence(rawConfidence) {
	if (rawConfidence === null || rawConfidence === undefined) return null;
	if (rawConfidence > 1) return rawConfidence / 100;
	return rawConfidence;
}

function hasNonZeroAmount(transaction) {
	const isNonZeroNumber = !isNaN(transaction.amount) && transaction.amount !== 0;
	return isNonZeroNumber;
}

function isDigitCharacter(character) {
	const isDigit = character >= "0" && character <= "9";
	return isDigit;
}

function readLeadingDigits({ text }) {
	const trimmedText = UtilityService.toTrimmedText(text);
	let leadingDigits = "";
	for (const character of trimmedText) {
		if (!isDigitCharacter(character)) break;
		leadingDigits += character;
	}
	return leadingDigits;
}

function buildAccountNumberMap({ generalLedgerEntries, resolveKey }) {
	const accountNumberByKey = {};
	(generalLedgerEntries || []).forEach(entry => {
		const key = resolveKey(entry);
		if (!key) return;
		const accountNumber = entry.accountNumber || readLeadingDigits({ text: entry.account || "" });
		if (accountNumber && !accountNumberByKey[key]) {
			accountNumberByKey[key] = accountNumber;
		}
	});
	return accountNumberByKey;
}

function isMappedToSuspense(transaction) {
	const canonicalCode = transaction.canonical_code || "";
	const classificationName = transaction.classification || "";
	const hasSuspenseCanonicalCode = canonicalCode.startsWith("SUSPENSE.");
	const hasSuspenseClassificationName = SUSPENSE_ACCOUNT_PATTERN.test(classificationName);
	const mappedToSuspense = hasSuspenseCanonicalCode || hasSuspenseClassificationName;
	return mappedToSuspense;
}

function needsSuspenseReview(transaction) {
	const unclassifiedOrSuspense = stillNeedsAnAccount(transaction) || isMappedToSuspense(transaction);
	return unclassifiedOrSuspense;
}

const METHODS_A_PERSON_MUST_SIGN_OFF = ["ai", "unmatched"];
const METHODS_THAT_CAN_BE_ACCEPTED = [...METHODS_A_PERSON_MUST_SIGN_OFF];

function awaitsReviewerSignOff(transaction) {
	const wasGuessedByTheTool = METHODS_A_PERSON_MUST_SIGN_OFF.includes(transaction.classification_method);
	if (!wasGuessedByTheTool) return false;
	if (!transaction.canonical_code) return false;

	const alreadySignedOff = transaction.classification_status === "approved";
	return !alreadySignedOff;
}

function canAcceptClassification(transaction) {
	const couldBeAccepted = METHODS_THAT_CAN_BE_ACCEPTED.includes(transaction.classification_method);
	if (!couldBeAccepted) return false;
	if (!transaction.canonical_code) return false;

	const alreadyAccepted = transaction.classification_status === "approved";
	return !alreadyAccepted;
}

function normalizeReviewIssue(reviewNote) {
	if (Array.isArray(reviewNote)) {
		const hasComments = reviewNote.length > 0;
		return { status: hasComments ? DEFAULT_ISSUE_STATUS : null, comments: reviewNote };
	}
	if (reviewNote && typeof reviewNote === "object") {
		const comments = Array.isArray(reviewNote.comments) ? reviewNote.comments : [];
		const status = comments.length > 0 ? reviewNote.status || DEFAULT_ISSUE_STATUS : null;
		return { status, comments };
	}
	return { status: null, comments: [] };
}

function getTransactionDecisionMethod(transaction) {
	if (transaction.classification_method) return transaction.classification_method;
	if (transaction.confidence != null) return "ai";
	return "manual";
}

function describeDecisionSource(transactions) {
	const methodValues = transactions.map(getTransactionDecisionMethod);
	const methods = new Set(methodValues);
	if (methods.size > 1) return "Mixed";

	const methodList = Array.from(methods);
	const method = methodList[0];
	if (method === "gl_reference") return "GL match";
	if (method === "ai") return "AI";
	if (method === "manual") return "Manual";
	return "Rule";
}

function rankDecisionRisk({ isException, sourceLabel }) {
	if (isException) return 0;
	if (sourceLabel === "Manual" || sourceLabel === "Mixed") return 1;
	if (sourceLabel === "AI" || sourceLabel === "Rule") return 2;
	return 3;
}

function compareDecisionsByRisk(decisionA, decisionB) {
	if (decisionA.riskRank !== decisionB.riskRank) return decisionA.riskRank - decisionB.riskRank;
	return decisionA.classification.localeCompare(decisionB.classification);
}

function rankIssueStatus(status) {
	if (status === "resolved") return 1;
	return 0;
}

function compareIssuesByStatusThenAccount(issueA, issueB) {
	const rankA = rankIssueStatus(issueA.status);
	const rankB = rankIssueStatus(issueB.status);
	if (rankA !== rankB) return rankA - rankB;
	return issueA.account.localeCompare(issueB.account);
}

function carriesAnIssue({ transaction }) {
	const issue = normalizeReviewIssue(transaction.review_note);
	const hasComments = issue.comments.length > 0;
	return hasComments;
}

function describeIssueRows({ transactions }) {
	const firstRow = transactions[0];
	if (!firstRow) return "";

	const description = String(firstRow.description || "").trim();
	const label = [firstRow.date, description].filter(Boolean).join(" · ");
	if (transactions.length === 1) return label;
	return `${label} and ${transactions.length - 1} more`;
}

function buildSuspenseCsvRow(transaction) {
	const emptyReasonForTheClientToFillIn = "";
	const bankAccountName = transaction.bankAccountName || transaction.accountName || "";
	const classificationName = transaction.classification || "";
	const row = [transaction.date, transaction.description, transaction.amount, bankAccountName, classificationName, emptyReasonForTheClientToFillIn];
	return row;
}

function formatGeneralLedgerTaxCode(ratePercent) {
	const roundedRate = Math.round(ratePercent * 10) / 10;
	if (roundedRate <= 0) return "Exempt";
	const taxCode = `Tax ${roundedRate}%`;
	return taxCode;
}

function deriveAccountTaxes({ accountTotals }) {
	const accountTaxes = {};

	for (const [account, totals] of Object.entries(accountTotals)) {
		if (totals.net < 1) continue;
		const ratePercent = (totals.tax / totals.net) * 100;
		accountTaxes[account] = formatGeneralLedgerTaxCode(ratePercent);
	}

	return accountTaxes;
}


function toSignSample(transaction) {
	if (!transaction) return null;
	return { description: transaction.description, amount: transaction.amount };
}

function flipTransactionAmount(transaction) {
	return { ...transaction, amount: -transaction.amount };
}

const ISO_DATE_LENGTH = 10;
const ISO_DATE_DASH_POSITIONS = [4, 7];

function isIsoDateText(text) {
	if (text.length !== ISO_DATE_LENGTH) return false;

	for (let position = 0; position < text.length; position++) {
		const character = text[position];
		const expectsDash = ISO_DATE_DASH_POSITIONS.includes(position);
		const isValidCharacter = expectsDash ? character === "-" : isDigitCharacter(character);
		if (!isValidCharacter) return false;
	}

	return true;
}


const BookkeepingService = {
	/**
	 * Whether a transaction is still waiting for a person to sign off on the tool's guess.
	 *
	 * The tool guessing an account (AI, fuzzy, unmatched) is not the same as a preparer having
	 * agreed with it, so those rows wait in To Review until someone accepts them — which sets
	 * classification_status to approved and leaves classification_method alone, because how the
	 * row was classified does not change when it is accepted.
	 *
	 * This is the one definition. It was written out by hand in four places, and three of them
	 * left out the approved check, so accepting every row emptied the To Review tab while the
	 * banner and the workflow step went on reporting the same fourteen.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {Boolean}
	 */
	awaitsReviewerSignOff({ transaction }) {
		const stillWaiting = awaitsReviewerSignOff(transaction);
		return stillWaiting;
	},

	/**
	 * Whether this transaction is still waiting for someone to say which account it belongs to.
	 *
	 * Having no classification is not the same as needing one. A transfer leg that has been
	 * matched is settled: it is one half of money moving between the client's own accounts, the
	 * other half carries the posting, and giving this one an account would record the movement
	 * twice. It has no classification because it should not have one.
	 *
	 * Every surface that asks this question has to ask it the same way. Eight places tested
	 * "classification is empty" in longhand, so approving a transfer removed it from the
	 * Transfers tab and left it sitting in Unclassified, in the Classify count, and in the
	 * arithmetic deciding whether the books were finished — which no amount of work could then
	 * satisfy, because a matched leg never gets a classification.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {Boolean}
	 */
	stillNeedsAnAccount({ transaction }) {
		const isStillWaiting = stillNeedsAnAccount(transaction);
		return isStillWaiting;
	},

	/**
	 * Whether a person can still accept this classification.
	 *
	 * Wider than awaitsReviewerSignOff and deliberately so: the tool's own guesses have to be
	 * signed off, but a preparer may also accept one a firm rule, keyword or pattern chose, even
	 * though those never sat in To Review. The two lists are defined so that the sign-off
	 * methods are part of the acceptable ones, which is why they cannot drift apart.
	 *
	 * What both share is the status check. Accepting sets classification_status to approved, so
	 * a row already carrying it has nothing left to accept — offering the action again is the
	 * same stale-state defect that had the banner counting rows the tab had let go.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {Boolean}
	 */
	canAcceptClassification({ transaction }) {
		const isStillAcceptable = canAcceptClassification(transaction);
		return isStillAcceptable;
	},

	/**
	 * The classification methods that are the tool's own guess rather than a person's or a
	 * rule's decision. Exposed so a caller asking a different question about the same fact —
	 * how confident the classification is, say — reads the one list instead of retyping it.
	 *
	 * @returns {Array<String>}
	 */
	listMethodsAPersonMustSignOff() {
		return [...METHODS_A_PERSON_MUST_SIGN_OFF];
	},

	/**
	 * Downloads bookkeeping summaries as an Excel file.
	 *
	 * @param {String} clientId
	 * @param {Array} transactions
	 * @param {Array} priorYearTrialBalance
	 * @param {Array} classifications
	 * @param {Boolean} hasTax
	 * @param {String} hstAccount
	 * @param {Object} reviewData
	 * @returns {Promise<void>}
	 */
	async downloadBookkeepingSummaries({ clientId, transactions, priorYearTrialBalance, classifications, hasTax, hstAccount, reviewData, invertSigns = false, sourceFiles = [] }) {

		try {
			let { client } = await ClientService.fetchClient({ clientId });

			let payload = JSON.stringify({
				clientId,
				transactions,
				priorYearTrialBalance,
				classifications,
				hasTax,
				hstAccount: hstAccount || "",
				reviewData,
				invertSigns,
			});
			let formData = new FormData();
			formData.append("payload", payload);
			sourceFiles.forEach(file => formData.append("files", file, file.name));

			let response = await authedFetch(`/tools/bookkeeping/download_summaries`, {
				method: "POST",
				body: formData,
			});

			let blob = await response.blob();

			UtilityService.downloadFile({ blob, fileName: `${client.name} Summary.xlsx` });
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Returns the province name when it matches a known tax province, otherwise an
	 * empty string. Junk values saved by older builds (like abandoned typed text)
	 * are treated as no province so the user is asked to pick a real one.
	 *
	 * @param {String} province
	 * @returns {String}
	 */
	/**
	 * The provinces the tool can actually apply tax to, shaped for a Tools dropdown.
	 * Taken from the tax table itself so the list and the validation can never drift
	 * apart — anything offered here is accepted by getKnownProvinceOrEmpty.
	 *
	 * @returns {Array} [{ value, label }]
	 */
	getProvinceOptions() {
		const provinceOptions = Object.keys(TAX_CATEGORIES).map(province => ({ value: province, label: province }));
		return provinceOptions;
	},

	getKnownProvinceOrEmpty({ province }) {
		const isKnownProvince = Boolean(TAX_CATEGORIES[province]);
		const knownProvince = isKnownProvince ? province : "";
		return knownProvince;
	},

	/**
	 * Returns the classification list with a Suspense account guaranteed: when the
	 * list has no suspense-named or 9xxx account, the default "9999 - Suspense"
	 * entry is appended so every chart of accounts has somewhere to park unclear
	 * transactions.
	 *
	 * @param {Array} classifications
	 * @returns {Array}
	 */
	ensureSuspenseClassification({ classifications }) {
		const existingClassifications = classifications || [];
		const existingSuspenseAccount = findSuspenseAccount(existingClassifications);
		if (existingSuspenseAccount) return existingClassifications;

		const defaultSuspenseAccount = {
			name: DEFAULT_SUSPENSE_ACCOUNT_NAME,
			canonical_code: CANONICAL.CLEARING_ACCOUNT,
			gifi_code: null,
			canonical_label: DEFAULT_SUSPENSE_ACCOUNT_NAME,
		};
		const classificationsWithSuspense = [...existingClassifications, defaultSuspenseAccount];
		return classificationsWithSuspense;
	},

	/**
	 * Groups transactions into allocation decisions a reviewer can judge as one unit:
	 * every transaction sharing a classification and tax code forms one decision, with
	 * its source (GL match, AI, manual, rule), transaction count, total amount, lowest
	 * AI confidence, and review progress. Decisions come back ordered by risk —
	 * exceptions (unclassified or suspense) first, manual next, AI and rules after,
	 * GL matches last.
	 *
	 * A settled transfer leg is not an allocation decision and is left out. It does not post —
	 * the payment is recorded on the other leg — so there is no account to choose for it, and
	 * asking is asking twice about one payment. Counting them here put eleven already-matched
	 * legs into the unresolved queue and overstated it by 11,395.40, while the Transfers tab
	 * had already let them go.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array} decisions: { decisionKey, classification, tax, transactions,
	 *   sourceLabel, isException, isUnclassifiedDecision, reviewedCount, isFullyReviewed,
	 *   totalAmount, lowestConfidence, riskRank }
	 */
	buildReviewDecisions({ transactions }) {
		const decisionsByKey = {};
		const needingADecision = (transactions || []).filter(transaction => !transaction.mirrors_transaction_id);

		for (const transaction of needingADecision) {
			const classificationName = isUnclassified(transaction.classification) ? "Unclassified" : transaction.classification;
			const tax = transaction.tax || "";
			const decisionKey = `${classificationName}||${tax}`;
			if (!decisionsByKey[decisionKey]) {
				decisionsByKey[decisionKey] = { decisionKey, classification: classificationName, tax, canonicalCode: null, transactions: [] };
			}
			if (!decisionsByKey[decisionKey].canonicalCode && transaction.canonical_code) {
				decisionsByKey[decisionKey].canonicalCode = transaction.canonical_code;
			}
			decisionsByKey[decisionKey].transactions.push(transaction);
		}

		const decisionGroups = Object.values(decisionsByKey);
		const decisions = decisionGroups.map(decision => {
			const sourceLabel = describeDecisionSource(decision.transactions);
			const isException = decision.transactions.some(needsSuspenseReview);
			const isUnclassifiedDecision = decision.classification === "Unclassified";
			const reviewedTransactions = decision.transactions.filter(transaction => transaction.reviewed_by);
			const reviewedCount = reviewedTransactions.length;
			const totalAmount = decision.transactions.reduce((sum, transaction) => sum + (transaction.amount || 0), 0);
			const allConfidences = decision.transactions.map(transaction => transaction.confidence);
			const knownConfidences = allConfidences.filter(confidence => confidence != null);
			const lowestConfidence = knownConfidences.length > 0 ? Math.min(...knownConfidences) : null;
			const reviewIssues = decision.transactions.map(transaction => normalizeReviewIssue(transaction.review_note));
			// An issue belongs to the group only when it was raised against the whole
			// group. One row's question is that row's, and saying otherwise puts a
			// badge on nine transactions nobody asked anything about.
			const everyRowCarriesTheIssue = reviewIssues.length > 0 && reviewIssues.every(issue => issue.comments.length > 0);
			const issue = everyRowCarriesTheIssue ? reviewIssues[0] : { status: null, comments: [] };
			return {
				...decision,
				noteThread: issue.comments,
				issueStatus: issue.status,
				sourceLabel,
				isException,
				isUnclassifiedDecision,
				reviewedCount,
				isFullyReviewed: reviewedCount === decision.transactions.length,
				totalAmount,
				lowestConfidence,
				riskRank: rankDecisionRisk({ isException, sourceLabel }),
			};
		});

		const sortedDecisions = decisions.sort(compareDecisionsByRisk);
		return sortedDecisions;
	},

	/**
	 * Returns the tax rate a general ledger entry implies — its tax amount as a
	 * percentage of the net amount — rounded to one decimal, or null when there is
	 * no tax or no net amount to divide by.
	 *
	 * @param {Number} taxAmount
	 * @param {Number} netAmount
	 * @returns {Number|null}
	 */
	calculateTaxPercent({ taxAmount, netAmount }) {
		const tax = Math.abs(Number(taxAmount) || 0);
		const net = Math.abs(Number(netAmount) || 0);
		if (tax <= 0 || net <= 0) return null;

		const percent = Math.round((tax / net) * 1000) / 10;
		return percent;
	},

	/**
	 * Groups general ledger reference entries by account into cards a reviewer can
	 * scan the same way as the allocation decisions: each group carries its account
	 * name, its entries, the entry count and the total amount. Groups come back
	 * sorted by account name.
	 *
	 * @param {Array} entries - [{ date, account, description, amount, tax }]
	 * @returns {Array} groups: { account, entries, entryCount, totalAmount }
	 */
	groupGeneralLedgerEntriesByAccount({ entries }) {
		const groupsByAccount = {};

		for (const entry of entries || []) {
			const account = entry.account || "—";
			if (!groupsByAccount[account]) {
				groupsByAccount[account] = { account, entries: [] };
			}
			groupsByAccount[account].entries.push(entry);
		}

		const groups = Object.values(groupsByAccount);
		const summarizedGroups = groups.map(group => {
			const entryCount = group.entries.length;
			const totalAmount = group.entries.reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
			const entryCarryingACode = group.entries.find(entry => entry.canonical_code);
			const canonicalCode = entryCarryingACode ? entryCarryingACode.canonical_code : null;
			return { ...group, entryCount, totalAmount, canonicalCode };
		});

		const sortedGroups = summarizedGroups.sort((a, b) => a.account.localeCompare(b.account));
		return sortedGroups;
	},

	/**
	 * Filters general ledger reference entries by free text matched against the
	 * account and description. An empty search returns every entry.
	 *
	 * @param {Array} entries - [{ date, account, description, amount, tax }]
	 * @param {String} searchText
	 * @returns {Array}
	 */
	filterGeneralLedgerEntries({ entries, searchText }) {
		const cleanSearch = UtilityService.toSearchText(searchText);
		if (!cleanSearch) return entries || [];

		const safeEntries = entries || [];
		const matchingEntries = safeEntries.filter(entry => {
			const accountText = UtilityService.toSearchText(entry.account);
			const descriptionText = UtilityService.toSearchText(entry.description);
			const matchesAccount = accountText.includes(cleanSearch);
			const matchesDescription = descriptionText.includes(cleanSearch);
			return matchesAccount || matchesDescription;
		});
		return matchingEntries;
	},

	/**
	 * Filters review decisions by search text, source label, and review status
	 * ("reviewed", "needs_review", or empty for all). The search matches the account
	 * name or any of the decision's transaction descriptions, mirroring the prior-year
	 * general ledger filter.
	 *
	 * @param {Array} decisions - Output of buildReviewDecisions
	 * @param {String} searchText
	 * @param {String} sourceLabel
	 * @param {String} reviewStatus
	 * @returns {Array}
	 */
	filterReviewDecisions({ decisions, searchText, sourceLabel, reviewStatus }) {
		const cleanSearch = UtilityService.toSearchText(searchText);
		const showingOnlyFlaggedRows = reviewStatus === REVIEW_STATUS_FILTERS.HAS_ISSUE;

		const matchesStatus = decision => {
			if (!reviewStatus) return true;
			if (reviewStatus === REVIEW_STATUS_FILTERS.APPROVED) return decision.isFullyReviewed;
			if (reviewStatus === REVIEW_STATUS_FILTERS.NOT_APPROVED) return !decision.isFullyReviewed;
			if (showingOnlyFlaggedRows) return (decision.transactions || []).some(transaction => carriesAnIssue({ transaction }));
			return true;
		};

		const safeDecisions = decisions || [];
		const keptDecisions = safeDecisions.filter(decision => {
			const classificationText = UtilityService.toSearchText(decision.classification);
			const matchesClassification = classificationText.includes(cleanSearch);
			const matchesAnyTransactionDescription = (decision.transactions || []).some(transaction => {
				const descriptionText = UtilityService.toSearchText(transaction.description);
				return descriptionText.includes(cleanSearch);
			});
			const matchesSearch = !cleanSearch || matchesClassification || matchesAnyTransactionDescription;
			const matchesSource = !sourceLabel || decision.sourceLabel === sourceLabel;
			return matchesSearch && matchesSource && matchesStatus(decision);
		});

		const nothingNarrowsTheRows = !cleanSearch && !showingOnlyFlaggedRows;
		if (nothingNarrowsTheRows) return keptDecisions;

		const narrowedDecisions = keptDecisions.map(decision => {
			const classificationText = UtilityService.toSearchText(decision.classification);
			const wholeGroupMatchesTheSearch = !cleanSearch || classificationText.includes(cleanSearch);

			let rows = decision.transactions || [];
			if (!wholeGroupMatchesTheSearch) {
				rows = rows.filter(transaction => UtilityService.toSearchText(transaction.description).includes(cleanSearch));
			}
			if (showingOnlyFlaggedRows) rows = rows.filter(transaction => carriesAnIssue({ transaction }));

			if (rows.length === (decision.transactions || []).length) return decision;
			return this.rebuildDecisionAroundTransactions({ decision, transactions: rows });
		});
		return narrowedDecisions;
	},

	/**
	 * Restates a group so its heading describes only the rows left in it. A search that
	 * kept a group because three of its rows matched must not still announce ten
	 * transactions and their total: the count and the amount are what a preparer reads
	 * before acting on the group, and acting on it would otherwise reach rows the search
	 * excluded and the screen never showed.
	 *
	 * @param {Object} params
	 * @param {Object} params.decision - the group as originally built
	 * @param {Array} params.transactions - the rows that survived the search
	 * @returns {Object} the same group, restated around those rows
	 */
	rebuildDecisionAroundTransactions({ decision, transactions }) {
		const totalAmount = transactions.reduce((runningTotal, transaction) => runningTotal + (transaction.amount || 0), 0);
		const reviewedCount = transactions.filter(transaction => transaction.reviewed_by).length;
		const knownConfidences = transactions.map(transaction => transaction.confidence).filter(confidence => confidence != null);

		// Every field buildReviewDecisions derives from the transaction list has to be
		// derived again here, or the heading describes rows the filter took away — an
		// exception mark left behind by the one suspense row that was filtered out is a
		// group claiming an unresolved item it no longer holds.
		return {
			...decision,
			transactions,
			totalAmount,
			reviewedCount,
			isFullyReviewed: transactions.length > 0 && reviewedCount === transactions.length,
			lowestConfidence: knownConfidences.length > 0 ? Math.min(...knownConfidences) : null,
			isException: transactions.some(needsSuspenseReview),
			sourceLabel: describeDecisionSource(transactions),
		};
	},

	/**
	 * Filters review decisions to a single account, matched by canonical code rather than
	 * by display name — a decision belongs to the account when any of its transactions
	 * carry that canonical code. Used when a trial-balance row is selected, so the current
	 * side lines up with the trial balance even when the display label differs.
	 *
	 * @param {Array} decisions - Output of buildReviewDecisions
	 * @param {String} canonicalCode
	 * @returns {Array}
	 */
	getDecisionsForCanonicalCode({ decisions, canonicalCode }) {
		const safeDecisions = decisions || [];
		const decisionsForCode = safeDecisions.filter(decision => {
			const hasTransactionWithCode = (decision.transactions || []).some(transaction => transaction.canonical_code === canonicalCode);
			return hasTransactionWithCode;
		});
		return decisionsForCode;
	},

	/**
	 * Filters prior-year general-ledger entries to a single account, matched by canonical
	 * code rather than by the entry's imported label — so selecting a trial-balance row
	 * shows its GL entries even when the GL called the account something different.
	 *
	 * @param {Array} entries - GL entries, each stamped with canonical_code
	 * @param {String} canonicalCode
	 * @returns {Array}
	 */
	filterGeneralLedgerEntriesByCanonicalCode({ entries, canonicalCode }) {
		const safeEntries = entries || [];
		const matchingEntries = safeEntries.filter(entry => entry.canonical_code === canonicalCode);
		return matchingEntries;
	},

	/**
	 * Maps each canonical account to its account number, taken from the imported general
	 * ledger — the number the GL carried on that account (its own column when mapped, else
	 * the leading digits of the account name). Lets the export show the source account
	 * number next to each classified account, matched by canonical code rather than label.
	 *
	 * @param {Array} generalLedgerEntries - GL entries stamped with canonical_code and accountNumber
	 * @returns {Object} { [canonical_code]: accountNumber } — the first non-empty number wins
	 */
	buildAccountNumberByCanonicalCode({ generalLedgerEntries }) {
		const accountNumberByCanonicalCode = buildAccountNumberMap({ generalLedgerEntries, resolveKey: entry => entry.canonical_code });
		return accountNumberByCanonicalCode;
	},

	/**
	 * Maps each general-ledger account name to its account number, taken from the imported
	 * general ledger. Used to number the bank-account rows on the export, whose account is
	 * the bank name (matched by name, since a bank leg carries no classification canonical
	 * code) rather than a classified account.
	 *
	 * @param {Array} generalLedgerEntries - GL entries stamped with accountNumber
	 * @returns {Object} { [account]: accountNumber } — the first non-empty number wins
	 */
	buildAccountNumberByAccountName({ generalLedgerEntries }) {
		const accountNumberByAccountName = buildAccountNumberMap({ generalLedgerEntries, resolveKey: entry => entry.account });
		return accountNumberByAccountName;
	},

	/**
	 * Summarizes how the current transactions were classified, for the download-review
	 * screen. Every transaction lands in exactly one bucket by priority — transfer, then
	 * GL-reference match, then rule-based auto-classification, then unclassified — so the
	 * counts sum to the total. Reports coverage (share of transactions classified) and a
	 * suggestion accuracy (of the rows the tool pre-classified, the share the reviewer
	 * kept as-is), plus the list of manual overrides.
	 *
	 * A transfer is recognised by the pair the server recorded, because these counts are sent
	 * with the download and kept as the engagement's record of the session. They used to be
	 * bucketed by an isTransfer flag that only ever existed in the browser, so a preparer who
	 * reloaded before confirming filed a summary reporting no transfers at all and counting both
	 * legs of each one as classifications the model had suggested.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - the client's bookkeeping transactions
	 * @returns {Object} counts, percentages, and the manual-change list for the review modal
	 */
	buildClassificationSummary({ transactions }) {
		const safeTransactions = transactions || [];
		const totalCount = safeTransactions.length;

		const hasRealClassification = classification => !!classification && classification !== "Unclassified";

		const transferTransactions = safeTransactions.filter(transaction => !!transaction.mirrors_transaction_id);
		const nonTransferTransactions = safeTransactions.filter(transaction => !transaction.mirrors_transaction_id);
		const glReferenceTransactions = nonTransferTransactions.filter(transaction => transaction.glClassified);
		const autoClassifiedTransactions = nonTransferTransactions.filter(transaction => !transaction.glClassified && hasRealClassification(transaction.classification));
		const unclassifiedTransactions = nonTransferTransactions.filter(transaction => !transaction.glClassified && !hasRealClassification(transaction.classification));

		const manualChanges = safeTransactions.filter(transaction => {
			if (!hasRealClassification(transaction.classification)) return false;
			if (transaction.aiClassification) return transaction.classification !== transaction.aiClassification;
			if (transaction.cwClassification) return transaction.classification !== transaction.cwClassification;
			return false;
		});

		const transferCount = transferTransactions.length;
		const glReferenceCount = glReferenceTransactions.length;
		const autoClassifiedCount = autoClassifiedTransactions.length;
		const unclassifiedCount = unclassifiedTransactions.length;
		const classifiedCount = totalCount - unclassifiedCount;

		const coveragePercent = totalCount > 0 ? Math.round((classifiedCount / totalCount) * 100) : 0;

		const preClassifiedCount = glReferenceCount + autoClassifiedCount;
		const keptCount = preClassifiedCount - manualChanges.length;
		const suggestionAccuracyPercent = preClassifiedCount > 0 ? Math.round((keptCount / preClassifiedCount) * 100) : null;

		return {
			totalCount,
			glReferenceCount,
			autoClassifiedCount,
			transferCount,
			unclassifiedCount,
			classifiedCount,
			manualChangeCount: manualChanges.length,
			coveragePercent,
			suggestionAccuracyPercent,
			manualChanges,
		};
	},

	/**
	 * Stamps a reviewer's sign-off on the given transactions: reviewed_by and
	 * reviewed_at are set on every transaction in the review set, others untouched.
	 *
	 * @param {Array} transactions - The full transaction list
	 * @param {Array} transactionsToReview - The rows being signed off, matched by identity
	 * @param {String} reviewerName
	 * @param {String} reviewedAt - ISO timestamp
	 * @returns {Array}
	 */
	stampReviewOnTransactions({ transactions, transactionsToReview, reviewerName, reviewedAt }) {
		const reviewSet = new Set(transactionsToReview);
		const updatedTransactions = transactions.map(transaction => (reviewSet.has(transaction) ? { ...transaction, reviewed_by: reviewerName, reviewed_at: reviewedAt } : transaction));
		return updatedTransactions;
	},

	/**
	 * Appends a comment to the review-note thread on the given transactions. The
	 * thread is append-only — reviewer questions and preparer answers accumulate
	 * with author and timestamp, so the conversation stays intact as evidence and
	 * survives value changes.
	 *
	 * @param {Array} transactions - The full transaction list
	 * @param {Array} transactionsToComment - The rows the comment applies to, matched by identity
	 * @param {String} author
	 * @param {String} text
	 * @param {String} commentedAt - ISO timestamp
	 * @returns {Array}
	 */
	addReviewCommentToTransactions({ transactions, transactionsToComment, author, text, commentedAt }) {
		const cleanText = UtilityService.toTrimmedText(text);
		if (!cleanText) return transactions;

		const commentSet = new Set(transactionsToComment);
		const newComment = { author, at: commentedAt, text: cleanText };
		const updatedTransactions = transactions.map(transaction => {
			if (!commentSet.has(transaction)) return transaction;
			const issue = normalizeReviewIssue(transaction.review_note);
			const status = issue.status || DEFAULT_ISSUE_STATUS;
			const updatedComments = [...issue.comments, newComment];
			return { ...transaction, review_note: { status, comments: updatedComments } };
		});
		return updatedTransactions;
	},

	/**
	 * Sets the status of the issue (comment thread) on the given transactions,
	 * preserving the comments. Used by reviewers tracking issues in the Issues panel.
	 *
	 * @param {Array} transactions - The full transaction list
	 * @param {Array} transactionsToUpdate - The rows whose issue status changes, matched by identity
	 * @param {String} status - One of ISSUE_STATUSES values
	 * @returns {Array}
	 */
	setReviewIssueStatusOnTransactions({ transactions, transactionsToUpdate, status }) {
		const updateSet = new Set(transactionsToUpdate);
		const updatedTransactions = transactions.map(transaction => {
			if (!updateSet.has(transaction)) return transaction;
			const issue = normalizeReviewIssue(transaction.review_note);
			return { ...transaction, review_note: { status, comments: issue.comments } };
		});
		return updatedTransactions;
	},

	/**
	 * Collects every allocation decision that has an issue raised (at least one
	 * comment) into a list a reviewer can track, ordered unresolved first then by
	 * account. Each issue carries its account, status, comment thread, latest
	 * comment and the transactions it applies to.
	 *
	 * @param {Array} decisions - Output of buildReviewDecisions
	 * @returns {Array} issues: { decisionKey, account, status, comments, lastComment, commentCount, transactions }
	 */
	/**
	 * Descriptions this file has answered more than one way.
	 *
	 * Rules used to make this impossible: one remembered answer per description meant every
	 * occurrence went the same way, whether or not that way was right. Without them the same
	 * merchant can land in two accounts and nothing would say so, which misstates both lines.
	 *
	 * Being left unanswered counts as one of the ways. Rows are asked about in groups of merchant
	 * and order of magnitude, so eight card payments of about three hundred dollars are one
	 * question and three of about three thousand are another — and when the second question comes
	 * back with no account, an identical description sits classified eight times and unclassified
	 * three times with nothing connecting them. That is the same disagreement as two accounts and
	 * is easier to miss, because an unclassified row looks like work not yet done rather than an
	 * answer that contradicts its neighbours.
	 *
	 * Shown rather than prevented. Genuine splits exist — one merchant selling both fuel and
	 * groceries, or a purchase large enough to capitalise — so this is a question for a preparer
	 * to settle, not an error to correct automatically.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - table rows
	 * @returns {Object} { descriptions, count }
	 */
	findDescriptionsAnsweredMoreThanOneWay({ transactions }) {
		const answersSeenPerDescription = new Map();

		for (const transaction of transactions || []) {
			if (transaction.isArchived) continue;
			if (!transaction.description) continue;

			const answer = this.stillNeedsAnAccount({ transaction })
				? "(left unanswered)"
				: transaction.canonical_code || transaction.classification;
			if (!answer) continue;

			const description = transaction.description.trim().toLowerCase();
			if (!answersSeenPerDescription.has(description)) answersSeenPerDescription.set(description, new Set());
			answersSeenPerDescription.get(description).add(answer);
		}

		const descriptions = [];
		for (const [description, answers] of answersSeenPerDescription) {
			if (answers.size > 1) descriptions.push(description);
		}

		return { descriptions, count: descriptions.length };
	},

	/**
	 * The transactions that are one leg of a transfer the tool has paired.
	 *
	 * A transfer is one movement of money seen twice, so both legs sit in the books unclassified
	 * until the pair is approved — and they were being counted as work to do as well as shown on
	 * the Transfers tab. A preparer looking at eight unclassified rows had no way to tell they
	 * were the same eight payments already waiting for them somewhere else.
	 *
	 * A pair a preparer has rejected does not appear here, because rejecting it is the statement
	 * that these are not two halves of one movement — the rows go back to being ordinary
	 * transactions that need an account, which is what the Unclassified tab is for.
	 *
	 * Takes the pairs rather than the transactions because the caller has usually just worked them
	 * out and caches them — pairing every row again to answer this would be the expensive half of
	 * the question repeated for no reason.
	 *
	 * @param {Object} params
	 * @param {Array} params.pairs - from getTransferPairs
	 * @returns {Set} the leg rows themselves, so a caller can exclude them by identity
	 */
	listTransactionsAlreadySettledAsTransfers({ pairs }) {
		const legs = new Set();

		for (const pair of pairs) {
			if (pair.outLeg) legs.add(pair.outLeg);
			if (pair.inLeg) legs.add(pair.inLeg);
		}

		return legs;
	},

	/**
	 * The transactions that still need an account from a person.
	 *
	 * One question with one answer, because half a dozen places ask it — the Unclassified tab and
	 * its count, the progress figures, the row highlight, and what gets sent to the model. They
	 * were each filtering on their own, so excluding transfers from one of them left the rest
	 * saying something different about the same rows: a tab reading nothing to do beside a banner
	 * reading eight still to classify.
	 *
	 * A leg of a paired transfer is not waiting for an account. It is waiting to be approved as a
	 * transfer, which is a different queue on a different tab; rejecting the pair breaks it and
	 * the rows come back here, which is what makes rejection the way out rather than a dead end.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Set} [params.settledAsTransfers] - from listTransactionsAlreadySettledAsTransfers, when the caller already has it
	 * @returns {Array}
	 */
	listTransactionsStillNeedingAnAccount({ transactions, settledAsTransfers = null }) {
		const legs = settledAsTransfers || this.listTransactionsAlreadySettledAsTransfers({ pairs: this.getTransferPairs({ transactions }) });

		const stillWaiting = (transactions || []).filter(transaction =>
			!transaction.isArchived && stillNeedsAnAccount(transaction) && !legs.has(transaction)
		);
		return stillWaiting;
	},

	getReviewIssues({ decisions }) {
		const issues = [];

		for (const decision of decisions || []) {
			const transactionsByThread = new Map();
			for (const transaction of decision.transactions) {
				const issue = normalizeReviewIssue(transaction.review_note);
				if (issue.comments.length === 0) continue;
				const threadKey = JSON.stringify(issue.comments);
				if (!transactionsByThread.has(threadKey)) transactionsByThread.set(threadKey, { issue, transactions: [] });
				transactionsByThread.get(threadKey).transactions.push(transaction);
			}

			for (const [threadKey, raised] of transactionsByThread) {
				const coversTheWholeGroup = raised.transactions.length === decision.transactions.length;
				const comments = raised.issue.comments;
				issues.push({
					issueKey: `${decision.decisionKey}||${threadKey}`,
					decisionKey: decision.decisionKey,
					account: decision.classification,
					rowLabel: coversTheWholeGroup ? "" : describeIssueRows({ transactions: raised.transactions }),
					status: raised.issue.status || DEFAULT_ISSUE_STATUS,
					comments,
					lastComment: comments[comments.length - 1],
					commentCount: comments.length,
					transactions: raised.transactions,
				});
			}
		}

		const sortedIssues = issues.sort(compareIssuesByStatusThenAccount);
		return sortedIssues;
	},

	/**
	 * Removes the reviewer's sign-off from the given transactions.
	 *
	 * @param {Array} transactions - The full transaction list
	 * @param {Array} transactionsToClear - The rows losing their sign-off, matched by identity
	 * @returns {Array}
	 */
	clearReviewOnTransactions({ transactions, transactionsToClear }) {
		const clearSet = new Set(transactionsToClear);
		const updatedTransactions = transactions.map(transaction => (clearSet.has(transaction) ? { ...transaction, reviewed_by: null, reviewed_at: null } : transaction));
		return updatedTransactions;
	},

	/**
	 * Reclassifies a set of transactions to a new account as a reviewer decision:
	 * classification, canonical mapping and tax are replaced, the method becomes
	 * manual, and the reviewer's sign-off is stamped in the same step.
	 *
	 * @param {Array} transactions - The full transaction list
	 * @param {Array} transactionsToChange - The rows being reclassified, matched by identity
	 * @param {Object} classification - { name, canonical_code, canonical_label, gifi_code }
	 * @param {String} tax
	 * @param {String} reviewerName
	 * @param {String} reviewedAt - ISO timestamp
	 * @returns {Array}
	 */
	reclassifyTransactions({ transactions, transactionsToChange, classification, tax, reviewerName, reviewedAt }) {
		const changeSet = new Set(transactionsToChange);
		const updatedTransactions = transactions.map(transaction => {
			if (!changeSet.has(transaction)) return transaction;
			return {
				...transaction,
				classification: classification.name,
				canonical_code: classification.canonical_code || null,
				canonical_label: classification.canonical_label || classification.name,
				gifi_code: classification.gifi_code || null,
				classification_method: "manual",
				tax,
				reviewed_by: reviewerName,
				reviewed_at: reviewedAt,
			};
		});
		return updatedTransactions;
	},

	/**
	 * Returns the transactions that still sit in suspense: still needing an account, or mapped
	 * to a suspense account (canonical SUSPENSE.* code or a suspense-named account).
	 *
	 * This is the list the client is sent to explain, so a matched transfer leg on it would ask
	 * the client what a movement between two of their own accounts was for — a question the
	 * books have already answered.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array}
	 */
	getSuspenseTransactions({ transactions }) {
		const safeTransactions = transactions || [];
		const suspenseTransactions = safeTransactions.filter(needsSuspenseReview);
		return suspenseTransactions;
	},

	/**
	 * Downloads the suspense/unclassified transactions as a CSV named after the client,
	 * with an empty Reason column for the client to fill in.
	 *
	 * @param {String} clientId
	 * @param {Array} transactions
	 * @returns {Promise<void>}
	 */
	async downloadSuspenseTransactions({ clientId, transactions }) {
		try {
			let { client } = await ClientService.fetchClient({ clientId });

			const suspenseTransactions = this.getSuspenseTransactions({ transactions });
			const headers = ["Date", "Description", "Amount", "Bank Account", "Classification", "Reason"];
			const rows = suspenseTransactions.map(buildSuspenseCsvRow);
			const csvText = UtilityService.buildCsv({ headers, rows });
			const blob = new Blob([csvText], { type: "text/csv" });

			UtilityService.downloadFile({ blob, fileName: `${client.name} Suspense Transactions.csv` });
		} catch (error) {
			console.log(error);
		}
	},

	/**
	 * Classifies a single transaction via the AI classification API.
	 *
	 * @param {String} clientId
	 * @param {String} transactionDescription
	 * @param {Number} amount
	 * @param {Array} classifications
	 * @param {Object} classificationReferences
	 * @param {String} industry
	 * @param {String} province
	 * @param {Object} generalLedgerReferences
	 * @returns {Promise<Object>} Classification result with classification, tax, confidence
	 */
	async categorizeTransaction({ clientId, transactionDescription, amount, classifications, classificationReferences, industry, province, generalLedgerReferences }) {

		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.classifyBookkeepingTransactions({
				clientId,
				transactionDescription,
				amount,
				classifications,
				classificationReferences,
				industry,
				province,
				generalLedgerReferences,
			}, {}, userData.authToken);

			let json = response.data;
			let result = json && json.data ? json.data : json;

			return result;
		} catch (error) {
			return { classification: "Unclassified", tax: "", confidence: null };
		}
	},

	/**
	 * Uploads a general ledger Excel export (CaseWare or Xero) and returns the parsed flat
	 * rows, column map, and opening balances (parsing runs on the API where exceljs lives).
	 *
	 * @param {Number} clientId
	 * @param {File} file - The .xlsx export
	 * @param {String} source - The accounting system: "caseware", "xero" or "quickbooks"
	 * @returns {Promise<Object>} { headers, rows, columnMap, openingBalances, entityName, layout }, or { error } describing why the export could not be read
	 */
	async parseGlExcel({ clientId, file, source, wpfEngagementId = null }) {
		try {
			let formData = new FormData();
			formData.append("files", file);
			formData.append("source", source);
			if (wpfEngagementId) formData.append("wpfEngagementId", String(wpfEngagementId));
			let response = await authedFetch(`/tools/bookkeeping/parse_gl?clientId=${clientId}`, { method: "POST", body: formData });
			return await readJsonAnswer({ response });
		} catch (error) {
			console.log(error);
			return { error: COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Totals the debits and credits of the opening balances read from a general ledger
	 * export, so the import can show whether they form a balanced trial balance. An
	 * unbalanced opening trial balance is reported rather than rejected — a general
	 * ledger that does not balance is exactly the file a preparer needs to open, so the
	 * difference is surfaced for them to resolve instead of blocking the import.
	 *
	 * @param {Array} openingBalances - [{ debit, credit }] as returned by the GL parse
	 * @returns {Object} { debitTotal, creditTotal, difference, isBalanced }
	 */
	summarizeOpeningBalanceTieOut({ openingBalances }) {
		const roundToCents = value => Math.round((parseFloat(value) || 0) * 100) / 100;
		const lines = openingBalances || [];
		const debitTotal = roundToCents(lines.reduce((runningTotal, line) => runningTotal + roundToCents(line.debit), 0));
		const creditTotal = roundToCents(lines.reduce((runningTotal, line) => runningTotal + roundToCents(line.credit), 0));
		const difference = roundToCents(debitTotal - creditTotal);
		const isBalanced = Math.abs(difference) <= OPENING_BALANCE_TOLERANCE;
		return { debitTotal, creditTotal, difference, isBalanced };
	},

	/**
	 * Parses one PDF bank statement on the API without saving anything: transactions,
	 * opening and closing balances, statement period, and the parser's integrity report
	 * (amounts re-derived from the bank's running-balance column, opening + movements
	 * tied to the stated closing).
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {File} params.file - The PDF statement
	 * @returns {Promise<Object>} Parse payload, or { error } when it could not be parsed
	 */
	async parseBankStatementPdf({ clientId, file }) {
		try {
			const formData = new FormData();
			formData.append("files", file);
			if (clientId) formData.append("clientId", String(clientId));
			const response = await authedFetch(`/tools/bookkeeping/parse_bank_statement?clientId=${clientId}`, { method: "POST", body: formData });
			return await readJsonAnswer({ response });
		} catch (error) {
			console.log(error);
			return { error: COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Builds the verification report for a run of parsed bank statements belonging to one
	 * account: orders them by period, checks each statement's own tie-out (opening plus
	 * movements equals its stated closing, from the parser's integrity report), and checks
	 * continuity between consecutive statements — each statement must open exactly where
	 * the previous one closed, which is what proves no month is missing, duplicated or
	 * misread across a multi-PDF set.
	 *
	 * Card statements print rows without a year, so the parser may have corrected one
	 * from the statement's own cycle. Those corrections are carried through per
	 * statement — a rewritten transaction date must be visible to whoever approves the
	 * import, not applied behind them.
	 *
	 * @param {Object} params
	 * @param {Array} params.parses - Parse payloads from parseBankStatementPdf, any order
	 * @returns {Object} { statements, allParsed, allTied, chainUnbroken, breakCount, totalTransactions, firstOpening, lastClosing, accountNumbersSeen }
	 */
	/**
	 * The distinct card numbers a run of statements mentions.
	 *
	 * A statement covering a card and its supplementary card reports both numbers in one string,
	 * and different statements separate them differently — "A / B" on one, "A; B" on another. Held
	 * whole, those read as four different accounts, which is how a normal supplementary card came
	 * to look like statements filed under the wrong client.
	 *
	 * @param {Object} params
	 * @param {Array} params.statements
	 * @returns {Array}
	 */
	listDistinctCardNumbers({ statements }) {
		const everyNumber = [];
		for (const statement of statements || []) {
			const reported = String(statement.accountNumberMasked || "").trim();
			if (!reported) continue;
			const separated = reported.split(/\s*[/;,]\s*/);
			for (const number of separated) {
				const cleaned = number.trim();
				if (cleaned) everyNumber.push(cleaned);
			}
		}
		return [...new Set(everyNumber)];
	},

	buildStatementRunReport({ parses, fiscalPeriod = null }) {
		const roundToCents = value => Math.round((Number(value) || 0) * 100) / 100;
		const ordered = [...(parses || [])].sort((parseA, parseB) => String(parseA.periodStart || "").localeCompare(String(parseB.periodStart || "")));

		let previousClosing = null;
		const statements = ordered.map(parse => {
			const parsedOk = parse.status === "ok" || parse.status === "partial";
			const bankTieCheck = parse.integrity ? parse.integrity.opening_to_closing_check : null;
			const cardTieCheck = parse.integrity ? parse.integrity.balance_arithmetic_check : null;
			const tieCheck = bankTieCheck || cardTieCheck || null;
			const tieOk = !!(tieCheck && tieCheck.ok);
			const tieDelta = tieCheck ? tieCheck.delta : null;

			const yearCorrections = parse.yearCorrections || [];
			const yearsCorrectedCount = yearCorrections.length;

			const hasBothEnds = parsedOk && parse.openingBalance !== null && parse.openingBalance !== undefined && previousClosing !== null;
			const continuityDelta = hasBothEnds ? roundToCents(parse.openingBalance - previousClosing) : null;
			const continuityOk = previousClosing === null ? null : hasBothEnds && Math.abs(continuityDelta) < 0.01;

			if (parsedOk && parse.closingBalance !== null && parse.closingBalance !== undefined) previousClosing = parse.closingBalance;

			let outOfPeriodCount = 0;
			let isWhollyOutsidePeriod = false;
			if (fiscalPeriod && fiscalPeriod.periodStart && fiscalPeriod.periodEnd && parsedOk) {
				const { excludedBefore, excludedAfter } = this.filterTransactionsToPeriod({
					transactions: parse.transactions || [],
					periodStart: fiscalPeriod.periodStart,
					periodEnd: fiscalPeriod.periodEnd,
				});
				outOfPeriodCount = excludedBefore.length + excludedAfter.length;

				const endsBeforePeriod = !!parse.periodEnd && String(parse.periodEnd) < fiscalPeriod.periodStart;
				const startsAfterPeriod = !!parse.periodStart && String(parse.periodStart) > fiscalPeriod.periodEnd;
				isWhollyOutsidePeriod = endsBeforePeriod || startsAfterPeriod;
			}

			return {
				fileName: parse.fileName,
				statementType: parse.statementType || "bank",
				status: parse.status,
				errorDetail: parse.errorDetail || parse.error || null,
				parsedOk,
				fromRegistry: !!parse.fromRegistry,
				periodStart: parse.periodStart,
				periodEnd: parse.periodEnd,
				openingBalance: parse.openingBalance,
				closingBalance: parse.closingBalance,
				accountNumberMasked: parse.accountNumberMasked,
				bankName: parse.bankName,
				transactionCount: (parse.transactions || []).length,
				tieOk,
				tieDelta,
				continuityOk,
				continuityDelta,
				outOfPeriodCount,
				isWhollyOutsidePeriod,
				yearsCorrectedCount,
				yearCorrections,
			};
		});

		const parsedStatements = statements.filter(statement => statement.parsedOk);
		const accountNumbersSeen = this.listDistinctCardNumbers({ statements: parsedStatements });
		const breakCount = statements.filter(statement => statement.continuityOk === false).length;

		const periodCoverage = this.buildStatementPeriodCoverage({ parsedStatements, fiscalPeriod });

		return {
			statements,
			allParsed: statements.length > 0 && parsedStatements.length === statements.length,
			allTied: parsedStatements.length > 0 && parsedStatements.every(statement => statement.tieOk),
			chainUnbroken: breakCount === 0,
			breakCount,
			totalTransactions: parsedStatements.reduce((total, statement) => total + statement.transactionCount, 0),
			totalOutOfPeriod: parsedStatements.reduce((total, statement) => total + statement.outOfPeriodCount, 0),
			firstOpening: parsedStatements.length ? parsedStatements[0].openingBalance : null,
			lastClosing: parsedStatements.length ? parsedStatements[parsedStatements.length - 1].closingBalance : null,
			accountNumbersSeen,
			periodCoverage,
		};
	},

	/**
	 * Compares the span of a run of parsed statements against the fiscal period and
	 * reports whether months are missing at either end — statements that start after
	 * the year begins or stop before it ends leave part of the year unbanked.
	 *
	 * @param {Object} params
	 * @param {Array} params.parsedStatements - report rows with periodStart/periodEnd
	 * @param {Object} [params.fiscalPeriod] - { periodStart, periodEnd }
	 * @returns {Object|null} { missingAtStart, missingAtEnd, statementsStart, statementsEnd } or null
	 */
	buildStatementPeriodCoverage({ parsedStatements, fiscalPeriod }) {
		if (!fiscalPeriod || !fiscalPeriod.periodStart || !fiscalPeriod.periodEnd) return null;

		const startDates = parsedStatements.map(statement => statement.periodStart).filter(Boolean).map(String);
		const endDates = parsedStatements.map(statement => statement.periodEnd).filter(Boolean).map(String);
		if (startDates.length === 0 || endDates.length === 0) return null;

		const statementsStart = startDates.sort()[0];
		const statementsEnd = endDates.sort()[endDates.length - 1];

		return {
			statementsStart,
			statementsEnd,
			missingAtStart: statementsStart > fiscalPeriod.periodStart,
			missingAtEnd: statementsEnd < fiscalPeriod.periodEnd,
		};
	},

	async importBankStatementPdf({ engagementId, clientId, file, bankAccountName }) {
		try {
			let formData = new FormData();
			if (engagementId) formData.append("engagementId", String(engagementId));
			if (clientId) formData.append("clientId", String(clientId));
			if (bankAccountName) formData.append("bankAccountName", bankAccountName);
			formData.append("files", file);
			let response = await authedFetch(`/tools/bookkeeping/import_bank_statement`, {
				method: "POST",
				body: formData,
			});
			let text = await response.text();
			let json;
			try { json = JSON.parse(text); }
			catch (parseErr) {
				return {
					success: false,
					error: response.status === 404
						? "API endpoint not found (HTTP 404) — restart the API server to register the new /import_bank_statement route"
						: `Server returned non-JSON response (HTTP ${response.status}): ${text.slice(0, 200)}`,
				};
			}
			return json;
		} catch (error) {
			console.error("importBankStatementPdf failed:", error);
			return { success: false, error: String(error) };
		}
	},

	async clearEngagementTransactions({ engagementId, clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.clearEngagementTransactions({}, { engagementId, clientId, wpfEngagementId }, userData.authToken);
			return { success: true, ...response.data };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { success: false, error: reason };
		}
	},

	/**
	 * Loads saved transactions and engagement detail for an engagement
	 * @param {Number} engagementId
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async loadEngagementTransactions({ engagementId, clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementTransactions({}, { engagementId, clientId, wpfEngagementId }, userData.authToken);
			return response.data || { transactions: [], engagementDetail: null };
		} catch (error) {
			return { transactions: [], engagementDetail: null };
		}
	},

	/**
	 * Maps saved engagement transactions from the API into the row shape the
	 * bookkeeping table renders.
	 *
	 * The shape is a whitelist, so anything the server sends and this does not name is lost on
	 * the way in. That is how an approved transfer came back unapproved: the pairing was stored,
	 * returned, and dropped here, so reopening the file offered to approve transfers that were
	 * already recorded. Whatever ties a row to something else — the account, the bank account,
	 * the leg it mirrors — has to be carried, or a screen decides what to show from a row that
	 * no longer knows what it belongs to.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array}
	 */
	mapEngagementTransactionsForTable({ transactions }) {
		const tableRows = transactions.map(transaction => {
			const isFromGlReference = transaction.classification_method === "gl_reference";
			return {
				id: transaction.id != null ? transaction.id : null,
				date: transaction.date,
				description: transaction.description || "",
				amount: transaction.amount,
				canonical_code: transaction.canonical_code || null,
				canonical_label: transaction.canonical_label || null,
				classification: transaction.classification || "Unclassified",
				classification_label: transaction.classification_label || null,
				classification_method: transaction.classification_method || null,
				classification_status: transaction.classification_status || null,
				clientBankStatementId: transaction.clientBankStatementId || null,
				mirrors_transaction_id: transaction.mirrors_transaction_id || null,
				account_id: transaction.account_id || null,
				bank_account_id: transaction.bank_account_id || null,
				confidence: transaction.confidence,
				gifi_code: transaction.gifi_code || null,
				tax: transaction.tax || "",
				reviewed_by: transaction.reviewed_by || null,
				reviewed_at: transaction.reviewed_at || null,
				review_note: transaction.review_note || null,
				source_evidence: transaction.source_evidence || null,
				suspenseAccepted: !!transaction.suspenseAccepted,
				notATransfer: !!transaction.notATransfer,
				isArchived: !!transaction.isArchived,
				archiveReason: transaction.archiveReason || null,
				archiveNote: transaction.archiveNote || null,
				archivedAt: transaction.archivedAt || null,
				archivedBy: transaction.archivedBy || null,
				accountName: transaction.accountName || "",
				bankAccountNumber: transaction.bankAccountNumber || null,
				currency: transaction.currency || "CAD",
				exchange_rate: transaction.exchange_rate != null ? transaction.exchange_rate : 1,
				selected: false,
				glClassified: isFromGlReference,
				cwClassified: isFromGlReference,
				cwClassification: isFromGlReference ? transaction.classification || null : null,
			};
		});
		return tableRows;
	},

	/**
	 * Everything this client's bookkeeping was built from: the statements on their
	 * record, the general ledger in force for this period, and the CaseWare export
	 * behind it. Each statement reports how many transactions it currently has in the
	 * books, so the tool can say what removing it would cost before anyone agrees.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} [params.wpfEngagementId]
	 * @return {Promise<Object>} { bankStatements, generalLedger, casewareFiles } or { error }
	 */
	async listImportedFiles({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.listImportedFiles({}, { clientId, wpfEngagementId }, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Opens the file an import was built from. A stored file nobody can read is only
	 * half an audit trail — a reviewer asking which general ledger produced a trial
	 * balance has to be able to open it, not just read its name.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.documentId
	 * @param {String} params.fileName
	 * @return {Promise<Object|null>} { error } when it could not be fetched
	 */
	async downloadImportedFile({ clientId, documentId, fileName }) {
		try {
			const response = await authedFetch(`/tools/bookkeeping/imported_file_download?clientId=${clientId}&documentId=${documentId}`, { method: "GET" });
			if (!response || !response.ok) return { error: REQUEST_FAILED_MESSAGE };

			const blob = await response.blob();
			UtilityService.downloadFile({ blob, fileName });
			return null;
		} catch (error) {
			return { error: error.message || REQUEST_FAILED_MESSAGE };
		}
	},

	/**
	 * Takes an imported file off the client's record. Removing a bank statement also
	 * moves everything it put into the books to Archived, so the trial balance stops
	 * including it while a reviewer can still see what went and put it back.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.kind - bank_statement | gl_reference | caseware_reference
	 * @param {Number} [params.statementId]
	 * @param {Number} [params.documentId]
	 * @return {Promise<Object>} { removed, archivedTransactionCount } or { error }
	 */
	async removeImportedFile({ clientId, kind, statementId = null, documentId = null }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.removeImportedFile({ clientId, kind, statementId, documentId }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Writes onto each row which registered statement it came from, so removing that
	 * statement later can take exactly its transactions out of the books rather than
	 * guessing by date and amount — which would also take an identical transaction that
	 * arrived on a different statement.
	 *
	 * The rows carry the file's content hash from the moment they are read; the registry
	 * hands back the statement id it filed each hash under, and this joins the two.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - rows carrying statementFileHash
	 * @param {Array} params.registrationResults - [{ fileHash, statementId }] from the registry
	 * @returns {Array} the same rows, each with clientBankStatementId where it is known
	 */
	stampStatementIdOnTransactions({ transactions, registrationResults }) {
		const statementIdByFileHash = {};
		for (const result of registrationResults || []) {
			if (result.fileHash && result.statementId) statementIdByFileHash[result.fileHash] = result.statementId;
		}

		const stampedTransactions = (transactions || []).map(transaction => {
			const statementId = statementIdByFileHash[transaction.statementFileHash];
			if (!statementId) return transaction;
			return { ...transaction, clientBankStatementId: statementId };
		});
		return stampedTransactions;
	},

	/**
	 * Orders the rows inside a group by whichever column the reviewer clicked. Numbers
	 * are compared as numbers so -2,500 sits below 269 rather than beside "-2"
	 * alphabetically, and everything else as words, case-insensitively. Whether a value
	 * is a number is decided by the value, not by which column it came from: the tax
	 * column holds a code like "HST 13%" on this year's rows and a plain amount on the
	 * prior-year ledger, and both have to sort sensibly through the same function.
	 *
	 * Rows missing the value being sorted on collect at the end whichever way round the
	 * sort runs, rather than pretending to be empty strings and heading the list.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {String} params.column - date | description | tax | amount
	 * @param {Boolean} params.ascending
	 * @returns {Array} a new array; the input is left alone
	 */
	sortTransactionsForReview({ transactions, column, ascending }) {
		const rows = [...(transactions || [])];
		const direction = ascending ? 1 : -1;

		const isMissing = value => value === null || value === undefined || value === "";
		const asNumber = value => (typeof value === "number" ? value : Number(String(value).replace(/,/g, "")));

		const compareRows = (left, right) => {
			const leftValue = left[column];
			const rightValue = right[column];

			if (isMissing(leftValue) && isMissing(rightValue)) return 0;
			if (isMissing(leftValue)) return 1;
			if (isMissing(rightValue)) return -1;

			const leftNumber = asNumber(leftValue);
			const rightNumber = asNumber(rightValue);
			const bothAreNumbers = Number.isFinite(leftNumber) && Number.isFinite(rightNumber);
			if (bothAreNumbers) return (leftNumber - rightNumber) * direction;

			return String(leftValue).localeCompare(String(rightValue), undefined, { sensitivity: "base" }) * direction;
		};

		rows.sort(compareRows);
		return rows;
	},

	/**
	 * The issue thread hanging off one transaction — its status and every comment on
	 * it. A note is stored in two shapes depending on when it was written, so asking
	 * this way saves each caller from having to know both.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {Object} { status, comments }
	 */
	readReviewIssue({ transaction }) {
		const issue = normalizeReviewIssue(transaction.review_note);
		return issue;
	},

	/**
	 * Turns the tax rates read out of a CaseWare .dbf ledger into the tax codes the
	 * table uses. The spreadsheet routes work this out for themselves while building
	 * the general ledger reference, but the .dbf route has its rates worked out on the
	 * server and keyed by account number, so they have to be matched back to the
	 * account names the classifications carry before anything can use them.
	 *
	 * @param {Object} params
	 * @param {Object} params.ratesByAccountNumber - { "5547": 0.13 }, a fraction per account
	 * @param {Array} params.accounts - [{ accountNumber, accountDescription }]
	 * @returns {Object} { "5547 - Auto Expenses": "Tax 13%" }
	 */
	buildAccountTaxCodesFromGeneralLedgerRates({ ratesByAccountNumber, accounts }) {
		const nameByAccountNumber = {};
		for (const account of accounts || []) {
			if (!account.accountNumber || !account.accountDescription) continue;
			nameByAccountNumber[String(account.accountNumber).trim()] = `${account.accountNumber} - ${account.accountDescription}`;
		}

		const accountTaxes = {};
		for (const [accountNumber, rate] of Object.entries(ratesByAccountNumber || {})) {
			const accountName = nameByAccountNumber[String(accountNumber).trim()];
			if (!accountName) continue;
			accountTaxes[accountName] = formatGeneralLedgerTaxCode(Number(rate) * 100);
		}
		return accountTaxes;
	},

	/**
	 * Names the stretch of time a set of rows covers, for a column heading. Reading the
	 * year off whichever row happens to come first — which is what this replaces — named
	 * a period running August 2025 to June 2026 "2025", and a prior-year ledger ending
	 * February 2025 "2025" as well: two different periods under the same heading, with
	 * nothing on screen to tell them apart.
	 *
	 * A fiscal year is named for the year it ends in, so the year end is used when it is
	 * known and the latest row otherwise. The span is given alongside it because a June
	 * year end is not the 2026 calendar year, and a reviewer comparing two columns has to
	 * see which months each one actually holds.
	 *
	 * @param {Object} params
	 * @param {Array} params.rows
	 * @param {String} [params.dateKey] - the field holding each row's date
	 * @param {String} [params.fiscalYearEnd] - the period's year end, when it is known
	 * @returns {Object} { yearName, span } — both "" when nothing is dated
	 */
	describeDatedPeriod({ rows, dateKey = "date", fiscalYearEnd = null }) {
		const dates = (rows || [])
			.map(row => String(row[dateKey] || ""))
			.filter(date => Number(date.slice(0, 4)) > 1900)
			.sort();

		if (dates.length === 0 && !fiscalYearEnd) return { yearName: "", span: "" };

		const earliest = dates[0];
		const latest = dates[dates.length - 1];
		const yearEndToName = fiscalYearEnd || latest;
		const yearName = yearEndToName ? String(Number(String(yearEndToName).slice(0, 4))) : "";

		if (dates.length === 0) return { yearName, span: "" };

		const asMonth = date => UtilityService.formatDate({ value: date, format: "MMM YYYY", fallback: "" });
		const firstMonth = asMonth(earliest);
		const lastMonth = asMonth(latest);
		const span = firstMonth === lastMonth ? firstMonth : `${firstMonth} – ${lastMonth}`;

		return { yearName, span };
	},

	/**
	 * A stable way to find the same row again in a list that has since been replaced.
	 * Classification runs hand back a whole new array every time one finishes, so a
	 * row held from an earlier render is no longer the same object — comparing objects
	 * would quietly match nothing and lose whatever the person just did. A saved row is
	 * identified by its id; one that has never been saved is identified by what was
	 * imported for it.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {String}
	 */
	getTransactionIdentity({ transaction }) {
		if (transaction.id != null) return `id:${transaction.id}`;
		const identity = `row:${transaction.date || ""}|${transaction.amount}|${transaction.description || ""}`;
		return identity;
	},

	/**
	 * Reduces a bank description to the merchant it names, so two rows for the same shop
	 * are recognised as the same question. A card line carries a reference number and a
	 * terminal that change every time — "Interac purchase - 8426 B001 COSTCO GAS W524"
	 * and "Interac purchase - 2434 B001 COSTCO GAS W524" are the same purchase at the
	 * same station, and asking about each separately gets one classified and the other
	 * left blank when a single call fails.
	 *
	 * Digits go, then the words that say how the money moved rather than who took it.
	 * Returns an empty string when nothing identifying is left — a bare "CHEQUE 2088"
	 * names no merchant — so callers fall back to the raw description and keep those rows
	 * as separate questions rather than collapsing every anonymous row onto one answer.
	 *
	 * This list is the frontend's own and is deliberately not shared with the backend's
	 * bank-line normalizer: the two serve different steps and both are replaced by the
	 * merchant extraction in W-926_MERCHANT_EXTRACTION_PLAN.md §8.1.
	 *
	 * @param {Object} params
	 * @param {String} params.description - the description as the bank wrote it
	 * @returns {String} the merchant words, lowercased and space-joined
	 */
	buildMerchantKey({ description }) {
		const withoutTrailingCount = String(description || "").replace(/\s*\(\d+\)\s*$/, "");
		const withoutDigits = withoutTrailingCount.replace(/\d+/g, " ");
		const words = withoutDigits.toLowerCase().split(/\W+/).filter(Boolean);
		const identifyingWords = words.filter(word => !MONEY_MOVEMENT_WORDS.includes(word) && word.length > 1);
		const merchantKey = identifyingWords.join(" ");
		return merchantKey;
	},

	/**
	 * The question a transaction is asking, for deciding whether two rows can share one
	 * answer. The merchant alone is not enough: the same shop can belong in two accounts
	 * depending on what was bought, and the size of the purchase is what separates them.
	 * In one real ledger APPLE STORE posts to Computer Equipment at $2,510.86 and to
	 * Office & General at $25.99 — one answer applied to both would capitalise a $26
	 * supply or expense a $2,510 asset.
	 *
	 * Rows are therefore grouped by merchant AND order of magnitude, so a fill-up shares
	 * an answer with the next fill-up but never with a capital purchase.
	 *
	 * @param {Object} params
	 * @param {String} params.description
	 * @param {Number} [params.amount]
	 * @returns {String} the key two rows must share to be answered together
	 */
	buildClassificationQuestionKey({ description, amount }) {
		const merchantKey = this.buildMerchantKey({ description }) || String(description || "");
		const size = Math.abs(Number(amount) || 0);
		if (!size) return `${merchantKey} @0`;

		const orderOfMagnitude = Math.floor(Math.log10(size));
		return `${merchantKey} @${orderOfMagnitude}`;
	},

	/**
	 * Says why a row carries the account it does when the imported general ledger
	 * decided it. The general ledger pass runs entirely in the browser, so unlike every
	 * other tier its sentence cannot come from the server — it is written here, once,
	 * rather than at the place that stamps the rows.
	 *
	 * The match is a similarity score between this description and a ledger line, not a
	 * posting the ledger actually made against this row, so the sentence says which line
	 * it resembled and how closely. Claiming the ledger "posts this description" reads as
	 * a fact checked against the source and would send a reviewer past the doubt.
	 *
	 * @param {Object} params
	 * @param {String} params.account - the account the matched ledger line posts to
	 * @param {String} [params.similarity] - how closely the descriptions matched, 0 to 1
	 * @param {String} [params.matchedLedgerDescription] - the ledger line it matched
	 * @returns {Object} evidence shaped like the server's
	 */
	describeGeneralLedgerClassification({ account, similarity = null, matchedLedgerDescription = null }) {
		const closeness = similarity ? ` (${Math.round(Number(similarity) * 100)}% match)` : "";
		const whatItMatched = matchedLedgerDescription
			? `"${matchedLedgerDescription}" in the imported general ledger${closeness}, which posts to ${account}`
			: `a line in the imported general ledger${closeness} that posts to ${account}`;

		return {
			reason: `This description matches ${whatItMatched}.`,
			sources: [],
			searched: false,
		};
	},

	/**
	 * The rows a classification run still has work to do on: everything without an
	 * account, ignoring rows archived out of the books.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array}
	 */
	getTransactionsNeedingClassification({ transactions }) {
		const needingClassification = this.listTransactionsStillNeedingAnAccount({ transactions });
		return needingClassification;
	},

	/**
	 * Adds a fresh import to books that already hold transactions. A row whose date,
	 * amount and description already appear in the books is not added again: importing
	 * a statement that overlaps one already loaded would otherwise double the overlap
	 * and overstate the accounts. Skipped rows are returned rather than dropped
	 * quietly, because a genuine same-day repeat can look identical to a duplicate and
	 * the preparer has to be able to see what was held back.
	 *
	 * Existing rows are never touched, so classifications and approvals already made
	 * survive the import untouched.
	 *
	 * A row that left the books because its statement was removed does not block an
	 * incoming one — that is what lets a corrected statement be imported in place of the
	 * one it replaces. Every other archived row still blocks, because a preparer who
	 * archived something as a duplicate meant it, and a later import must not quietly
	 * undo that.
	 *
	 * @param {Object} params
	 * @param {Array} params.existingTransactions - what is already in the books, archived rows included
	 * @param {Array} params.incomingTransactions - rows from the new import
	 * @returns {Object} { transactions, addedCount, skippedTransactions }
	 */
	mergeImportedTransactions({ existingTransactions, incomingTransactions }) {
		const existingRows = existingTransactions || [];
		const takenKeys = new Set();
		for (const transaction of existingRows) {
			const leftWithItsStatement = transaction.isArchived && transaction.archiveReason === ARCHIVE_REASONS.STATEMENT_REMOVED;
			if (leftWithItsStatement) continue;
			const key = buildDuplicateKey({ transaction });
			if (key) takenKeys.add(key);
		}

		const addedTransactions = [];
		const skippedTransactions = [];
		for (const transaction of incomingTransactions || []) {
			const key = buildDuplicateKey({ transaction });
			if (key && takenKeys.has(key)) {
				skippedTransactions.push(transaction);
				continue;
			}
			if (key) takenKeys.add(key);
			addedTransactions.push(transaction);
		}

		return {
			transactions: [...existingRows, ...addedTransactions],
			addedCount: addedTransactions.length,
			skippedTransactions,
		};
	},

	/**
	 * Widens a chosen set of rows to include the other leg of any settled transfer among them.
	 *
	 * A settled transfer is one movement of money written down on both sides. Taking one side out
	 * of the books and leaving the other records that movement once: the surviving leg still posts
	 * to the account at the far end, but the account it names has no matching entry. The row also
	 * goes quiet — it is not waiting for an account, so it is not on the Unclassified tab, and its
	 * pair can no longer be found, so the Transfers tab cannot offer to reverse it either.
	 *
	 * Both legs therefore move together in either direction: archiving one archives its partner,
	 * restoring one restores its partner. The books stay balanced by construction instead of by
	 * the preparer noticing they had to select both.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - every table row
	 * @param {Function} params.isChosen - (transaction, index) => boolean
	 * @returns {Function} (transaction, index) => boolean
	 */
	alsoChooseTheOtherLegOfEachTransfer({ transactions, isChosen }) {
		const rows = transactions || [];
		const idsOfTheOtherLegs = new Set();

		rows.forEach((transaction, index) => {
			if (!isChosen(transaction, index)) return;
			if (transaction.mirrors_transaction_id) idsOfTheOtherLegs.add(String(transaction.mirrors_transaction_id));
		});
		if (idsOfTheOtherLegs.size === 0) return isChosen;

		const alsoTheOtherLeg = (transaction, index) => {
			if (isChosen(transaction, index)) return true;
			if (transaction.id === undefined || transaction.id === null) return false;
			return idsOfTheOtherLegs.has(String(transaction.id));
		};
		return alsoTheOtherLeg;
	},

	/**
	 * Marks the chosen transactions as archived — removed from the books but kept on
	 * the engagement, with who removed them and why, so the decision can be traced and
	 * undone. Rows the caller did not choose come back untouched, except the other leg of a
	 * transfer, which cannot be left behind without recording one side of a movement.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - every table row
	 * @param {Function} params.isChosen - (transaction, index) => boolean
	 * @param {String} params.reason - one of ARCHIVE_REASONS
	 * @param {String} [params.note]
	 * @param {String} [params.archivedBy]
	 * @param {String} [params.archivedAt] - ISO timestamp; the caller supplies it so the mapper stays pure
	 * @returns {Object} { transactions, archivedCount }
	 */
	archiveTransactions({ transactions, isChosen, reason, note = "", archivedBy = "", archivedAt = null }) {
		const isChosenOrItsOtherLeg = this.alsoChooseTheOtherLegOfEachTransfer({ transactions, isChosen });
		let archivedCount = 0;
		let transferCounterpartsIncluded = 0;
		const updatedTransactions = (transactions || []).map((transaction, index) => {
			if (transaction.isArchived) return transaction;
			if (!isChosenOrItsOtherLeg(transaction, index)) return transaction;

			archivedCount += 1;
			if (!isChosen(transaction, index)) transferCounterpartsIncluded += 1;
			return {
				...transaction,
				selected: false,
				isArchived: true,
				archiveReason: reason,
				archiveNote: note || null,
				archivedBy: archivedBy || null,
				archivedAt: archivedAt,
			};
		});
		return { transactions: updatedTransactions, archivedCount, transferCounterpartsIncluded };
	},

	/**
	 * Puts archived transactions back into the books, clearing the archive trail so a
	 * restored row is indistinguishable from one that was never removed. The other leg of a
	 * transfer comes back with it, for the same reason it left with it.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - every table row
	 * @param {Function} params.isChosen - (transaction, index) => boolean
	 * @returns {Object} { transactions, restoredCount }
	 */
	restoreTransactions({ transactions, isChosen }) {
		const isChosenOrItsOtherLeg = this.alsoChooseTheOtherLegOfEachTransfer({ transactions, isChosen });
		let restoredCount = 0;
		let transferCounterpartsIncluded = 0;
		const updatedTransactions = (transactions || []).map((transaction, index) => {
			if (!transaction.isArchived) return transaction;
			if (!isChosenOrItsOtherLeg(transaction, index)) return transaction;

			restoredCount += 1;
			if (!isChosen(transaction, index)) transferCounterpartsIncluded += 1;
			return {
				...transaction,
				selected: false,
				isArchived: false,
				archiveReason: null,
				archiveNote: null,
				archivedBy: null,
				archivedAt: null,
			};
		});
		return { transactions: updatedTransactions, restoredCount, transferCounterpartsIncluded };
	},

	/**
	 * Points out rows that look like the same transaction imported twice — identical
	 * date, amount and description within the live books. Only a pointer: two genuine
	 * transactions can legitimately match (two $5 coffees on one day), so nothing is
	 * archived automatically and the preparer decides. Archived rows are ignored, so a
	 * resolved duplicate stops being flagged.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - table rows
	 * @returns {Object} { duplicateKeys: Set, duplicateCount } - keys are date|description|amount
	 */
	findLikelyDuplicateTransactions({ transactions }) {
		const rowsByKey = {};
		for (const transaction of transactions || []) {
			if (transaction.isArchived) continue;
			const key = buildDuplicateKey({ transaction });
			if (!key) continue;
			rowsByKey[key] = (rowsByKey[key] || 0) + 1;
		}

		const duplicateKeys = new Set();
		let duplicateCount = 0;
		for (const key of Object.keys(rowsByKey)) {
			if (rowsByKey[key] < 2) continue;
			duplicateKeys.add(key);
			duplicateCount += rowsByKey[key];
		}
		return { duplicateKeys, duplicateCount };
	},

	/**
	 * The identity used to spot a repeated transaction. Exposed so the table can ask
	 * whether one row is part of a flagged group without rebuilding the rule.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction
	 * @returns {String} "" when the row has too little to compare
	 */
	buildDuplicateKeyForTransaction({ transaction }) {
		const key = buildDuplicateKey({ transaction });
		return key;
	},

	/**
	 * Builds the audit-trail block for the review report describing what was archived
	 * out of the books and why, so a reviewer can see that transactions were removed
	 * rather than discovering a silent gap between the statements and the books.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - every table row, archived included
	 * @returns {Object|null} { archivedCount, byReason, archivedTransactions } or null when nothing is archived
	 */
	buildArchiveSummary({ transactions }) {
		const archivedRows = (transactions || []).filter(transaction => transaction.isArchived);
		if (archivedRows.length === 0) return null;

		const byReason = {};
		for (const row of archivedRows) {
			const reasonLabel = ARCHIVE_REASON_LABELS[row.archiveReason] || "Other";
			byReason[reasonLabel] = (byReason[reasonLabel] || 0) + 1;
		}

		const archivedTransactions = archivedRows.map(row => ({
			date: row.date || null,
			description: row.description || "",
			amount: row.amount,
			accountName: row.accountName || "",
			reason: ARCHIVE_REASON_LABELS[row.archiveReason] || "Other",
			note: row.archiveNote || "",
			archivedBy: row.archivedBy || "",
		}));

		return { archivedCount: archivedRows.length, byReason, archivedTransactions };
	},

	/**
	 * Maps table-shaped transactions down to the fields the save endpoint persists,
	 * including the account currency and exchange rate so a foreign-currency conversion
	 * survives a reload.
	 *
	 * Each row names itself. The server decides what to update by that id, so a payload without
	 * one reads as a file of entirely new rows and entirely deleted old ones — every save would
	 * replace the table, and anything referring to a transaction by id would be destroyed with it.
	 *
	 * The row also names the account it was put in. The preparer picked that account from this
	 * client's own chart, so the id is already known here; leaving it out asks the server to work
	 * back to it from the label it was displayed under, which only matches while the display string
	 * and the stored account name agree. When it fails the row is filed as unclassified and the
	 * label, the method and the confidence are cleared with it — so a lookup that missed reads as a
	 * preparer who never chose, and the classification disappears on the next reload.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - Table rows
	 * @returns {Array}
	 */
	mapTableTransactionsForSave({ transactions }) {
		const savedTransactions = (transactions || []).map(transaction => ({
			id: transaction.id || null,
			date: transaction.date,
			description: transaction.description,
			amount: transaction.amount,
			accountName: transaction.accountName,
			currency: transaction.currency || "CAD",
			exchange_rate: transaction.exchange_rate != null ? transaction.exchange_rate : 1,
			account_id: transaction.account_id || null,
			canonical_code: transaction.canonical_code || null,
			classification_label: transaction.classification_label || null,
			classification_method: transaction.classification_method || null,
			classification_status: transaction.classification_status || null,
			clientBankStatementId: transaction.clientBankStatementId || null,
			confidence: transaction.confidence != null ? transaction.confidence : null,
			tax: transaction.tax || "",
			reviewed_by: transaction.reviewed_by || null,
			reviewed_at: transaction.reviewed_at || null,
			review_note: transaction.review_note || null,
			source_evidence: transaction.source_evidence || null,
			suspenseAccepted: !!transaction.suspenseAccepted,
			notATransfer: !!transaction.notATransfer,
			isArchived: !!transaction.isArchived,
			archiveReason: transaction.archiveReason || null,
			archiveNote: transaction.archiveNote || null,
			archivedAt: transaction.archivedAt || null,
			archivedBy: transaction.archivedBy || null,
		}));
		return savedTransactions;
	},

	/**
	 * A record of what the server last told us each row contained.
	 *
	 * Kept in the payload shape rather than the table shape so the comparison later asks exactly
	 * the question that matters — would this save write anything different — and not a broader one
	 * about fields the endpoint never persists.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - Table rows as the server last returned them
	 * @returns {Object} the saved contents of each row, keyed by id
	 */
	rememberWhatTheServerHas({ transactions }) {
		const remembered = {};
		for (const row of this.mapTableTransactionsForSave({ transactions })) {
			if (row.id) remembered[row.id] = JSON.stringify(row);
		}
		return remembered;
	},

	/**
	 * The rows a save actually needs to send, and the rows it needs to delete.
	 *
	 * Sending the whole file to record one classification costs about sixteen megabytes at a
	 * hundred thousand transactions, and the browser pays it on every keystroke-triggered save.
	 * Comparing each row against what the server last returned turns that into the handful of
	 * rows a preparer touched.
	 *
	 * A row the server never mentioned is new and is always sent. A row that has left the table
	 * is named for deletion, because the endpoint no longer infers removal from absence — that is
	 * what makes sending a subset safe. With nothing remembered, every row counts as changed and
	 * nothing is deleted, so a first save after loading still writes the whole file rather than
	 * quietly skipping it.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - Table rows as they stand now
	 * @param {Object} params.serverHas - From rememberWhatTheServerHas
	 * @returns {Object} { transactions, removeTransactionIds }
	 */
	pickWhatChangedSinceTheServerSawIt({ transactions, serverHas }) {
		const remembered = serverHas || {};
		const rows = this.mapTableTransactionsForSave({ transactions });

		const stillInTheTable = new Set();
		const changed = [];
		for (const row of rows) {
			if (!row.id) {
				changed.push(row);
				continue;
			}
			stillInTheTable.add(String(row.id));
			if (remembered[row.id] !== JSON.stringify(row)) changed.push(row);
		}

		const removeTransactionIds = Object.keys(remembered)
			.filter(id => !stillInTheTable.has(id))
			.map(Number);

		return { transactions: changed, removeTransactionIds };
	},

	/**
	 * Gathers the transactions for a bookkeeping generation run from whichever source
	 * the wizard used: standalone CSV files (with each file's confirmed sign convention
	 * applied), CSVs mapped to CaseWare bank accounts, or checked Plaid accounts.
	 * Returns the rows shaped for the bookkeeping table plus the number of CSV rows
	 * whose date or amount could not be parsed.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Boolean} params.useDirectStatementFiles
	 * @param {File[]} params.directStatementFiles
	 * @param {Object} params.statementSignByFile - Sign-convention confirmations keyed by file key
	 * @param {Object} params.statementColumnMapByFile - Confirmed column maps keyed by file key
	 * @param {Array} params.bankAccountsFromCw
	 * @param {Object} params.bankAccountStatementMap - Chosen CSV File per CaseWare account number
	 * @param {Array} params.connectedAccounts
	 * @param {String} params.startDate - "YYYY-MM-DD" start of the Plaid fetch window
	 * @param {String} params.endDate - "YYYY-MM-DD" end of the Plaid fetch window
	 * @returns {Promise<Object>} { transactions, invalidCount }
	 */
	async collectTransactionsForGeneration({ clientId, useDirectStatementFiles, directStatementFiles, statementSignByFile, statementColumnMapByFile = {}, bankAccountsFromCw, bankAccountStatementMap, connectedAccounts, startDate, endDate }) {
		let rawTransactions = [];
		let plaidAccounts = [];
		let invalidCount = 0;

		if (useDirectStatementFiles) {
			for (const file of directStatementFiles) {
				const bankAccountDescription = UtilityService.getFileNameWithoutExtension(file.name);
				const columnMap = statementColumnMapByFile[UtilityService.getFileKey(file)] || null;
				const extracted = await this.extractTransactionsFromStatementFile({ file, clientId, columnMap, bankAccountNumber: null, bankAccountDescription });
				const sign = statementSignByFile[UtilityService.getFileKey(file)];
				const isInverted = !!(sign && sign.inverted);
				const signedTransactions = this.applySignConvention({ transactions: extracted.transactions, inverted: isInverted });
				rawTransactions.push(...signedTransactions);
				invalidCount += extracted.invalidCount || 0;
			}
		} else if (bankAccountsFromCw.length > 0) {
			for (const account of bankAccountsFromCw) {
				const file = bankAccountStatementMap[account.accountNumber];
				if (!file) continue;
				const columnMap = statementColumnMapByFile[UtilityService.getFileKey(file)] || null;
				const extracted = await this.extractTransactionsFromStatementFile({ file, clientId, columnMap, bankAccountNumber: account.accountNumber, bankAccountDescription: account.description });
				rawTransactions.push(...extracted.transactions);
				invalidCount += extracted.invalidCount || 0;
			}
		} else {
			const checkedAccounts = connectedAccounts.filter(account => account.isChecked);
			for (const account of checkedAccounts) {
				const { accounts, transactions } = await PlaidService.fetchAccountsAndTransactions({ startDate, endDate, plaidAccountId: account.id, clientId });
				plaidAccounts.push(accounts[0]);
				rawTransactions.push(...transactions);
			}
		}

		const accountNameById = {};
		for (const account of plaidAccounts) {
			accountNameById[account.account_id] = account.name;
		}

		const transactions = rawTransactions.map(transaction => ({
			date: transaction.date,
			description: transaction.name || transaction.description || "",
			merchant: transaction.merchant_name,
			amount: transaction.amount,
			accountName: accountNameById[transaction.account_id] || transaction.bankAccountName || "",
			bankAccountNumber: transaction.bankAccountNumber || null,
			selected: false,
			classification: null,
			tax: "",
		}));

		return { transactions, invalidCount };
	},

	/**
	 * Gathers transactions for a GL-first generation run, where each confirmed bank account
	 * has one statement uploaded against it. CSV statements are parsed in the browser and
	 * tagged with the account name (with the file's confirmed sign convention applied); PDF
	 * statements are sent to the vision parser and saved to the engagement tagged to the
	 * chosen account, then read back. The account name on every row is what drives the split.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} params.wpfEngagementId
	 * @param {Array} params.bankAccountUploads - [{ accountName, file, kind }] kind is "spreadsheet" or "pdf"
	 * @param {Object} params.statementSignByFile - Sign-convention confirmations keyed by file key
	 * @param {Object} params.statementColumnMapByFile - Confirmed column maps keyed by file key
	 * @returns {Promise<Object>} { transactions, invalidCount, pdfImportResults }
	 */
	async collectGlModeTransactions({ clientId, wpfEngagementId, bankAccountUploads, statementSignByFile, statementColumnMapByFile = {} }) {
		let statementTransactions = [];
		let invalidCount = 0;
		let pdfImportResults = [];
		let importedAnyPdf = false;

		for (const upload of bankAccountUploads) {
			if (!upload.file) continue;

			if (upload.kind === "pdf") {
				importedAnyPdf = true;
				const result = await this.importBankStatementPdf({ clientId, engagementId: wpfEngagementId, file: upload.file, bankAccountName: upload.accountName });
				pdfImportResults.push({ name: upload.file.name, accountName: upload.accountName, ...result });
				continue;
			}

			const columnMap = statementColumnMapByFile[UtilityService.getFileKey(upload.file)] || null;
			const extracted = await this.extractTransactionsFromStatementFile({ file: upload.file, clientId, columnMap, bankAccountNumber: null, bankAccountDescription: upload.accountName });
			const sign = statementSignByFile[UtilityService.getFileKey(upload.file)];
			const isInverted = !!(sign && sign.inverted);
			const signedTransactions = this.applySignConvention({ transactions: extracted.transactions, inverted: isInverted });
			statementTransactions.push(...signedTransactions);
			invalidCount += extracted.invalidCount || 0;
		}

		const statementTableTransactions = statementTransactions.map(transaction => ({
			date: transaction.date,
			description: transaction.description || "",
			merchant: transaction.merchant,
			amount: transaction.amount,
			accountName: transaction.bankAccountName || "",
			bankAccountNumber: transaction.bankAccountNumber || null,
			selected: false,
			classification: null,
			tax: "",
		}));

		let pdfTableTransactions = [];
		if (importedAnyPdf) {
			const dbResult = await this.loadEngagementTransactions({ clientId, wpfEngagementId });
			const savedTransactions = dbResult && dbResult.transactions ? dbResult.transactions : [];
			pdfTableTransactions = this.mapEngagementTransactionsForTable({ transactions: savedTransactions });
		}

		const transactions = [...pdfTableTransactions, ...statementTableTransactions];
		return { transactions, invalidCount, pdfImportResults };
	},

	/**
	 * Converts foreign-currency transactions to CAD via the Bank of Canada rate for each
	 * transaction's date, on the API. Returns an index-aligned list of conversions.
	 *
	 * @param {Array} transactions - [{ date, amount }]
	 * @param {String} currency - ISO code (e.g. "USD")
	 * @returns {Promise<Array>} [{ cadAmount, rate, rateDate }] index-aligned to input, or []
	 */
	async convertTransactionsToCad({ transactions, currency }) {
		try {
			let userData = UserService.getUserData();
			let payload = transactions.map(transaction => ({ date: transaction.date, amount: transaction.amount }));
			let response = await Kichiri.tools.convertCurrency({ transactions: payload, currency }, {}, userData.authToken);
			let converted = response.data && Array.isArray(response.data.converted) ? response.data.converted : [];
			return converted;
		} catch (error) {
			console.log(error);
			return [];
		}
	},

	/**
	 * Guesses an account's currency from its name so a foreign account defaults to the
	 * right ISO code at import instead of silently staying CAD (which would make a
	 * cross-currency transfer read as a mismatched CAD pair and go undetected). Returns
	 * "CAD" when no currency hint is found; the guess is always user-overridable.
	 *
	 * @param {Object} params
	 * @param {String} params.accountName
	 * @returns {String} ISO currency code
	 */
	guessAccountCurrency({ accountName }) {
		const normalizedName = (accountName || "").toLowerCase();
		for (const entry of CURRENCY_NAME_HINTS) {
			const hasHint = entry.hints.some(hint => normalizedName.includes(hint));
			if (hasHint) return entry.code;
		}
		return "CAD";
	},

	/**
	 * Re-derives one bank account's currency after import: converts its transactions to
	 * CAD at the Bank of Canada rate when a foreign currency is chosen, or restores the
	 * native amounts when switched back to CAD. Lets a user fix a currency that was set
	 * wrong (or auto-defaulted wrong) without re-importing. Returns a new array.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {String} params.accountName
	 * @param {String} params.currency - ISO code
	 * @returns {Promise<Array>}
	 */
	/**
	 * Reports what one bank account's transactions currently look like, so the sign
	 * convention can be judged before it is changed: how many rows there are, and how
	 * many read as money in versus money out.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - Every transaction in the engagement
	 * @param {String} params.accountName - The bank account to describe
	 * @returns {Object} { transactionCount, moneyInCount, moneyOutCount, netAmount }
	 */
	summarizeBankAccountSigns({ transactions, accountName }) {
		const accountTransactions = (transactions || []).filter(transaction => (transaction.accountName || transaction.bankAccountName) === accountName);
		const amounts = accountTransactions.map(transaction => Number(transaction.amount) || 0);
		const moneyInCount = amounts.filter(amount => amount > 0).length;
		const moneyOutCount = amounts.filter(amount => amount < 0).length;
		const netAmount = amounts.reduce((runningTotal, amount) => runningTotal + amount, 0);
		const roundedNetAmount = Math.round(netAmount * 100) / 100;
		return { transactionCount: accountTransactions.length, moneyInCount, moneyOutCount, netAmount: roundedNetAmount };
	},

	/**
	 * Flips the sign of every transaction on one bank account, for a statement that was
	 * imported with the wrong sign convention. The pre-conversion native amount is
	 * flipped alongside the CAD amount, so a later currency change re-converts from the
	 * corrected figure instead of resurrecting the original sign. Flipping a second time
	 * restores the original amounts exactly, which is what makes this safe to undo.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - Every transaction in the engagement
	 * @param {String} params.accountName - The bank account whose signs are flipped
	 * @returns {Array}
	 */
	flipSignsForBankAccount({ transactions, accountName }) {
		const flippedTransactions = (transactions || []).map(transaction => {
			const belongsToAccount = (transaction.accountName || transaction.bankAccountName) === accountName;
			if (!belongsToAccount) return transaction;

			const flippedTransaction = { ...transaction, amount: -(Number(transaction.amount) || 0) };
			const hasNativeAmount = transaction.originalAmount !== null && transaction.originalAmount !== undefined;
			if (hasNativeAmount) flippedTransaction.originalAmount = -(Number(transaction.originalAmount) || 0);
			return flippedTransaction;
		});
		return flippedTransactions;
	},

	async setAccountCurrency({ transactions, accountName, currency }) {
		const accountTransactions = (transactions || []).filter(transaction => transaction.accountName === accountName);
		if (accountTransactions.length === 0) return transactions;

		const nativeAmountFor = transaction => (transaction.originalAmount != null ? Number(transaction.originalAmount) : Number(transaction.amount));
		const isForeign = !!currency && currency !== "CAD";

		if (!isForeign) {
			return transactions.map(transaction => {
				if (transaction.accountName !== accountName) return transaction;
				const nativeAmount = nativeAmountFor(transaction);
				const { originalAmount, exchangeRateDate, ...rest } = transaction;
				return { ...rest, amount: nativeAmount, currency: "CAD", exchange_rate: 1 };
			});
		}

		const nativeForConversion = accountTransactions.map(transaction => ({ date: transaction.date, amount: nativeAmountFor(transaction) }));
		const conversions = await this.convertTransactionsToCad({ transactions: nativeForConversion, currency });

		const conversionByTransaction = new Map();
		accountTransactions.forEach((transaction, index) => conversionByTransaction.set(transaction, conversions[index]));

		return transactions.map(transaction => {
			if (transaction.accountName !== accountName) return transaction;
			const conversion = conversionByTransaction.get(transaction);
			if (!conversion) return transaction;
			const nativeAmount = nativeAmountFor(transaction);
			return { ...transaction, amount: conversion.cadAmount, originalAmount: nativeAmount, currency, exchange_rate: conversion.rate, exchangeRateDate: conversion.rateDate };
		});
	},

	/**
	 * Converts every transaction whose account is marked in a non-CAD currency into CAD,
	 * grouping by currency so each currency is fetched once. Converted rows carry the CAD
	 * amount, the original foreign amount, the currency code, and the exchange rate used;
	 * CAD accounts (and untouched rows) are returned unchanged.
	 *
	 * @param {Array} transactions - Table-shaped rows carrying accountName, date, amount
	 * @param {Object} currencyByAccount - { [accountName]: isoCode }
	 * @returns {Promise<Array>}
	 */
	async applyCurrencyConversions({ transactions, currencyByAccount }) {
		const currencyForAccount = currencyByAccount || {};
		const isForeignCurrency = code => !!code && code !== "CAD";

		const indicesByCurrency = {};
		transactions.forEach((transaction, index) => {
			const code = currencyForAccount[transaction.accountName];
			if (!isForeignCurrency(code)) return;
			if (!indicesByCurrency[code]) indicesByCurrency[code] = [];
			indicesByCurrency[code].push(index);
		});

		const currencyCodes = Object.keys(indicesByCurrency);
		if (currencyCodes.length === 0) return transactions;

		const convertedTransactions = [...transactions];
		for (const code of currencyCodes) {
			const indices = indicesByCurrency[code];
			const foreignTransactions = indices.map(index => ({ date: transactions[index].date, amount: transactions[index].amount }));
			const conversions = await this.convertTransactionsToCad({ transactions: foreignTransactions, currency: code });
			indices.forEach((originalIndex, position) => {
				const conversion = conversions[position];
				if (!conversion) return;
				convertedTransactions[originalIndex] = {
					...transactions[originalIndex],
					amount: conversion.cadAmount,
					originalAmount: transactions[originalIndex].amount,
					currency: code,
					exchange_rate: conversion.rate,
					exchangeRateDate: conversion.rateDate,
				};
			});
		}
		return convertedTransactions;
	},

	/**
	 * Checks generated transactions against the expected bookkeeping period (the day
	 * after the prior year end through the chosen number of months) and reports how
	 * many fall outside it, with readable period boundary labels for the warning toast.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {String} params.priorYearEnd - "YYYY-MM-DD"
	 * @param {Number} params.periodMonths
	 * @returns {Object|null} { outOfRangeCount, periodStartLabel, periodEndLabel }, or null when every transaction is in range or no check applies
	 */
	findTransactionsOutsidePeriod({ transactions, priorYearEnd, periodMonths }) {
		if (!priorYearEnd || transactions.length === 0) return null;

		const priorYearEndDate = UtilityService.parseLocalDate(priorYearEnd);
		const periodStart = new Date(priorYearEndDate);
		periodStart.setDate(periodStart.getDate() + 1);
		const periodEnd = new Date(priorYearEndDate);
		periodEnd.setMonth(periodEnd.getMonth() + periodMonths);

		const isOutsidePeriod = transaction => {
			const transactionDate = new Date(transaction.date);
			const isBeforePeriodStart = transactionDate < periodStart;
			const isAfterPeriodEnd = transactionDate > periodEnd;
			const isOutside = isBeforePeriodStart || isAfterPeriodEnd;
			return isOutside;
		};
		const outOfRangeCount = transactions.filter(isOutsidePeriod).length;
		if (outOfRangeCount === 0) return null;

		return {
			outOfRangeCount,
			periodStartLabel: UtilityService.formatShortDate(periodStart),
			periodEndLabel: UtilityService.formatShortDate(periodEnd),
		};
	},

	/**
	 * Pulls a "YYYY-MM-DD" date out of a CaseWare file name, e.g.
	 * "Client 2025-08-31am.dbf" gives "2025-08-31"; returns null when the name has no date.
	 *
	 * @param {Object} params
	 * @param {String} params.fileName
	 * @returns {String|null}
	 */
	extractIsoDateFromFileName({ fileName }) {
		const lastPossibleStart = fileName.length - ISO_DATE_LENGTH;
		for (let start = 0; start <= lastPossibleStart; start++) {
			const candidate = fileName.slice(start, start + ISO_DATE_LENGTH);
			if (isIsoDateText(candidate)) return candidate;
		}
		return null;
	},

	/**
	 * Strips the trailing year or "YYYY-MM-DD" date (and any parenthesized suffix) off
	 * a CaseWare client folder name, leaving just the entity name, e.g.
	 * "Acme Ltd - 2025-08-31 (copy)" gives "Acme Ltd".
	 *
	 * @param {Object} params
	 * @param {String} params.folderName
	 * @returns {String}
	 */
	stripTrailingYearFromFolderName({ folderName }) {
		const withoutTrailingYear = folderName.replace(/\s*-?\s*\d{4}(-\d{2}-\d{2})?\s*(\(.*\))?\s*$/, "");
		const entityName = withoutTrailingYear.trim();
		return entityName;
	},

	/**
	 * Saves transactions and engagement settings for an engagement
	 * @param {Number} engagementId
	 * @param {Number} clientId
	 * @param {Array} transactions
	 * @param {String} province
	 * @param {String} fiscalYearEnd
	 * @param {String} accountantName
	 * @param {String} firmName
	 * @param {Object} csrsChecklist
	 * @param {Array} generalLedgerEntries
	 * @return {Promise<Object>}
	 */
	async saveEngagementTransactions({ engagementId, clientId, wpfEngagementId, transactions, removeTransactionIds, province, provinceChosenByAPerson, fiscalYearEnd, accountantName, firmName, csrsChecklist, generalLedgerEntries }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.saveEngagementTransactions({ engagementId, clientId, wpfEngagementId, transactions, removeTransactionIds, province, provinceChosenByAPerson, fiscalYearEnd, accountantName, firmName, csrsChecklist, generalLedgerEntries }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Loads the adjusting journal entries for a client's engagement
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async loadAdjustingEntries({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementAdjustingEntries({}, { clientId, wpfEngagementId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Saves the adjusting journal entries for a client's engagement
	 * @param {Number} clientId
	 * @param {Array} entries
	 * @return {Promise<Object>}
	 */
	async saveAdjustingEntries({ clientId, wpfEngagementId, entries }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.saveEngagementAdjustingEntries({ clientId, wpfEngagementId, entries }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	async createChartOfAccount({ clientId, accountNumber, accountName, accountType, normalBalance, canonicalCode, gifiCode }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createChartOfAccount({ clientId, accountNumber, accountName, accountType, normalBalance, canonicalCode, gifiCode }, {}, userData.authToken);
			return { account: response.data.account };
		} catch (error) {
			if (error.response && error.response.status === 409) return { conflict: true, existing: error.response.data.existing };
			if (error.response) return { error: (error.response.data && error.response.data.message) || "Failed to create account" };
			return null;
		}
	},

	/**
	 * Loads the financial statement notes for a client's engagement
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async loadFsNotes({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementFsNotes({}, { clientId, wpfEngagementId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Saves the financial statement notes for a client's engagement
	 * @param {Number} clientId
	 * @param {Array} notes
	 * @return {Promise<Object>}
	 */
	async saveFsNotes({ clientId, wpfEngagementId, notes }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.saveEngagementFsNotes({ clientId, wpfEngagementId, notes }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Builds a lookup of custom-note title keyed by note id, used to label custom notes
	 * in the preview and the downloaded PDF.
	 * @param {Array} customNotes
	 * @return {Object}
	 */
	buildCustomNoteTitleMap({ customNotes }) {
		let titleByNoteId = {};
		(customNotes || []).forEach(function (note) { titleByNoteId[note.id] = note.title || ""; });
		return titleByNoteId;
	},

	/**
	 * Builds the persisted financial-statement notes list from the edited note bodies and
	 * the user-added custom notes. Custom notes carry their own title and source "custom";
	 * standard notes use their template key as the title and source "manual". A custom note
	 * is included even when its body is still empty so the note itself survives a reload.
	 * @param {Object} noteTexts
	 * @param {Array} customNotes
	 * @return {Array}
	 */
	buildFsNotesToPersist({ noteTexts, customNotes }) {
		let customNoteById = {};
		(customNotes || []).forEach(function (note) { customNoteById[note.id] = note; });

		let noteKeysFromBodies = Object.keys(noteTexts || {});
		let noteKeysFromCustom = (customNotes || []).map(function (note) { return note.id; });
		let allNoteKeys = [...new Set([...noteKeysFromBodies, ...noteKeysFromCustom])];

		let notes = allNoteKeys.map(function (noteKey, index) {
			let customNote = customNoteById[noteKey];
			let isCustomNote = !!customNote;
			let body = (noteTexts && noteTexts[noteKey]) || "";
			return {
				noteKey: noteKey,
				title: isCustomNote ? (customNote.title || "") : noteKey,
				body: body,
				sortOrder: index,
				isIncluded: true,
				source: isCustomNote ? "custom" : "manual",
			};
		});
		return notes;
	},

	/**
	 * Shapes and saves the full financial-statement notes list (standard edits + custom notes).
	 * @param {Number} clientId
	 * @param {Number} wpfEngagementId
	 * @param {Object} noteTexts
	 * @param {Array} customNotes
	 * @return {Promise<Object>}
	 */
	async saveAllFsNotes({ clientId, wpfEngagementId, noteTexts, customNotes }) {
		let notes = this.buildFsNotesToPersist({ noteTexts, customNotes });
		return this.saveFsNotes({ clientId, wpfEngagementId, notes });
	},

	/**
	 * Records a sign-off on a client's engagement
	 * @param {Number} clientId
	 * @param {String} signoffLevel
	 * @param {String} designation
	 * @param {String} attestation
	 * @param {Object} retainedEarnings
	 * @param {Array} notesToFs
	 * @param {String} noticeToReader
	 * @return {Promise<Object>}
	 */
	async signOffEngagement({ clientId, signoffLevel, designation, attestation, retainedEarnings, notesToFs, noticeToReader }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createEngagementSignoff({ clientId, signoffLevel, designation, attestation, retainedEarnings, notesToFs, noticeToReader }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Lists a client's compilation files across years (the roll-forward chain) —
	 * powers the "view different years' working papers" navigator.
	 * @param {Number} clientId
	 * @return {Promise<Object>} { years: [...] }
	 */
	async getEngagementYears({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementYears({}, { clientId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Loads a specific compilation file's frozen deliverable (locked statements),
	 * for viewing a prior year in the navigator.
	 * @param {Number} wpfEngagementId
	 * @return {Promise<Object>}
	 */
	async getEngagementDeliverable({ wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementDeliverable({}, { wpfEngagementId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Rolls a finalized engagement forward into the next period. Creates the
	 * successor engagement (linked via prior_engagement_id) with settings and
	 * working papers carried forward; openings + comparatives derive from the
	 * prior signed deliverable.
	 * @param {Number} clientId
	 * @param {Number} [priorEngagementId]
	 * @param {Boolean} [force] - roll forward again even if already rolled forward
	 * @return {Promise<Object>}
	 */
	async rollForward({ clientId, priorEngagementId, force }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.rollForward({ clientId, priorEngagementId, force }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Loads the year-end package for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async loadYearEndPackage({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getYearEndPackage({}, { clientId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Builds the year-end package for a client
	 * @param {Number} clientId
	 * @param {Array} attachedDocumentIds
	 * @param {Number} targetFolderId
	 * @param {Boolean} force
	 * @param {String} lawyerName
	 * @param {String} lawyerFirm
	 * @param {String} lawyerEmail
	 * @param {String} lawyerAddress
	 * @param {Array} excludedDocTypes
	 * @return {Promise<Object>}
	 */
	async buildYearEndPackage({ clientId, attachedDocumentIds, targetFolderId, force, lawyerName, lawyerFirm, lawyerEmail, lawyerAddress, excludedDocTypes }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createYearEndPackage({ clientId, attachedDocumentIds, targetFolderId, force, lawyerName, lawyerFirm, lawyerEmail, lawyerAddress, excludedDocTypes }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			if (error.response) return { error: error.response.data || { message: "Build failed" } };
			return { error: { message: error.message || "Network error" } };
		}
	},

	/**
	 * Builds and downloads the year-end package as a zip file
	 * @param {Number} clientId
	 * @param {Array} attachedDocumentIds
	 * @param {Number} targetFolderId
	 * @param {Boolean} force
	 * @param {String} lawyerName
	 * @param {String} lawyerFirm
	 * @param {String} lawyerEmail
	 * @param {String} lawyerAddress
	 * @param {Array} excludedDocTypes
	 * @return {Promise<String>}
	 */
	async downloadYearEndPackageZip({ clientId, attachedDocumentIds, targetFolderId, force, lawyerName, lawyerFirm, lawyerEmail, lawyerAddress, excludedDocTypes }) {
		let response = await authedFetch(`/tools/bookkeeping/year_end_package?download=zip`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ clientId, attachedDocumentIds, targetFolderId, force, lawyerName, lawyerFirm, lawyerEmail, lawyerAddress, excludedDocTypes }),
		});
		if (response.status >= 400) {
			let body = await response.json().catch(function () { return null; });
			throw new Error(body && body.message ? body.message : "Download failed");
		}
		let blob = await response.blob();
		let disposition = response.headers.get("content-disposition") || "";
		let nameMatch = /filename="?([^"]+)"?/i.exec(disposition);
		let filename = nameMatch ? nameMatch[1] : "Year-End Package.zip";
		let url = window.URL.createObjectURL(blob);
		let a = document.createElement("a");
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
		return filename;
	},


	/**
	 * Fetches the client document requests for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async getClientRequests({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getClientRequests({}, { clientId }, userData.authToken);
			return response.data;
		} catch (error) { return { request: null, items: [] }; }
	},

	/**
	 * Adds items to a client's document request
	 * @param {Number} clientId
	 * @param {Array} items
	 * @return {Promise<Object>}
	 */
	async addClientRequestItems({ clientId, items }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createClientRequests({ clientId, items }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Updates items on a client's document request
	 * @param {Number} requestId
	 * @param {Array} items
	 * @return {Promise<Object>}
	 */
	async updateClientRequestItems({ requestId, items }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateClientRequests({ requestId, items }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Fetches the fixed asset register for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async fetchFixedAssetRegister({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.fixedAssetRegister({ client_id: clientId }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Deletes a single item from a client's document request
	 * @param {Number} itemId
	 * @return {Promise<Object>}
	 */
	async deleteClientRequestItem({ itemId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.deleteClientRequests({ itemId }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Sends a client's document request to a contact email
	 * @param {Number} requestId
	 * @param {String} contactEmail
	 * @return {Promise<Object>}
	 */
	async sendClientRequests({ requestId, contactEmail }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createClientRequestsSend({ requestId, contactEmail }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},


	/**
	 * Fetches the transactions classified to a specific account
	 * @param {Number} clientId
	 * @param {String} accountNumber
	 * @return {Promise<Object>}
	 */
	async getTransactionsForAccount({ clientId, accountNumber }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementTransactionsByAccount({}, { clientId, accountNumber }, userData.authToken);
			return response.data;
		} catch (error) { return { transactions: [] }; }
	},

	/**
	 * Turns account drill-down transactions into a debit/credit working-paper
	 * breakdown with the tax portion on each row. The amount posted to the account
	 * is the opposite of the bank movement (invertSign true) for bank-classified
	 * transactions, and the amount itself for general ledger entries. A positive
	 * posting is a debit, a negative posting a credit. Tax is read straight off the
	 * row when it is already a dollar amount (general ledger), otherwise derived
	 * from the tax code on the tax-inclusive amount.
	 *
	 * @param {Array} transactions
	 * @param {Boolean} invertSign - True for bank transactions, false for GL entries
	 * @param {Boolean} taxIsAmount - True when the tax field already holds a dollar amount
	 * @returns {Object} { rows: [{ ...transaction, debit, credit, taxAmount }], totalDebit, totalCredit, totalTax }
	 */
	buildAccountDrilldownRows({ transactions, invertSign, taxIsAmount }) {
		let totalDebit = 0;
		let totalCredit = 0;
		let totalTax = 0;

		const rows = (transactions || []).map(transaction => {
			const amount = Number(transaction.amount) || 0;
			const postedAmount = invertSign ? -amount : amount;
			const debit = postedAmount > 0 ? postedAmount : 0;
			const credit = postedAmount < 0 ? -postedAmount : 0;
			const taxAmount = taxIsAmount ? (Number(transaction.tax) || 0) : this.calculateTaxAmount({ amount, tax: transaction.tax, hasTax: true });

			totalDebit += debit;
			totalCredit += credit;
			totalTax += taxAmount;

			return { ...transaction, debit, credit, taxAmount };
		});

		return { rows, totalDebit, totalCredit, totalTax };
	},


	/**
	 * Fetches the lead sheets for a client's engagement
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async getLeadSheets({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getLeadSheets({}, { clientId, wpfEngagementId }, userData.authToken);
			return response.data;
		} catch (error) { return { leadSheets: [] }; }
	},

	/**
	 * Updates the notes and sign-off on a lead sheet
	 * @param {Number} clientId
	 * @param {String} wpRef
	 * @param {String} notes
	 * @param {Object} signoff
	 * @return {Promise<Object>}
	 */
	async updateLeadSheet({ clientId, wpRef, notes, signoff }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateLeadSheets({ clientId, wpRef, notes, signoff }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},


	/**
	 * Fetches the documents attached to a client's lead sheets
	 * @param {Number} clientId
	 * @param {String} wpRef
	 * @return {Promise<Object>}
	 */
	async getLeadSheetDocuments({ clientId, wpRef }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getLeadSheetDocuments({}, { clientId, wpRef }, userData.authToken);
			return response.data;
		} catch (error) { return { documents: [], tags: [] }; }
	},

	/**
	 * Uploads a document to a client's lead sheet
	 * @param {Number} clientId
	 * @param {String} wpRef
	 * @param {String} evidenceRequirementCode
	 * @param {String} canonicalCode
	 * @param {Number} expectedBalance
	 * @param {File} file
	 * @return {Promise<Object>}
	 */
	async uploadLeadSheetDocument({ clientId, wpRef, evidenceRequirementCode, canonicalCode, expectedBalance, file }) {
		try {
			let formData = new FormData();
			formData.append("files", file);
			formData.append("clientId", clientId);
			formData.append("wpRef", wpRef);
			formData.append("evidenceRequirementCode", evidenceRequirementCode);
			if (canonicalCode) formData.append("canonicalCode", canonicalCode);
			if (expectedBalance != null) formData.append("expectedBalance", String(expectedBalance));
			let response = await authedFetch(`/tools/bookkeeping/lead_sheet_documents`, {
				method: "POST",
				body: formData,
			});
			return await response.json();
		} catch (error) { return null; }
	},

	/**
	 * Fetches the canonical chart of accounts taxonomy
	 * @return {Promise<Array>}
	 */
	async getCanonicalTaxonomy() {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getCanonicalAccounts({}, {}, userData.authToken);
			let data = response.data;
			return data.accounts || [];
		} catch (error) { return []; }
	},

	/**
	 * Toggles whether a bank statement is excluded from reconciliation
	 * @param {Number} verificationId
	 * @param {Boolean} excluded
	 * @return {Promise<Object>}
	 */
	/**
	 * The client's chart as options a picker can offer, labelled the same way describeChartAccount
	 * labels the same account everywhere else
	 * @param {Array} clientChart - rows from getClientCoA, which arrive camelCased
	 * @return {Array}
	 */
	listChartAccountOptions({ clientChart }) {
		const rows = clientChart || [];
		const accountsWithANumber = rows.filter(row => row && row.accountNumber);
		const options = accountsWithANumber.map(row => ({ value: row.id, label: this.describeChartAccount({ account: row }) }));
		return options;
	},

	/**
	 * Records pairs of statement lines as payments between the client's own accounts.
	 *
	 * The whole batch goes in one request. Sent one at a time, each pair paid the full round trip
	 * to the server and back for work that takes milliseconds.
	 *
	 * @param {Object} params
	 * @param {Array} params.pairs  [{ outgoingTransactionId, incomingTransactionId }]
	 * @return {Promise<Object>} { results } — one verdict per pair, in the order sent
	 */
	/**
	 * Records which equity account an imported prior year's profit closes into.
	 *
	 * @param {Object} params
	 * @param {Number} params.wpfEngagementId
	 * @param {Number} params.accountId
	 * @return {Promise<Object>} { ok, reason, account } — account is null when the designation is cleared
	 */
	/**
	 * What the prior year's profit is, which equity account it closes into, and what else it could.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} [params.wpfEngagementId]
	 * @return {Promise<Object>} { earningsClose } or { error }
	 */
	/**
	 * The statements whose lines do not account for the movement they print.
	 *
	 * A statement states its own opening and closing balance, so it says how much moved. When the
	 * lines that reached the books fall short of that, money is missing from the books and stays
	 * missing for the rest of the engagement. This is a different question from the integrity
	 * check run at import, which asks whether the statement adds up against itself.
	 *
	 * @param {Object} params
	 * @param {Number} params.wpfEngagementId
	 * @return {Promise<Object>} { statements } or { error }
	 */
	async fetchStatementsThatDoNotReconcile({ wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getStatementsThatDoNotReconcile({}, { engagementId: wpfEngagementId }, userData.authToken);
			return { statements: response.data.statements || [] };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Records a line that is on the statement but never made it into the books.
	 *
	 * @param {Object} params
	 * @param {Number} params.statementId
	 * @param {Number} params.wpfEngagementId
	 * @param {String} params.transactionDate
	 * @param {String} params.description
	 * @param {Number} params.amount - signed the way the statement reads it
	 * @return {Promise<Object>} { added, reason, transactionId }
	 */
	async addLineReadOffTheStatement({ statementId, wpfEngagementId, transactionDate, description, amount }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.addLineReadOffTheStatement(
				{ statementId, engagementId: wpfEngagementId, transactionDate, description, amount },
				{},
				userData.authToken
			);
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { added: false, reason };
		}
	},

	async fetchEarningsClose({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEarningsClose({}, { clientId, wpfEngagementId }, userData.authToken);
			return { earningsClose: response.data.earningsClose };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	async designateEarningsCloseAccount({ wpfEngagementId, accountId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateEarningsClose({ wpfEngagementId, accountId }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { ok: false, reason };
		}
	},

	async recordTransferPairs({ pairs }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.recordTransferPairs({ pairs }, {}, userData.authToken);
		return response.data;
	},

	/**
	 * Undoes a transfer match so both legs post again
	 * @param {Number} transactionId  the leg that was recorded as the mirror
	 * @return {Promise<Object>}
	 */
	async releaseTransferPair({ transactionId }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.releaseTransferPair({ transactionId }, {}, userData.authToken);
		return response.data;
	},

	/**
	 * Lists each imported bank or card account with the chart account it posts to.
	 *
	 * The engagement is named rather than left to the server to guess. These links decide which
	 * chart account the second side of every transaction posts to, and a client has one
	 * engagement per year — answering for the wrong year would offer the wrong accounts to retie.
	 *
	 * A failure comes back as a reason, never as an empty list. An empty list is a real answer —
	 * this client has imported nothing yet — and returning it for a failed request tells the
	 * screen a client with accounts has none.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Number} [params.wpfEngagementId]
	 * @return {Promise<Object>} { bankAccounts } or { error }
	 */
	async fetchBankAccountLinks({ clientId, wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getBankAccountLinks({}, { clientId, wpfEngagementId }, userData.authToken);
			return { bankAccounts: response.data.bankAccounts || [] };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Points an imported bank or card account at a different account on the client's chart
	 * @param {Number} bankAccountId
	 * @param {Number} accountId
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async setBankAccountChartAccount({ bankAccountId, accountId, clientId }) {
		let userData = UserService.getUserData();
		let response = await Kichiri.tools.updateBankAccountLink({ bankAccountId, accountId, clientId }, {}, userData.authToken);
		return response.data;
	},

	async toggleStatementExclusion({ verificationId, excluded }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateLeadSheetDocuments({ verificationId, toggleExclude: excluded }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Applies a reconciliation action to a single line item
	 * @param {Number} verificationId
	 * @param {String} side
	 * @param {Number} index
	 * @param {String} action
	 * @param {Object} payload
	 * @return {Promise<Object>}
	 */
	async reconciliationAction({ verificationId, side, index, action, payload }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createReconciliationAction({ verificationId, side, index, action, payload }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Applies a reconciliation action to multiple line items
	 * @param {Number} verificationId
	 * @param {String} side
	 * @param {Array} indexes
	 * @param {String} action
	 * @param {Object} payload
	 * @return {Promise<Object>}
	 */
	async reconciliationBulkAction({ verificationId, side, indexes, action, payload }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateReconciliationAction({ verificationId, side, indexes, action, payload }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Updates the reviewed flag and tag on a lead sheet document
	 * @param {Number} documentId
	 * @param {Boolean} reviewed
	 * @param {String} tag
	 * @return {Promise<Object>}
	 */
	async updateLeadSheetDocument({ documentId, reviewed, tag }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateLeadSheetDocuments({ documentId, reviewed, tag }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Deletes a lead sheet document
	 * @param {Number} documentId
	 * @return {Promise<Object>}
	 */
	async deleteLeadSheetDocument({ documentId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.deleteLeadSheetDocuments({ documentId }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},


	/**
	 * Fetches the fixed assets and totals for a client
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async getFixedAssets({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getFixedAssets({}, { clientId }, userData.authToken);
			return response.data;
		} catch (error) { return { assets: [], totals: { cost: 0, accumulatedDepreciation: 0, nbv: 0 } }; }
	},

	/**
	 * Adds a fixed asset to a client's register
	 * @param {Number} clientId
	 * @param {Object} asset
	 * @return {Promise<Object>}
	 */
	async addFixedAsset({ clientId, asset }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.createFixedAssets({ clientId, asset }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Updates a fixed asset in a client's register
	 * @param {Number} clientId
	 * @param {Number} assetId
	 * @param {Object} updates
	 * @return {Promise<Object>}
	 */
	async updateFixedAsset({ clientId, assetId, updates }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateFixedAssets({ clientId, assetId, updates }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Deletes a fixed asset from a client's register
	 * @param {Number} clientId
	 * @param {Number} assetId
	 * @return {Promise<Object>}
	 */
	async deleteFixedAsset({ clientId, assetId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.deleteFixedAssets({ clientId, assetId }, {}, userData.authToken);
			return response.data;
		} catch (error) { return null; }
	},

	/**
	 * Fetches the available engagement templates
	 * @return {Promise<Array>}
	 */
	async getEngagementTemplates() {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getEngagementTemplates({}, {}, userData.authToken);
			let data = response.data;
			return data.templates || [];
		} catch (error) {
			return [];
		}
	},

	/**
	 * Reruns the engagement cross-checks and returns updated results
	 * @param {Number} clientId
	 * @param {Object} previousResults
	 * @param {Object} approvalContext
	 * @return {Promise<Object>}
	 */
	async rerunCrossChecks({ clientId, previousResults, approvalContext }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.rerunCrossChecks({ clientId: clientId, previousResults: previousResults, approvalContext: approvalContext }, {}, userData.authToken);
			let data = response.data;
			return {
				crossCheckResults: data.crossCheckResults || [],
				crossCheckFacts: data.crossCheckFacts || null,
				rerunTrace: data.rerunTrace || null,
				afs: data.afs || null,
			};
		} catch (error) {
			return { crossCheckResults: [], crossCheckFacts: null, rerunTrace: null, afs: null };
		}
	},

	/**
	 * Updates a single key in the engagement context
	 * @param {Number} clientId
	 * @param {String} key
	 * @param {*} value
	 * @return {Promise<Object>}
	 */
	async updateEngagementContext({ clientId, key, value }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.updateEngagementContext({ clientId: clientId, key: key, value: value }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Fetches the client's chart of accounts mapping
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async getClientCoA({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getCoaMapping({}, { clientId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Suggests canonical mappings for a client's chart of accounts
	 * @param {Number} clientId
	 * @param {Array} accounts
	 * @return {Promise<Object>}
	 */
	async mapCoaSuggest({ clientId, accounts }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.mapCoaSuggest({ clientId, accounts }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Saves the chart of accounts mappings for a client
	 * @param {Number} clientId
	 * @param {Object} mappings
	 * @return {Promise<Object>}
	 */
	async mapCoaSave({ clientId, mappings }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.mapCoaSave({ clientId, mappings }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return { error: error.message || "The chart of accounts could not be saved." };
		}
	},

	/**
	 * Materializes a WpfEngagement for a client (no transaction side effects) so
	 * opening balances can be entered before the bank import. Returns
	 * { wpfEngagementId, priorEngagementId, isBootstrap, fiscalYearEnd }.
	 * @param {Number} clientId
	 * @return {Promise<Object>}
	 */
	async ensureEngagement({ clientId, fiscalYearEnd = undefined, periodStart = undefined, periodEnd = undefined, periodType = undefined, periodReason = undefined, province = undefined }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.ensureEngagement({ clientId, fiscalYearEnd, periodStart, periodEnd, periodType, periodReason, province }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Derives a fiscal period from its end date and how many months it runs. Twelve
	 * months is a full fiscal year ending on that date; a shorter length is an
	 * interim period; a custom start covers a first year, a stub year from a changed
	 * year-end, and a final period on wind-up. No period may exceed 53 weeks
	 * (ITA 249(1)), and the length decides the period type the books are kept under.
	 *
	 * @param {Object} params
	 * @param {String} params.fiscalYearEnd - "YYYY-MM-DD"
	 * @param {Number|String} [params.periodLengthMonths] - 12, 6, 3, 1, or CUSTOM_PERIOD_LENGTH
	 * @param {String} [params.periodStartOverride] - "YYYY-MM-DD", required when the length is custom
	 * @returns {Object} { periodStart, periodEnd, periodMonths, daysInPeriod, isFullYear, isShortTaxYear, periodType, periodReason } or { error }
	 */
	derivePeriodFromYearEnd({ fiscalYearEnd, periodLengthMonths = 12, periodStartOverride = null }) {
		const yearEnd = moment(fiscalYearEnd, "YYYY-MM-DD", true);
		if (!yearEnd.isValid()) return { error: "Pick the period end date." };

		const isCustomLength = periodLengthMonths === CUSTOM_PERIOD_LENGTH;
		let periodStart;

		if (isCustomLength) {
			const chosenStart = moment(periodStartOverride, "YYYY-MM-DD", true);
			if (!chosenStart.isValid()) return { error: "Pick the first day of the period." };
			if (!chosenStart.isBefore(yearEnd)) return { error: "The period must start before it ends." };
			periodStart = chosenStart;
		} else if (periodLengthMonths === 12) {
			periodStart = this.deriveFullYearStart({ fiscalYearEnd: yearEnd.format("YYYY-MM-DD") });
		} else {
			periodStart = this.deriveStartFromMonthsBack({ fiscalYearEnd: yearEnd.format("YYYY-MM-DD"), monthsBack: periodLengthMonths });
		}

		const daysInPeriod = yearEnd.diff(periodStart, "days") + 1;
		if (daysInPeriod > LONGEST_LEGAL_TAX_YEAR_IN_DAYS) {
			return { error: "A fiscal period cannot be longer than 53 weeks (371 days)." };
		}

		const periodMonths = Math.max(1, Math.round(yearEnd.diff(periodStart, "months", true)));
		const isChosenInterim = !isCustomLength && periodLengthMonths !== 12;
		const isFullYear = !isChosenInterim && daysInPeriod >= SHORTEST_PERIOD_TREATED_AS_A_FULL_YEAR_IN_DAYS;

		return {
			periodStart: periodStart.format("YYYY-MM-DD"),
			periodEnd: yearEnd.format("YYYY-MM-DD"),
			periodMonths,
			daysInPeriod,
			isFullYear,
			isShortTaxYear: !isFullYear && !isChosenInterim,
			periodType: derivePeriodTypeFromLength({ isChosenInterim, daysInPeriod }),
			periodReason: isFullYear ? PERIOD_BASIS.FULL_YEAR : isChosenInterim ? PERIOD_BASIS.INTERIM : PERIOD_BASIS.YEAR_END_CHANGE,
		};
	},

	/**
	 * Works out which period-length radio a stored fiscal period corresponds to, so
	 * reopening the settings shows the choice that produced it rather than resetting
	 * to twelve months. A period whose dates no standard length reproduces is custom.
	 *
	 * @param {Object} params
	 * @param {Object} params.fiscalPeriod - { periodStart, periodEnd }
	 * @returns {Number|String} a month count, or CUSTOM_PERIOD_LENGTH
	 */
	matchPeriodLengthOption({ fiscalPeriod }) {
		if (!fiscalPeriod || !fiscalPeriod.periodStart || !fiscalPeriod.periodEnd) return 12;

		const standardLengths = PERIOD_LENGTH_OPTIONS.map(option => option.value).filter(value => value !== CUSTOM_PERIOD_LENGTH);
		for (const lengthMonths of standardLengths) {
			const derived = this.derivePeriodFromYearEnd({ fiscalYearEnd: fiscalPeriod.periodEnd, periodLengthMonths: lengthMonths });
			if (!derived.error && derived.periodStart === fiscalPeriod.periodStart) return lengthMonths;
		}
		return CUSTOM_PERIOD_LENGTH;
	},

	/**
	 * The first day of a period that runs a whole number of months back from its end
	 * date — a 3-month period ending Jun 30 starts Apr 1.
	 *
	 * @param {Object} params
	 * @param {String} params.fiscalYearEnd - "YYYY-MM-DD"
	 * @param {Number} params.monthsBack
	 * @returns {Object} a moment for the first day of the period
	 */
	deriveStartFromMonthsBack({ fiscalYearEnd, monthsBack }) {
		const yearEnd = moment(fiscalYearEnd, "YYYY-MM-DD", true);
		const isMonthEnd = yearEnd.clone().endOf("month").isSame(yearEnd, "day");
		if (isMonthEnd) {
			const startOfPeriodMonth = yearEnd.clone().subtract(monthsBack - 1, "months").startOf("month");
			return startOfPeriodMonth;
		}
		const dayAfterPriorPeriodEnd = yearEnd.clone().subtract(monthsBack, "months").add(1, "day").startOf("day");
		return dayAfterPriorPeriodEnd;
	},

	/**
	 * The first day of a full year ending on the given year-end: the day after the
	 * previous year-end. A month-end year-end is treated as following a month-end,
	 * so an end-of-February year always starts March 1 and the leap day belongs to
	 * exactly one fiscal year.
	 *
	 * @param {Object} params
	 * @param {String} params.fiscalYearEnd - "YYYY-MM-DD"
	 * @returns {Object} a moment for the first day of the period
	 */
	deriveFullYearStart({ fiscalYearEnd }) {
		const yearEnd = moment(fiscalYearEnd, "YYYY-MM-DD", true);
		const isMonthEndYearEnd = yearEnd.clone().endOf("month").isSame(yearEnd, "day");
		if (isMonthEndYearEnd) {
			const priorYearEndAtMonthEnd = yearEnd.clone().subtract(1, "year").endOf("month").startOf("day");
			return priorYearEndAtMonthEnd.add(1, "day");
		}
		return yearEnd.clone().subtract(1, "year").add(1, "day").startOf("day");
	},

	/**
	 * Splits transactions into those inside the fiscal period and those outside it —
	 * the hard cutoff. Rows after the year-end belong to the next fiscal year and rows
	 * before the period started belong to the prior one; neither may enter these books.
	 * Excluded rows are returned, never discarded, so they can be reported and kept on
	 * file for the neighbouring period.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions - [{ date: "YYYY-MM-DD", ... }]
	 * @param {String} params.periodStart - "YYYY-MM-DD"
	 * @param {String} params.periodEnd - "YYYY-MM-DD"
	 * @returns {Object} { kept, excludedBefore, excludedAfter }
	 */
	filterTransactionsToPeriod({ transactions, periodStart, periodEnd }) {
		const kept = [];
		const excludedBefore = [];
		const excludedAfter = [];
		const undated = [];

		for (const transaction of transactions || []) {
			const transactionDate = String(transaction.date || "");
			if (!transactionDate) {
				undated.push(transaction);
				kept.push(transaction);
				continue;
			}
			if (periodStart && transactionDate < periodStart) {
				excludedBefore.push(transaction);
				continue;
			}
			if (periodEnd && transactionDate > periodEnd) {
				excludedAfter.push(transaction);
				continue;
			}
			kept.push(transaction);
		}

		return { kept, excludedBefore, excludedAfter, undated };
	},

	/**
	 * Builds the audit-trail block for the review report describing which imported
	 * transactions were held out of the books by the fiscal-period cutoff, so a
	 * reviewer can trace every excluded row (date, description, amount, account)
	 * instead of trusting a bare count.
	 *
	 * @param {Object} params
	 * @param {Object} [params.fiscalPeriod] - { periodStart, periodEnd }
	 * @param {Object} [params.periodExclusions] - { excludedBefore, excludedAfter }
	 * @returns {Object|null} { periodStart, periodEnd, excludedBeforeCount, excludedAfterCount, excludedTransactions } or null
	 */
	buildPeriodExclusionSummary({ fiscalPeriod, periodExclusions }) {
		if (!periodExclusions) return null;

		const excludedBefore = periodExclusions.excludedBefore || [];
		const excludedAfter = periodExclusions.excludedAfter || [];
		if (excludedBefore.length === 0 && excludedAfter.length === 0) return null;

		const describeExcludedRow = (transaction, sideOfPeriod) => ({
			date: transaction.date || null,
			description: transaction.description || "",
			amount: transaction.amount,
			accountName: transaction.accountName || "",
			sideOfPeriod,
		});

		const beforeRows = excludedBefore.map(transaction => describeExcludedRow(transaction, "before_period"));
		const afterRows = excludedAfter.map(transaction => describeExcludedRow(transaction, "after_period"));

		return {
			periodStart: (fiscalPeriod && fiscalPeriod.periodStart) || periodExclusions.periodStart || null,
			periodEnd: (fiscalPeriod && fiscalPeriod.periodEnd) || periodExclusions.periodEnd || null,
			excludedBeforeCount: excludedBefore.length,
			excludedAfterCount: excludedAfter.length,
			excludedTransactions: [...beforeRows, ...afterRows],
		};
	},

	/**
	 * Turns the verification modal's parses into the rows the statement registry
	 * expects, so verified statements are filed on the client's record at import.
	 * Only parses that actually produced data (ok/partial) and carry a file hash
	 * are registrable; a statement the registry already served back is skipped —
	 * re-filing it would only restate what is already on file.
	 *
	 * @param {Object} params
	 * @param {Array} params.parsesWithAccounts - [{ parse, accountLabel }]
	 * @returns {Array} [{ fileHash, fileName, accountLabel, parse }]
	 */
	buildStatementRegistrationRows({ parsesWithAccounts }) {
		const registrableParses = (parsesWithAccounts || []).filter(({ parse }) => {
			const producedData = parse.status === "ok" || parse.status === "partial";
			return producedData && parse.fileHash && !parse.fromRegistry;
		});

		const rows = registrableParses.map(({ parse, accountLabel }) => {
			const bankTieCheck = parse.integrity ? parse.integrity.opening_to_closing_check : null;
			const cardTieCheck = parse.integrity ? parse.integrity.balance_arithmetic_check : null;
			const tieCheck = bankTieCheck || cardTieCheck || null;
			const integrityPass = !!(tieCheck && tieCheck.ok);
			const integrityDelta = tieCheck && tieCheck.delta !== undefined ? tieCheck.delta : null;

			return {
				fileHash: parse.fileHash,
				fileName: parse.fileName,
				accountLabel: accountLabel || null,
				parse: {
					statementType: parse.statementType || "bank",
					bankName: parse.bankName || null,
					accountNumberMasked: parse.accountNumberMasked || null,
					periodStart: parse.periodStart || null,
					periodEnd: parse.periodEnd || null,
					openingBalance: parse.openingBalance,
					closingBalance: parse.closingBalance,
					integrityPass,
					integrityDelta,
					parseStatus: parse.status || "ok",
					source: parse.source || "pdf_import",
					transactions: (parse.transactions || []).map(transaction => ({
						date: transaction.date || null,
						description: transaction.description || "",
						amount: transaction.amount,
					})),
				},
			};
		});
		return rows;
	},

	/**
	 * Builds a registry row for transactions that did NOT come from a parsed PDF —
	 * a CSV or spreadsheet import, or a bank feed. Without this, rows the fiscal
	 * cutoff holds out of these books exist nowhere the next period can reach, and
	 * a statement that straddles the year-end loses its post-cutoff weeks entirely.
	 *
	 * These carry no printed balances to tie to, so they are filed with the source
	 * that produced them and no integrity claim, rather than a claim that was never
	 * checked. The identity hash is derived from the client, source and content so
	 * re-importing the same file does not duplicate it.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.source - e.g. "csv_import", "spreadsheet_import", "bank_feed"
	 * @param {String} params.sourceName - the file name or feed name, shown to the user
	 * @param {String} [params.accountLabel]
	 * @param {Array} params.transactions - [{ date, description, amount }]
	 * @returns {Object|null} a registration row, or null when there is nothing to file
	 */
	/**
	 * Names an import for the statement registry's file_name column, which holds
	 * 255 characters. A long list of file names is summarised rather than truncated
	 * mid-name, so the label always reads as a whole thought.
	 *
	 * @param {Object} params
	 * @param {Array<String>} [params.fileNames]
	 * @returns {String}
	 */
	buildImportSourceLabel({ fileNames }) {
		const names = (fileNames || []).filter(Boolean);
		if (names.length === 0) return "Imported transactions";

		const joinedNames = names.join(", ");
		if (joinedNames.length <= REGISTRY_FILE_NAME_LIMIT) return joinedNames;

		const summary = `${names[0]} and ${names.length - 1} more file${names.length - 1 === 1 ? "" : "s"}`;
		if (summary.length <= REGISTRY_FILE_NAME_LIMIT) return summary;
		return `${names.length} imported files`;
	},

	buildNonStatementRegistrationRow({ clientId, source, sourceName, accountLabel = null, transactions }) {
		const datedTransactions = (transactions || []).filter(transaction => !!transaction.date);
		if (datedTransactions.length === 0) return null;

		const contentFingerprint = datedTransactions
			.map(transaction => [transaction.date, transaction.description || "", Number(transaction.amount) || 0].join("|"))
			.join("~");
		const fileHash = UtilityService.buildStableHash({ text: `${clientId}|${source}|${sourceName}|${contentFingerprint}` });

		const sortedDates = datedTransactions.map(transaction => String(transaction.date)).sort();

		return {
			fileHash,
			fileName: sourceName,
			accountLabel,
			parse: {
				statementType: "bank",
				bankName: null,
				accountNumberMasked: null,
				periodStart: sortedDates[0],
				periodEnd: sortedDates[sortedDates.length - 1],
				openingBalance: null,
				closingBalance: null,
				integrityPass: false,
				integrityDelta: null,
				parseStatus: "unverified",
				source,
				transactions: datedTransactions.map(transaction => ({
					date: transaction.date,
					description: transaction.description || "",
					amount: transaction.amount,
				})),
			},
		};
	},

	/**
	 * Files verified statements in the per-client registry so they persist across
	 * fiscal years and are never re-parsed (or re-paid-for) again.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Array} params.statements - rows from buildStatementRegistrationRows
	 * @return {Promise<Object>} { results } or { error }
	 */
	async registerStatements({ clientId, statements }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.registerStatements({ clientId, statements }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Reads the client's statement registry — the statement shelf (no view), a
	 * date window of transactions (view "window"), or post-year-end subsequent
	 * activity (view "subsequent").
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} [params.view] - "window" | "subsequent" | undefined for the shelf
	 * @param {String} [params.windowStart]
	 * @param {String} [params.windowEnd]
	 * @param {String} [params.periodEnd]
	 * @param {Number} [params.windowDays]
	 * @return {Promise<Object>} view payload or { error }
	 */
	async readStatementRegistry({ clientId, view = undefined, windowStart = undefined, windowEnd = undefined, periodEnd = undefined, windowDays = undefined }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.readStatementRegistry({ clientId, view, windowStart, windowEnd, periodEnd, windowDays }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			return response.data;
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Pulls the fiscal period's slice of transactions from statements already on
	 * file for this client, leaving out statements that were imported in this
	 * session (their transactions are already in the books being generated).
	 * This is how a statement that straddled LAST year's cutoff contributes its
	 * early-days rows to THIS year without being uploaded again.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {String} params.periodStart - "YYYY-MM-DD"
	 * @param {String} params.periodEnd - "YYYY-MM-DD"
	 * @param {Array<String>} [params.sessionFileHashes] - hashes of statements imported this session
	 * @param {Array<Object>} [params.sessionTransactions] - this session's rows, so a registry row that matches one exactly (same date, description, amount) is dropped instead of duplicated
	 * @return {Promise<Object>} { transactions, statementsUsed, duplicateCount } or { error }; a row is taken at most once even when two filed sources both contain it, so an overlapping re-import can never enter the books twice
	 */
	async collectRegistryTransactionsForPeriod({ clientId, periodStart, periodEnd, sessionFileHashes = [], sessionTransactions = [] }) {
		const window = await this.readStatementRegistry({ clientId, view: "window", windowStart: periodStart, windowEnd: periodEnd });
		if (!window || window.error) return { error: (window && window.error) || "the statement registry could not be read" };

		const sessionHashes = new Set(sessionFileHashes);
		const statementsNotInSession = (window.statements || []).filter(statement => !sessionHashes.has(statement.fileHash));
		const statementIdsNotInSession = new Set(statementsNotInSession.map(statement => statement.id));

		const describeRowForMatching = row => [row.date || "", row.description || "", Number(row.amount) || 0].join("|");
		const sessionRowKeys = new Set(sessionTransactions.map(describeRowForMatching));
		const rowKeysAlreadyTaken = new Set();

		let duplicateCount = 0;
		const transactions = [];
		for (const transaction of window.transactions || []) {
			if (!statementIdsNotInSession.has(transaction.statementId)) continue;

			const registryRow = {
				date: transaction.date,
				description: transaction.description,
				merchant: "",
				amount: transaction.amount,
				accountName: transaction.accountLabel || "Bank",
				bankAccountNumber: null,
				selected: false,
				classification: null,
				tax: "",
				fromRegistry: true,
				registryStatementId: transaction.statementId,
			};
			const rowKey = describeRowForMatching(registryRow);
			const isAlreadyInSession = sessionRowKeys.has(rowKey);
			const isAlreadyTakenFromRegistry = rowKeysAlreadyTaken.has(rowKey);
			if (isAlreadyInSession || isAlreadyTakenFromRegistry) {
				duplicateCount += 1;
				continue;
			}
			rowKeysAlreadyTaken.add(rowKey);
			transactions.push(registryRow);
		}

		const usedStatementIds = new Set(transactions.map(transaction => transaction.registryStatementId));
		const statementsUsed = statementsNotInSession.filter(statement => usedStatementIds.has(statement.id));
		return { transactions, statementsUsed, duplicateCount };
	},

	/**
	 * Saves a manual / imported prior-year trial balance (opening balances +
	 * comparatives) for a bootstrap engagement. Server rejects an unbalanced TB.
	 * @param {Number} wpfEngagementId
	 * @param {String} source - manual | csv_tb | excel_tb | qbo_tb
	 * @param {Array} lines - [{ canonical_code, accountNumber, accountDescription, debit, credit }]
	 * @return {Promise<Object>}
	 */
	async saveOpeningBalances({ wpfEngagementId, source, lines }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.saveOpeningBalances({ wpfEngagementId, source, lines }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Loads saved prior-year figures for an engagement (split into debit/credit).
	 * @param {Number} wpfEngagementId
	 * @return {Promise<Object>}
	 */
	async getOpeningBalances({ wpfEngagementId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getOpeningBalances({}, { wpfEngagementId }, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Determines tax codes for a batch of transactions
	 * @param {String} province
	 * @param {Boolean} isGstRegistered
	 * @param {Array} transactions
	 * @return {Promise<Array>}
	 */
	async batchTaxCodes({ province, isGstRegistered, transactions, wpfEngagementId, industry }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.batchTaxCodes({ province, isGstRegistered, transactions, wpfEngagementId, industry }, {}, userData.authToken);
			let json = response.data;
			return json && json.results ? json.results : [];
		} catch (error) {
			return [];
		}
	},

	/**
	 * Retrieves tax rate for an account from the classification API.
	 *
	 * @param {String} account
	 * @param {String} province
	 * @param {String} industry
	 * @param {String} accountClass
	 * @returns {Promise<Object>} Tax rate data
	 */
	async getTaxRates({ clientId, account, province, industry, accountClass }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.classifyAccountsTax(
				{},
				{
					clientId,
					account,
					province,
					industry,
					...(accountClass ? { accountClass } : {}),
				},
				userData.authToken
			);

			return response.data;
		} catch (error) {
			console.log(error);
		}

		return null;
	},

	/**
	 * Infers the client's industry from the entity name via the classification API.
	 *
	 * @param {String} entityName
	 * @returns {Promise<Object>} Inferred industry result, or null on error
	 */
	async inferIndustry({ entityName }) {

		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.inferIndustry({ entityName }, {}, userData.authToken);

			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Asks the API's AI which general ledger account names are real-world bank or credit
	 * card accounts, so the wizard can pre-select them for statement upload.
	 *
	 * @param {Number} clientId
	 * @param {Array<String>} accountNames - Account names from the imported general ledger
	 * @returns {Promise<Array<String>|null>} The account names the AI judged to be bank accounts, or null when the AI call failed
	 */
	async identifyLikelyBankAccounts({ clientId, accountNames }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.identifyBankAccountsFromGl({ clientId, accountNames }, {}, userData.authToken);
			if (!response) return { error: REQUEST_FAILED_MESSAGE };
			let bankAccountNames = response.data && Array.isArray(response.data.bankAccountNames) ? response.data.bankAccountNames : [];
			return { bankAccountNames };
		} catch (error) {
			const serverBody = error && error.response && error.response.data ? error.response.data : {};
			const reason = serverBody.error || serverBody.message || error.message || "the server could not be reached";
			return { error: reason };
		}
	},

	/**
	 * Suggests the HST/GST control account from the chart of accounts
	 * @param {Array} accounts
	 * @return {Promise<Object>}
	 */
	async suggestHstAccount({ accounts }) {

		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.suggestHstAccount({ accounts }, {}, userData.authToken);

			return response.data;
		} catch (error) {
			console.log(error);
			return null;
		}
	},

	/**
	 * Uploads CaseWare reference files for transaction classification.
	 *
	 * @param {String} clientId
	 * @param {Array} files
	 * @param {Array} transactions
	 * @returns {Promise<Object>} Server response with trial balance and classifications
	 */
	async uploadCasewareReference({ clientId, files, transactions, wpfEngagementId = null }) {

		try {
			await ClientService.fetchClient({ clientId });

			let formData = new FormData();

			for (let file of files) {
				formData.append("files", file);
			}

			if (transactions && transactions.length > 0) {
				formData.append("transactions", JSON.stringify(transactions));
			}

			if (wpfEngagementId) formData.append("wpfEngagementId", String(wpfEngagementId));

			let response = await authedFetch(`/tools/bookkeeping/upload_caseware_reference?clientId=${clientId}`, {
				method: "POST",
				body: formData,
				headers: {
					Accept: "application/json",
				},
			});

			return await response.json();
		} catch (error) {
			console.log(error);
		}

		return null;
	},


	/**
	 * Extracts transactions from one bank statement file, CSV or Excel. When the caller
	 * has a confirmed column map it is used as-is; otherwise the columns are detected from
	 * the file so a statement still imports without anyone mapping it by hand.
	 *
	 * @param {Object} params
	 * @param {File} params.file - The .csv or .xlsx statement
	 * @param {Number} [params.clientId] - Required only for Excel, which is read on the API
	 * @param {Object} [params.columnMap] - Confirmed { date, description, amount, moneyOut, moneyIn }
	 * @param {String} [params.bankAccountNumber] - Account number from CaseWare
	 * @param {String} [params.bankAccountDescription] - Account description from CaseWare
	 * @returns {Promise<Object>} { transactions, invalidCount } or { error } when the file could not be read
	 */
	async extractTransactionsFromStatementFile({ file, clientId = null, columnMap = null, bankAccountNumber = null, bankAccountDescription = null }) {
		try {
			const readResult = await this.readStatementGrid({ clientId, file });
			if (readResult.error) return { transactions: [], invalidCount: 0, error: readResult.error };

			const { hasHeadings, headers, dataRows } = this.detectStatementHeadings({ grid: readResult.grid });
			const confirmedOrGuessedColumnMap = columnMap || this.guessStatementColumnMap({ headers, dataRows, hasHeadings });
			const bankAccountName = bankAccountDescription || UtilityService.getFileNameWithoutExtension(file.name);

			const extracted = this.extractTransactionsFromStatementGrid({
				dataRows,
				columnMap: confirmedOrGuessedColumnMap,
				bankAccountName,
				bankAccountNumber,
			});
			return extracted;
		} catch (error) {
			console.log(error);
			return { transactions: [], invalidCount: 0, error: COULD_NOT_READ_STATEMENT };
		}
	},

	/**
	 * Reads a statement and returns its first deposit (positive amount) and first payment
	 * (negative amount) so the user can confirm the file's sign convention. Parses through
	 * the same path as the real import, with the same column map, so the preview matches.
	 *
	 * @param {Object} params
	 * @param {File} params.file - The .csv or .xlsx statement
	 * @param {Number} [params.clientId] - Required only for Excel, which is read on the API
	 * @param {Object} [params.columnMap] - Confirmed column map, when the user has set one
	 * @returns {Promise<Object>} { deposit, payment } — each { description, amount } or null
	 */
	async sampleSignConvention({ file, clientId = null, columnMap = null }) {
		let result = await this.extractTransactionsFromStatementFile({ file, clientId, columnMap });
		let transactions = result && result.transactions ? result.transactions : [];

		let firstDeposit = transactions.find(transaction => hasNonZeroAmount(transaction) && transaction.amount > 0);
		let firstPayment = transactions.find(transaction => hasNonZeroAmount(transaction) && transaction.amount < 0);

		return { deposit: toSignSample(firstDeposit), payment: toSignSample(firstPayment) };
	},

	/**
	 * Flips every transaction amount when the file's convention is inverted (positive
	 * amounts represent money out); otherwise returns the transactions unchanged.
	 *
	 * @param {Array} transactions
	 * @param {Boolean} inverted
	 * @returns {Array}
	 */
	applySignConvention({ transactions, inverted }) {
		if (!inverted) return transactions;
		const flippedTransactions = transactions.map(flipTransactionAmount);
		return flippedTransactions;
	},

	/**
	 * Builds a general ledger reference from mapped CSV rows: the {account, description}
	 * pairs the NLP matcher trains on, the full per-row entries the reviewer can browse,
	 * the distinct account list, and — when the tax and debit/credit columns are mapped —
	 * a default tax code per account. That tax rate is reverse-engineered straight from the
	 * general ledger (each account's tax amount as a percentage of its net amount), so the
	 * general ledger, not the province, is the source of the prior-year tax rates.
	 *
	 * Entry amounts use the same cash-flow convention as the bank-statement transactions
	 * (money in positive): credits (revenue) are positive and debits (expenses) are
	 * negative, so the prior-year figures line up sign-for-sign with the current year.
	 *
	 * @param {Array<Array<string>>} rows - CSV data rows (header row already removed)
	 * @param {number} accountColumnIndex
	 * @param {number} descriptionColumnIndex
	 * @param {number} [taxColumnIndex=-1]
	 * @param {number} [debitColumnIndex=-1]
	 * @param {number} [creditColumnIndex=-1]
	 * @param {number} [dateColumnIndex=-1]
	 * @param {number} [accountNumberColumnIndex=-1] - when mapped, the account number comes
	 *   straight from this column; otherwise it falls back to the leading digits of the account name
	 * @returns {Object} { generalLedgerReference: [{ account, description }], generalLedgerEntries: [{ date, account, accountNumber, description, amount, tax }], accountList: string[], accountTaxes: { [account]: taxCode } }
	 */
	buildGeneralLedgerReference({ rows, accountColumnIndex, descriptionColumnIndex, taxColumnIndex = -1, debitColumnIndex = -1, creditColumnIndex = -1, dateColumnIndex = -1, accountNumberColumnIndex = -1 }) {
		let generalLedgerReference = [];
		let generalLedgerEntries = [];
		let seenPairs = new Set();
		let accountList = [];
		let seenAccounts = new Set();
		let accountTotals = {};

		for (let row of rows) {
			let account = UtilityService.toTrimmedText(row[accountColumnIndex]);
			let description = UtilityService.toTrimmedText(row[descriptionColumnIndex]);
			if (!account) continue;

			if (!seenAccounts.has(account)) {
				seenAccounts.add(account);
				accountList.push(account);
			}

			let debitAmount = debitColumnIndex >= 0 ? UtilityService.parseAmount(row[debitColumnIndex]) : 0;
			let creditAmount = creditColumnIndex >= 0 ? UtilityService.parseAmount(row[creditColumnIndex]) : 0;
			let cashFlowAmount = Math.abs(creditAmount) - Math.abs(debitAmount);
			let taxValue = taxColumnIndex >= 0 ? UtilityService.parseAmount(row[taxColumnIndex]) : 0;
			let entryDate = dateColumnIndex >= 0 ? UtilityService.toTrimmedText(row[dateColumnIndex]) : "";
			let mappedAccountNumber = accountNumberColumnIndex >= 0 ? UtilityService.toTrimmedText(row[accountNumberColumnIndex]) : "";
			let accountNumber = mappedAccountNumber || readLeadingDigits({ text: account });

			if (taxColumnIndex >= 0) {
				let netAmount = Math.abs(debitAmount) || Math.abs(creditAmount);
				if (!accountTotals[account]) accountTotals[account] = { net: 0, tax: 0 };
				accountTotals[account].net += netAmount;
				accountTotals[account].tax += Math.abs(taxValue);
			}

			generalLedgerEntries.push({ date: entryDate, account, accountNumber, description, amount: cashFlowAmount, tax: Math.abs(taxValue) });

			if (!description) continue;
			let pairKey = account + "||" + description;
			if (seenPairs.has(pairKey)) continue;
			seenPairs.add(pairKey);
			generalLedgerReference.push({ account, description });
		}

		const accountTaxes = deriveAccountTaxes({ accountTotals });
		return { generalLedgerReference, generalLedgerEntries, accountList, accountTaxes };
	},

	/**
	 * Categorizes transactions by fuzzy-matching their descriptions against a general
	 * ledger reference, reusing the CaseWare NLP matcher on the API.
	 *
	 * @param {String} clientId
	 * @param {Array} generalLedgerReference - [{ account, description }]
	 * @param {Array} transactions - [{ description }]
	 * @returns {Promise<Array>} categorized transactions (index-aligned to input), or []
	 */
	async categorizeTransactionsFromGeneralLedger({ clientId, generalLedgerReference, transactions }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.categorizeFromGl({ clientId, generalLedgerReference, transactions }, {}, userData.authToken);
			let json = response.data;
			let result = json && json.data ? json.data : json;
			return result && result.categorizedTransactions ? result.categorizedTransactions : [];
		} catch (error) {
			console.log(error);
			return [];
		}
	},

	/**
	 * Builds a custom tax option like "Landscaping 7.5%" from a name and rate, enforcing
	 * the constraints the rest of the tax flow depends on: the rate must be the first
	 * number in the string (so the name can't contain digits) and the whole code must fit
	 * the 20-character tax_code column.
	 *
	 * @param {String} name
	 * @param {String|Number} ratePercent
	 * @returns {Object} { option } on success, { error } with a user-facing message otherwise
	 */
	buildCustomTaxRateOption({ name, ratePercent }) {
		const cleanName = UtilityService.toTrimmedText(name);
		const rate = parseFloat(ratePercent);
		const nameCharacters = [...cleanName];
		const nameContainsDigit = nameCharacters.some(isDigitCharacter);

		if (!cleanName) return { error: "Give the rate a name." };
		if (nameContainsDigit) return { error: "The name can't contain numbers — the rate is read from the first number in the code." };
		if (isNaN(rate) || rate < 0 || rate > 100) return { error: "Enter a rate between 0 and 100." };

		const option = `${cleanName} ${rate}%`;
		if (option.length > 20) return { error: "Keep the name shorter — tax codes are limited to 20 characters." };

		return { option };
	},

	/**
	 * Splits a custom tax option like "Landscaping 7.5%" back into its name and rate,
	 * for loading into the edit inputs.
	 *
	 * @param {String} option
	 * @returns {Object} { name, ratePercent }
	 */
	parseCustomTaxRateOption({ option }) {
		const rawOption = UtilityService.toTrimmedText(option);
		const words = rawOption.split(" ");
		const lastWord = words[words.length - 1] || "";
		const lastWordIsTheRate = lastWord.endsWith("%");
		const wordsWithoutRate = words.slice(0, -1);

		const ratePercent = lastWordIsTheRate ? lastWord.slice(0, -1) : "";
		const name = lastWordIsTheRate ? wordsWithoutRate.join(" ") : words.join(" ");
		return { name, ratePercent };
	},

	/**
	 * Replaces one tax code with another on every transaction that carries it;
	 * other transactions are untouched.
	 *
	 * @param {Array} transactions
	 * @param {String} oldTaxCode
	 * @param {String} newTaxCode
	 * @returns {Array}
	 */
	/**
	 * Returns the transaction list with one transaction's tax replaced (an empty tax
	 * disables tax for that transaction), plus the updated transaction itself so
	 * callers holding the old reference can swap to the new one. Changing the tax
	 * clears any reviewer sign-off on the transaction, since the reviewed values
	 * no longer hold.
	 *
	 * @param {Array} transactions
	 * @param {Object} transaction - The row to change, matched by identity
	 * @param {String} tax - Tax code, or "" for no tax
	 * @returns {Object} { updatedTransactions, updatedTransaction }
	 */
	setTaxOnTransaction({ transactions, transaction, tax }) {
		let updatedTransaction = null;
		const updatedTransactions = transactions.map(row => {
			if (row !== transaction) return row;
			updatedTransaction = { ...row, tax, reviewed_by: null, reviewed_at: null };
			return updatedTransaction;
		});
		return { updatedTransactions, updatedTransaction };
	},

	replaceTaxCodeOnTransactions({ transactions, oldTaxCode, newTaxCode }) {
		const updatedTransactions = transactions.map(transaction => (transaction.tax === oldTaxCode ? { ...transaction, tax: newTaxCode } : transaction));
		return updatedTransactions;
	},

	/**
	 * Reads the client's saved custom tax rates.
	 *
	 * @param {String} clientId
	 * @returns {Array<String>} custom tax options like "Landscaping 7.5%"
	 */
	getCustomTaxRates({ clientId }) {
		const storageKey = `bookkeeping_custom_tax_rates_${clientId}`;
		const storedRates = localStorage.getItem(storageKey) || "[]";
		const customTaxRates = JSON.parse(storedRates);
		return customTaxRates;
	},

	/**
	 * Persists the client's custom tax rates.
	 *
	 * @param {String} clientId
	 * @param {Array<String>} customTaxRates
	 */
	saveCustomTaxRates({ clientId, customTaxRates }) {
		const storageKey = `bookkeeping_custom_tax_rates_${clientId}`;
		const serializedRates = JSON.stringify(customTaxRates);
		localStorage.setItem(storageKey, serializedRates);
	},

	/**
	 * Reads the preparer's saved column layout for a client's transaction table: the
	 * widths they dragged and the columns they hid. A display preference only — it never
	 * changes what is imported, classified or reported.
	 *
	 * @param {Object} params
	 * @param {String|Number} params.clientId
	 * @returns {Object} { columnWidths, hiddenColumns }
	 */
	getTableColumnPreferences({ clientId }) {
		const storageKey = `bookkeeping_table_columns_${clientId}`;
		const storedPreferences = localStorage.getItem(storageKey);
		if (!storedPreferences) return { columnWidths: {}, hiddenColumns: [] };

		try {
			const preferences = JSON.parse(storedPreferences);
			const columnWidths = preferences.columnWidths || {};
			const hiddenColumns = preferences.hiddenColumns || [];
			return { columnWidths, hiddenColumns };
		} catch (error) {
			return { columnWidths: {}, hiddenColumns: [] };
		}
	},

	/**
	 * Saves the preparer's column layout for a client's transaction table.
	 *
	 * @param {Object} params
	 * @param {String|Number} params.clientId
	 * @param {Object} params.columnWidths - { [columnKey]: widthInPixels }
	 * @param {Array} params.hiddenColumns - Column keys the preparer has hidden
	 * @returns {void}
	 */
	saveTableColumnPreferences({ clientId, columnWidths, hiddenColumns }) {
		const storageKey = `bookkeeping_table_columns_${clientId}`;
		const serializedPreferences = JSON.stringify({ columnWidths, hiddenColumns });
		localStorage.setItem(storageKey, serializedPreferences);
	},

	/**
	 * Builds the CSS grid track list for the transaction table from the columns the
	 * preparer can currently see, so the header, every data row and the totals row stay
	 * aligned. Hidden columns contribute no track — their cells are hidden in CSS, which
	 * keeps the track count and the visible cell count in step.
	 *
	 * @param {Object} params
	 * @param {Array} params.columns - [{ key, width }] in display order
	 * @param {Object} params.columnWidths - Preparer overrides, { [columnKey]: widthInPixels }
	 * @param {Array} params.hiddenColumns - Column keys to leave out
	 * @param {Number} params.leadingWidth - Width of the leading checkbox column
	 * @returns {String} e.g. "36px 100px 240px 108px minmax(0, 1fr)"
	 */
	buildTableGridTemplate({ columns, columnWidths, hiddenColumns, leadingWidth }) {
		const visibleColumns = columns.filter(column => !hiddenColumns.includes(column.key));
		const widths = visibleColumns.map(column => {
			const overriddenWidth = columnWidths[column.key];
			const width = overriddenWidth || column.width;
			return `${width}px`;
		});
		const trailingSpacer = "minmax(0, 1fr)";
		const tracks = [`${leadingWidth}px`, ...widths, trailingSpacer];
		return tracks.join(" ");
	},

	/**
	 * Reads the client's keyword→tax rules. A per-client convenience the accountant
	 * records: any transaction whose description or merchant contains the keyword gets
	 * the rule's tax. The result is always reviewable and overridable per transaction.
	 *
	 * @param {String} clientId
	 * @returns {Array<Object>} rules like { keyword: "usa", tax: "Exempt" }
	 */
	getTaxRules({ clientId }) {
		const storageKey = `bookkeeping_tax_rules_${clientId}`;
		const storedRules = localStorage.getItem(storageKey) || "[]";
		const taxRules = JSON.parse(storedRules);
		return taxRules;
	},

	/**
	 * Persists the client's keyword→tax rules.
	 *
	 * @param {String} clientId
	 * @param {Array<Object>} taxRules - each { keyword, tax }
	 */
	saveTaxRules({ clientId, taxRules }) {
		const storageKey = `bookkeeping_tax_rules_${clientId}`;
		const serializedRules = JSON.stringify(taxRules);
		localStorage.setItem(storageKey, serializedRules);
	},

	/**
	 * True when `phrase` appears as whole word(s) in `text`. Compares token sequences,
	 * so "usa" matches "paid to usa inc" but not "usage", and multi-word keywords like
	 * "new york" match only when both words appear adjacent and whole.
	 *
	 * @param {Object} params
	 * @param {String} params.text
	 * @param {String} params.phrase
	 * @returns {Boolean}
	 */
	containsWholePhrase({ text, phrase }) {
		const textTokens = UtilityService.toWordTokens({ text });
		const phraseTokens = UtilityService.toWordTokens({ text: phrase });
		if (phraseTokens.length === 0) return false;

		const lastPossibleStart = textTokens.length - phraseTokens.length;
		for (let startIndex = 0; startIndex <= lastPossibleStart; startIndex++) {
			let everyTokenMatches = true;
			for (let offset = 0; offset < phraseTokens.length; offset++) {
				if (textTokens[startIndex + offset] !== phraseTokens[offset]) {
					everyTokenMatches = false;
					break;
				}
			}
			if (everyTokenMatches) return true;
		}
		return false;
	},

	/**
	 * Finds the tax a keyword rule assigns to a transaction: the first rule whose keyword
	 * appears as a whole word in the description or merchant. Returns null when none match.
	 *
	 * @param {Object} params
	 * @param {Object} params.transaction - needs description and/or merchant
	 * @param {Array<Object>} params.taxRules - each { keyword, tax }
	 * @returns {String|null} the tax to apply, or null
	 */
	findTaxForTransaction({ transaction, taxRules }) {
		const haystack = `${transaction.description || ""} ${transaction.merchant || ""}`;
		const matchingRule = (taxRules || []).find(rule => {
			const keyword = (rule.keyword || "").trim();
			if (!keyword) return false;
			return this.containsWholePhrase({ text: haystack, phrase: keyword });
		});
		return matchingRule ? matchingRule.tax : null;
	},

	/**
	 * Applies the keyword tax rules to transactions that have no tax yet, leaving any
	 * transaction with an existing tax untouched so a manual choice is never overwritten.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Array<Object>} params.taxRules
	 * @returns {Object} { updatedTransactions, changedCount }
	 */
	applyTaxRulesToUntaxed({ transactions, taxRules }) {
		let changedCount = 0;
		const updatedTransactions = (transactions || []).map(transaction => {
			const alreadyHasTax = !!transaction.tax;
			if (alreadyHasTax) return transaction;
			const ruleTax = this.findTaxForTransaction({ transaction, taxRules });
			if (!ruleTax) return transaction;
			changedCount++;
			return { ...transaction, tax: ruleTax };
		});
		return { updatedTransactions, changedCount };
	},

	/**
	 * Fetches the canonical trial balance the AFS is built from, for the client's current
	 * transactions — the accurate, tied-out trial balance, not a frontend approximation.
	 * Returns { trialBalance, metadata } or null on failure.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {Array} params.transactions
	 * @returns {Promise<Object|null>} { trialBalance, metadata } or null
	 */
	async getTrialBalance({ clientId, transactions, priorEntries }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getBookkeepingTrialBalance({ clientId, transactions, priorEntries }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	async getGlOpeningBalanceLines({ clientId }) {
		try {
			let userData = UserService.getUserData();
			let response = await Kichiri.tools.getGlOpeningBalanceLines({ clientId }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			return null;
		}
	},

	/**
	 * Converts imported prior-year GL entries (each already carrying a canonical_code and
	 * a credit-positive cash-flow amount) into canonical postings the trial-balance pipeline
	 * understands: a positive amount is a credit, a negative amount a debit.
	 *
	 * @param {Object} params
	 * @param {Array} params.generalLedgerEntries - each { canonical_code, amount }
	 * @returns {Array<Object>} postings like { canonical_code, debit_credit, amount }
	 */
	buildPriorTrialBalanceEntries({ generalLedgerEntries }) {
		return (generalLedgerEntries || []).map(entry => {
			const cashFlowAmount = Number(entry.amount) || 0;
			const debitCredit = cashFlowAmount >= 0 ? "CREDIT" : "DEBIT";
			return { canonical_code: entry.canonical_code || null, debit_credit: debitCredit, amount: Math.abs(cashFlowAmount) };
		});
	},

	/**
	 * Shapes the canonical trial balance into sorted comparative rows: each account's
	 * current-year net balance beside its prior-year net balance (the same figures the AFS
	 * comparatives use). Includes accounts present in either year and drops rows with no
	 * balance in either.
	 *
	 * @param {Object} params
	 * @param {Object} params.trialBalance - canonical_code → current-year entry
	 * @param {Object} params.priorTrialBalance - canonical_code → prior-year entry
	 * @returns {Array<Object>} rows like { canonicalCode, account, gifiCode, current, prior }
	 */
	buildTrialBalanceRows({ trialBalance, priorTrialBalance }) {
		const currentByCode = trialBalance || {};
		const priorByCode = priorTrialBalance || {};
		const currentCodes = Object.keys(currentByCode);
		const priorCodes = Object.keys(priorByCode);
		const uniqueCodes = [...new Set([...currentCodes, ...priorCodes])];

		const rows = uniqueCodes.map(canonicalCode => {
			const currentEntry = currentByCode[canonicalCode];
			const priorEntry = priorByCode[canonicalCode];
			const current = currentEntry ? (currentEntry.net_balance || 0) : 0;
			const prior = priorEntry ? (priorEntry.net_balance || 0) : 0;
			const account = (currentEntry && currentEntry.display_name) || (priorEntry && priorEntry.display_name) || canonicalCode;
			const gifiCode = (currentEntry && currentEntry.gifi_code) || (priorEntry && priorEntry.gifi_code) || "";
			return { canonicalCode, account, gifiCode, current, prior };
		});

		const rowsWithActivity = rows.filter(row => row.current !== 0 || row.prior !== 0);
		const sortedRows = rowsWithActivity.sort((first, second) => first.account.localeCompare(second.account));
		return sortedRows;
	},

	/**
	 * Pulls the leading account number out of an account name like "6250 - Office supplies".
	 * Falls back to the name truncated to the client-CoA column limit (20) when the name
	 * has no leading number.
	 *
	 * @param {String} accountName
	 * @returns {String}
	 */
	extractAccountNumberFromName(accountName) {
		const trimmedName = UtilityService.toTrimmedText(accountName);
		const leadingDigits = readLeadingDigits({ text: trimmedName });

		const nameHasLeadingNumber = leadingDigits !== "";
		const accountNumber = nameHasLeadingNumber ? leadingDigits.slice(0, 20) : trimmedName.slice(0, 20);
		return accountNumber;
	},

	/**
	 * Returns just the account number embedded at the start of an account name (the leading
	 * digits of "6250 Office Supplies"), or an empty string when the name has no leading
	 * number. Unlike extractAccountNumberFromName it never falls back to the name itself, so
	 * it is safe for a numeric "Account #" column that should stay blank when unknown.
	 *
	 * @param {Object} params
	 * @param {String} params.accountName
	 * @returns {String}
	 */
	getLeadingAccountNumber({ accountName }) {
		const leadingDigits = readLeadingDigits({ text: accountName || "" });
		return leadingDigits;
	},

	/**
	 * Finds the client chart row an account name refers to: the account number the name
	 * starts with, then an exact name, and nothing else.
	 *
	 * This deliberately mirrors accountIdentityService.resolveAccountIdsByLabel on the API.
	 * The two must stay in step, or the browser and the database will disagree about which
	 * account a row belongs to. It uses getLeadingAccountNumber rather than
	 * extractAccountNumberFromName because the latter falls back to returning the name
	 * itself, which would then be compared against real account numbers.
	 *
	 * @param {Object} params
	 * @param {Array} params.clientChart - rows from getClientCoA
	 * @param {String} params.accountName
	 * @returns {Object|null} the chart row, or null when the name refers to no account
	 */
	findChartRowForAccountName({ clientChart, accountName }) {
		const chartRows = clientChart || [];
		if (!accountName || chartRows.length === 0) return null;

		const accountNumber = this.getLeadingAccountNumber({ accountName });
		if (accountNumber) {
			const rowWithThatNumber = chartRows.find(account => String(account.accountNumber || "").trim() === accountNumber);
			if (rowWithThatNumber) return rowWithThatNumber;
		}

		const plainly = text => String(text || "").trim().toLowerCase();
		const rowWithThatName = chartRows.find(account => plainly(account.accountName) === plainly(accountName));
		return rowWithThatName || null;
	},

	/**
	 * The label for one chart account: its number and description, unless the description
	 * already opens with that number.
	 *
	 * The description holds the name and nothing else — the number is stripped off it when the
	 * account is written, so composing is unconditional here. A numberless account keeps its
	 * name in account_number, which is why that is checked before it is prefixed.
	 *
	 * Mirrors coaMappingService.describeAccountName on the API, which labels the same accounts
	 * on the transactions endpoint. The two must agree or the same account reads two ways.
	 *
	 * @param {Object} params
	 * @param {Object} params.account - a row from getClientCoA
	 * @returns {String}
	 */
	describeChartAccount({ account }) {
		const accountNumber = String(account.accountNumber || "").trim();
		const accountName = String(account.accountName || "").trim();

		const numberIsReallyANumber = !!this.getLeadingAccountNumber({ accountName: accountNumber });
		if (!accountNumber || !numberIsReallyANumber) return accountName || accountNumber;
		if (!accountName) return accountNumber;

		return `${accountNumber} ${accountName}`;
	},

	/**
	 * Lists every account the client has, not only the ones the open file posts to.
	 *
	 * A balance-sheet account carries a balance without any transaction classified to it —
	 * 2150 Visa - RBC, 2400 Income taxes payable — so a list scraped from transactions cannot
	 * show it at all, and no lookup fix reaches that. The chart is the record of which
	 * accounts exist; transactions only say which were used.
	 *
	 * A chart row's number is only prefixed onto its name when it really is a number. Some rows
	 * carry the account NAME in account_number — "Interest Income", or "Software Subscriptio"
	 * truncated to the column width — because the importer falls back to the name when a GL has
	 * no numbers. Prefixing those produced "Interest Income Interest Income", which then matched
	 * no chart row and reported a mapped account as unmapped.
	 *
	 * Saved transactions can only name a chart account — account_id is NOT NULL and a foreign
	 * key. But a person can add a classification in the tool and start using it before it is
	 * ever mapped, and that account lives only in the browser until they save. So the chart is
	 * unioned with the names in play, and the two are reconciled.
	 *
	 * Names are deduplicated by the chart row each resolves to, not by the row's id (absent on
	 * some payloads, which would collapse the list into one entry) and not by the string (the
	 * chart's spelling and a transaction's label are two spellings of one account — "2150 Visa
	 * -RBC" and "2150 - Visa -RBC" — which would list it twice, once mapped and once not). A
	 * name resolving to no chart row keeps its own entry: that is the unmapped account, and it
	 * is the one the AFS gate has to stop for.
	 *
	 * @param {Object} params
	 * @param {Array} params.clientChart - rows from getClientCoA
	 * @param {Array<String>} params.usedClassificationNames - names the open file classifies to
	 * @returns {Array<String>} every account name, sorted, one entry per account
	 */
	listEveryClientAccountName({ clientChart, usedClassificationNames }) {
		const chartRows = clientChart || [];
		const namesOnTheChart = chartRows.map(account => this.describeChartAccount({ account })).filter(Boolean);

		const chosenNameForChartRow = new Map();
		const namesWithNoChartRow = [];

		for (const accountName of [...namesOnTheChart, ...(usedClassificationNames || [])]) {
			const chartRow = this.findChartRowForAccountName({ clientChart: chartRows, accountName });
			if (!chartRow) {
				if (!namesWithNoChartRow.includes(accountName)) namesWithNoChartRow.push(accountName);
				continue;
			}
			if (!chosenNameForChartRow.has(chartRow)) chosenNameForChartRow.set(chartRow, accountName);
		}

		const everyAccountName = [...chosenNameForChartRow.values(), ...namesWithNoChartRow];
		return everyAccountName.sort();
	},

	/**
	 * Renames an account across all transactions: every field that carries the old
	 * account name (current, GL label, CaseWare/GL source, AI source) is updated so
	 * the rename doesn't register as a manual reclassification.
	 *
	 * @param {Array} transactions
	 * @param {String} oldName
	 * @param {String} newName
	 * @returns {Array}
	 */
	renameClassificationOnTransactions({ transactions, oldName, newName }) {
		let renameIfMatching = value => (value === oldName ? newName : value);
		const renamedTransactions = transactions.map(transaction => ({
			...transaction,
			classification: renameIfMatching(transaction.classification),
			classification_label: renameIfMatching(transaction.classification_label),
			cwClassification: renameIfMatching(transaction.cwClassification),
			aiClassification: renameIfMatching(transaction.aiClassification),
		}));
		return renamedTransactions;
	},

	/**
	 * Maps every transaction classified under an account name to a canonical FS account
	 * (canonical code, display label, GIFI); other transactions are untouched.
	 *
	 * @param {Array} transactions
	 * @param {String} classificationName
	 * @param {Object} canonicalNode - { canonical_code, display_name, gifi_code }
	 * @returns {Array}
	 */
	remapTransactionsToCanonical({ transactions, classificationName, canonicalNode }) {
		const remappedTransactions = transactions.map(transaction => {
			if (transaction.classification !== classificationName) return transaction;
			return {
				...transaction,
				canonical_code: canonicalNode.canonical_code,
				canonical_label: canonicalNode.display_name || canonicalNode.name || canonicalNode.canonical_code,
				gifi_code: canonicalNode.gifi_code || null,
			};
		});
		return remappedTransactions;
	},

	/**
	 * Reads a general ledger CSV and returns its headers, data rows, and a best-guess
	 * column map. Account and description drive the NLP match; date/debit/credit/tax are
	 * captured so the user can confirm the mapping of their own export.
	 *
	 * @param {File} file
	 * @returns {Promise<Object|null>} { headers, rows, columnMap } or null when there are no data rows
	 */
	async readGeneralLedgerCsv({ file }) {
		let text = await file.text();
		let matrix = UtilityService.parseCsv(text);
		if (matrix.length < 2) return null;

		let headers = matrix[0];
		let rows = matrix.slice(1);
		let columnMap = {
			account: UtilityService.guessColumnIndex(headers, ["account", "acct", "gl", "category"]),
			description: UtilityService.guessColumnIndex(headers, ["description", "memo", "payee", "name", "detail"]),
			date: UtilityService.guessColumnIndex(headers, ["date"]),
			debit: UtilityService.guessColumnIndex(headers, ["debit", "dr"]),
			credit: UtilityService.guessColumnIndex(headers, ["credit", "cr"]),
			tax: UtilityService.guessColumnIndex(headers, ["tax", "hst", "gst"]),
		};

		return { headers, rows, columnMap };
	},

	/**
	 * Reads a bank statement into a grid of text cells. CSVs are parsed in the browser;
	 * Excel statements are read on the API, where the spreadsheet library lives. The grid
	 * still holds every row, including any heading row, so the caller can decide what the
	 * first row is.
	 *
	 * @param {Object} params
	 * @param {Number} params.clientId
	 * @param {File} params.file - A .csv or .xlsx bank statement
	 * @returns {Promise<Object>} { grid } of string arrays, or { error } describing why it could not be read
	 */
	async readStatementGrid({ clientId, file }) {
		const isExcelStatement = UtilityService.fileNameEndsWith({ fileName: file.name, suffix: ".xlsx" });

		if (!isExcelStatement) {
			const text = await file.text();
			const grid = UtilityService.parseCsv(text);
			return { grid };
		}

		try {
			const formData = new FormData();
			formData.append("files", file);
			const response = await authedFetch(`/tools/bookkeeping/parse_spreadsheet?clientId=${clientId}`, { method: "POST", body: formData });
			const answer = await readJsonAnswer({ response });
			if (answer.error) return answer;
			return { grid: answer.rows || [] };
		} catch (error) {
			console.log(error);
			return { error: COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Decides whether a statement's first row holds column headings rather than a
	 * transaction, by reading the cells rather than matching known heading words: a
	 * heading row has no cell that reads as a date and none that reads as an amount,
	 * while a later row does. Statements with no heading row keep every row as data and
	 * get blank headings, which the column picker shows as "Column 1", "Column 2", ….
	 *
	 * @param {Object} params
	 * @param {Array} params.grid - Rows of text cells
	 * @returns {Object} { hasHeadings, headers, dataRows }
	 */
	detectStatementHeadings({ grid }) {
		const rows = grid || [];
		if (rows.length === 0) return { hasHeadings: false, headers: [], dataRows: [] };

		const firstRow = rows[0];
		const laterRows = rows.slice(1);
		const cellIsDate = cell => normalizeDate(cell) !== null;
		const cellIsAmount = cell => !isNaN(normalizeAmount(cell));

		const firstRowHasNoDate = !firstRow.some(cellIsDate);
		const firstRowHasNoAmount = !firstRow.some(cellIsAmount);
		const firstRowReadsAsLabels = firstRowHasNoDate && firstRowHasNoAmount;
		const someLaterRowReadsAsData = laterRows.some(row => row.some(cellIsDate) || row.some(cellIsAmount));
		const hasHeadings = firstRowReadsAsLabels && someLaterRowReadsAsData;

		const blankHeadings = firstRow.map(() => "");
		const headers = hasHeadings ? firstRow : blankHeadings;
		const dataRows = hasHeadings ? laterRows : rows;
		return { hasHeadings, headers, dataRows };
	},

	/**
	 * Best-guess column map for a bank statement. When the file has headings the guess
	 * comes from the heading words; otherwise it comes from the data itself — the column
	 * whose cells most often read as dates is the date, the longest text column is the
	 * description, and the remaining numeric column is the amount. Every guess is only a
	 * starting point for the user to confirm.
	 *
	 * @param {Object} params
	 * @param {Array} params.headers
	 * @param {Array} params.dataRows
	 * @param {Boolean} params.hasHeadings
	 * @returns {Object} { date, description, amount, moneyOut, moneyIn } column indexes, -1 when unmapped
	 */
	guessStatementColumnMap({ headers, dataRows, hasHeadings }) {
		if (hasHeadings) {
			const columnMap = {
				date: UtilityService.guessColumnIndex(headers, ["date", "posted", "transaction date", "post date"]),
				description: UtilityService.guessColumnIndex(headers, ["description", "details", "memo", "payee", "narrative", "particulars", "transaction"]),
				amount: UtilityService.guessColumnIndex(headers, ["amount", "value"]),
				moneyOut: UtilityService.guessColumnIndex(headers, ["withdrawal", "withdrawals", "debit", "paid out", "money out", "payment"]),
				moneyIn: UtilityService.guessColumnIndex(headers, ["deposit", "deposits", "credit", "paid in", "money in", "receipt"]),
			};
			return columnMap;
		}

		const columnCount = dataRows.reduce((widest, row) => Math.max(widest, row.length), 0);
		const columnIndexes = Array.from({ length: columnCount }, (unused, index) => index);
		const countMatching = (columnIndex, matches) => dataRows.filter(row => matches(row[columnIndex])).length;
		const dateCounts = columnIndexes.map(columnIndex => countMatching(columnIndex, cell => normalizeDate(cell) !== null));
		const amountCounts = columnIndexes.map(columnIndex => countMatching(columnIndex, cell => !isNaN(normalizeAmount(cell))));
		const textLengths = columnIndexes.map(columnIndex => dataRows.reduce((total, row) => total + String(row[columnIndex] || "").length, 0));

		const bestIndex = (scores, excluded) => {
			let winningIndex = -1;
			let winningScore = 0;
			scores.forEach((score, index) => {
				if (excluded.includes(index)) return;
				if (score > winningScore) {
					winningScore = score;
					winningIndex = index;
				}
			});
			return winningIndex;
		};

		const dateColumn = bestIndex(dateCounts, []);
		const amountColumn = bestIndex(amountCounts, [dateColumn]);
		const descriptionColumn = bestIndex(textLengths, [dateColumn, amountColumn]);
		return { date: dateColumn, description: descriptionColumn, amount: amountColumn, moneyOut: -1, moneyIn: -1 };
	},

	/**
	 * Turns mapped statement rows into transactions. The amount comes either from one
	 * signed column or from a money-out / money-in pair, where money in is positive and
	 * money out is negative — the direction the rest of the pipeline expects, and the
	 * reason a mapped pair needs no sign confirmation. Rows whose date or amount cannot
	 * be read are still returned, and counted, so nothing is silently dropped.
	 *
	 * @param {Object} params
	 * @param {Array} params.dataRows
	 * @param {Object} params.columnMap - { date, description, amount, moneyOut, moneyIn }
	 * @param {String} params.bankAccountName
	 * @param {String} [params.bankAccountNumber]
	 * @returns {Object} { transactions, invalidCount }
	 */
	extractTransactionsFromStatementGrid({ dataRows, columnMap, bankAccountName, bankAccountNumber = null }) {
		const cellAt = (row, columnIndex) => (columnIndex >= 0 ? row[columnIndex] || "" : "");
		const usesMoneyOutAndIn = columnMap.moneyOut >= 0 || columnMap.moneyIn >= 0;

		const amountForRow = row => {
			if (!usesMoneyOutAndIn) return normalizeAmount(cellAt(row, columnMap.amount));
			const moneyOut = normalizeAmount(cellAt(row, columnMap.moneyOut));
			const moneyIn = normalizeAmount(cellAt(row, columnMap.moneyIn));
			const hasNeither = isNaN(moneyOut) && isNaN(moneyIn);
			if (hasNeither) return NaN;
			const moneyOutOrZero = isNaN(moneyOut) ? 0 : Math.abs(moneyOut);
			const moneyInOrZero = isNaN(moneyIn) ? 0 : Math.abs(moneyIn);
			return moneyInOrZero - moneyOutOrZero;
		};

		let invalidCount = 0;
		const transactions = dataRows.map(row => {
			const rawDate = cellAt(row, columnMap.date);
			const date = normalizeDate(rawDate) || rawDate;
			const description = cellAt(row, columnMap.description);
			const amount = amountForRow(row);

			const hasInvalidDate = !normalizeDate(rawDate);
			const hasInvalidAmount = isNaN(amount);
			if (hasInvalidDate || hasInvalidAmount) invalidCount++;

			return { date, description, merchant: "", amount, bankAccountName, bankAccountNumber };
		});

		return { transactions, invalidCount };
	},

	/**
	 * Stamps NLP-matched general ledger accounts onto transactions (index-aligned with the
	 * categorized results); unmatched rows keep their existing classification. When the GL
	 * derived a default tax code for the matched account, it is applied unless the
	 * transaction already carries a tax code.
	 *
	 * A match also carries the account's FS mapping across. Matching only set the display
	 * string before, which left every GL-matched row showing an account in the Classification
	 * column and an em dash under FS Account — and left a stale canonical_code from an earlier
	 * classification sitting underneath a name that no longer agreed with it. The chart is
	 * what says which FS account an account maps to, so the mapping is read from there; an
	 * account absent from the chart keeps whatever the transaction already carried rather than
	 * being blanked.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Array} params.categorized - [{ mappedAccount }] index-aligned to transactions
	 * @param {Object} [params.accountTaxes] - { [account]: taxCode } defaults derived from the GL
	 * @param {Array} [params.clientChart] - rows from getClientCoA, the record of FS mappings
	 * @param {Array} [params.canonicalTaxonomyOptions] - canonical nodes, for the display name
	 * @returns {Array}
	 */
	stampGeneralLedgerCategories({ transactions, categorized, accountTaxes = {}, clientChart = [], canonicalTaxonomyOptions = [] }) {
		let isUsableAccount = account => account && account !== "Uncategorized" && account !== "Needs Review";
		const stampedTransactions = transactions.map((transaction, index) => {
			let mappedAccount = categorized[index] ? categorized[index].mappedAccount : null;
			let matched = isUsableAccount(mappedAccount);

			const chartRow = matched ? this.findChartRowForAccountName({ clientChart, accountName: mappedAccount }) : null;
			const mappedCanonicalCode = chartRow && chartRow.canonical_code ? chartRow.canonical_code : null;
			const canonicalNode = mappedCanonicalCode ? canonicalTaxonomyOptions.find(option => option.canonical_code === mappedCanonicalCode) : null;

			return {
				...transaction,
				classification: matched ? mappedAccount : transaction.classification,
				classification_label: matched ? mappedAccount : (transaction.classification_label || null),
				classification_method: matched ? "gl_reference" : transaction.classification_method,
				canonical_code: mappedCanonicalCode || transaction.canonical_code || null,
				canonical_label: canonicalNode ? canonicalNode.display_name : (mappedCanonicalCode ? "" : transaction.canonical_label || null),
				gifi_code: mappedCanonicalCode ? (chartRow.gifi_code || (canonicalNode ? canonicalNode.gifi_code : null)) : (transaction.gifi_code || null),
				tax: matched ? (transaction.tax || accountTaxes[mappedAccount] || "") : transaction.tax,
				cwClassification: matched ? mappedAccount : null,
				cwClassified: matched,
				glClassified: matched,
				source_evidence: matched
					? this.describeGeneralLedgerClassification({
						account: mappedAccount,
						similarity: categorized[index] ? categorized[index].score : null,
						matchedLedgerDescription: categorized[index] ? categorized[index].matchedLedgerDescription : null,
					})
					: transaction.source_evidence || null,
			};
		});
		return stampedTransactions;
	},



	/**
	 * Calculates the tax amount using the tax-inclusive formula.
	 * Formula: taxAmount = (amount × rate) / (100 + rate)
	 *
	 * @param {Number} amount
	 * @param {String} tax - Tax rate string (e.g. "HST 13%")
	 * @param {Boolean} hasTax
	 * @returns {Number} Calculated tax amount
	 */
	calculateTaxAmount({ amount, tax, hasTax }) {
		if (!hasTax) return 0.0;
		if (!tax && tax !== 0) return 0.0;

		let taxRate = extractTaxRateFromString(tax);
		if (taxRate === null) return 0.0;

		let parsedAmount = parseFloat(amount);
		if (!isFinite(parsedAmount) || isNaN(parsedAmount)) return 0.0;

		let taxAmount = (parsedAmount * taxRate) / (100 + taxRate);
		return parseFloat(taxAmount.toFixed(2));
	},


	/**
	 * Maps CaseWare categorized accounts to their full display names.
	 * Returns "Unclassified" for accounts not found in the trial balance.
	 *
	 * @param {Array} casewareCategorizedAccounts
	 * @param {Array} priorYearTrialBalanceExtract
	 * @returns {Array} Array of strings like "12000 - Accounts Receivable"
	 */
	mapAccountDescriptionToAccount({ casewareCategorizedAccounts, priorYearTrialBalanceExtract }) {
		let descriptions = [];

		for (let categorizedAccount of casewareCategorizedAccounts) {
			let mappedAccountNumber = categorizedAccount.mappedAccount;

			let matchingAccount = null;
			for (let trialBalanceItem of priorYearTrialBalanceExtract) {
				if (trialBalanceItem.accountNumber === mappedAccountNumber) {
					matchingAccount = trialBalanceItem;
					break;
				}
			}

			if (!matchingAccount) {
				descriptions.push("Unclassified");
				continue;
			}

			descriptions.push(buildFullAccountDescription(matchingAccount.accountNumber, matchingAccount.accountDescription));
		}

		return descriptions;
	},


	/**
	 * Separates transactions into the ones that still need an account and the ones that do not.
	 *
	 * This decides what the AI is asked to classify, so it asks the same question the Unclassified
	 * tab and the counters ask. A transfer leg waiting on the Transfers tab is not work for the
	 * model: asking anyway pays for a guess and then writes an account onto a row whose whole
	 * point is that it must not carry one until the pair is approved or rejected.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Object} { classifiedTransactions, unclassifiedTransactions }
	 */
	getClassifiedAndUnclassifiedTransactions({ transactions }) {
		const unclassifiedTransactions = this.listTransactionsStillNeedingAnAccount({ transactions });
		const stillWaiting = new Set(unclassifiedTransactions);
		const classifiedTransactions = (transactions || []).filter(transaction => !stillWaiting.has(transaction));

		return { classifiedTransactions, unclassifiedTransactions };
	},


	/**
	 * Merges AI classification results with transactions and fetches tax rates.
	 *
	 * @param {Array} unclassifiedTransactions
	 * @param {Array} openAiClassifications - AI results parallel to unclassifiedTransactions
	 * @param {Array} classifiedTransactions - Already classified (CaseWare or manual)
	 * @param {String} province
	 * @param {String} industry
	 * @returns {Promise<Object>} { openAiAccountsTaxClassifications, updatedTransactions }
	 */
	async getOpenAiAccountsTaxClassifications({ unclassifiedTransactions, openAiClassifications, classifiedTransactions, province, industry }) {
		let aiClassifiedTransactions = this.applyAiClassifications({ unclassifiedTransactions, aiClassifications: openAiClassifications });
		let allTransactions = [...classifiedTransactions, ...aiClassifiedTransactions];
		let uniqueAccountNames = this.getUniqueClassifiedAccountNames({ transactions: allTransactions });
		let accountTaxRates = await this.fetchTaxRatesForAccounts({ accountNames: uniqueAccountNames, province, industry });

		return {
			openAiAccountsTaxClassifications: accountTaxRates,
			updatedTransactions: allTransactions,
		};
	},

	/**
	 * Applies AI classification results to unclassified transactions.
	 *
	 * @param {Array} unclassifiedTransactions
	 * @param {Array} aiClassifications
	 * @returns {Array} Transactions with AI classifications applied
	 */
	applyAiClassifications({ unclassifiedTransactions, aiClassifications }) {
		let results = [];

		for (let transactionIndex = 0; transactionIndex < unclassifiedTransactions.length; transactionIndex++) {
			let transaction = unclassifiedTransactions[transactionIndex];
			let aiResult = (aiClassifications && aiClassifications[transactionIndex]) || {};

			results.push({
				...transaction,
				classification: aiResult.classification || "Unclassified",
				tax: aiResult.tax || "",
				confidence: normalizeConfidence(aiResult.confidence),
			});
		}

		return results;
	},

	/**
	 * Extracts unique classified account names from transactions.
	 *
	 * @param {Array} transactions
	 * @returns {Array} Unique account name strings
	 */
	getUniqueClassifiedAccountNames({ transactions }) {
		let uniqueNames = new Set();

		for (let transaction of transactions) {
			if (isUnclassified(transaction.classification)) continue;
			uniqueNames.add(transaction.classification);
		}

		const accountNames = Array.from(uniqueNames);
		return accountNames;
	},

	/**
	 * Fetches tax rates for a list of account names.
	 *
	 * @param {Array} accountNames
	 * @param {String} province
	 * @param {String} industry
	 * @returns {Promise<Array>} Array of { account, taxRate }
	 */
	async fetchTaxRatesForAccounts({ accountNames, province, industry }) {
		let promises = [];

		for (let accountName of accountNames) {
			promises.push(
				this.getTaxRates({ account: accountName, province, industry }).then(function (taxRate) {
					return { account: accountName, taxRate };
				})
			);
		}

		return await Promise.all(promises);
	},


	/**
	 * Clears a free-text classification that names no account.
	 *
	 * This used to classify by keyword before the model was asked: a cheque went to the clearing
	 * account, anything matching "receiver general" went to Payroll Source Deductions, and any
	 * other government payment went to Income Tax Payable — real accounts, chosen by a regular
	 * expression, at a confidence of 0.80 and marked as a known payee pattern. It was a rule
	 * engine in the browser, and it survived the removal of the one on the server. A description
	 * that merely mentions a government body is not evidence of which account it belongs in, and
	 * a remittance filed to income tax when it was payroll misstates two liabilities at once.
	 *
	 * Those rows are now unclassified, which is what they are, and the model is asked about them
	 * like everything else.
	 *
	 * It also used to file both legs of every transfer it spotted into the clearing account before
	 * anyone had approved them. Detecting a transfer is a proposal, and a proposal is not a place
	 * to put money: a leg parked in suspense on the strength of a guess is indistinguishable from
	 * one a preparer settled there, and the server strips a decision that names no real account, so
	 * the marking did not survive the trip anyway. Proposals are worked out live for the Transfers
	 * tab and recorded only when approved.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array}
	 */
	clearClassificationsThatNameNoAccount({ transactions }) {
		const results = [];

		for (const transaction of transactions || []) {
			const namesAnAccountThatDoesNotExist = !transaction.canonical_code && !transaction.glClassified && !isUnclassified(transaction.classification);
			if (namesAnAccountThatDoesNotExist) {
				results.push({ ...transaction, classification: null });
				continue;
			}
			results.push(transaction);
		}

		return results;
	},


	/**
	 * The transfer pairs a preparer already approved, read from the key the server stored rather
	 * than worked out again.
	 *
	 * Approving a transfer writes mirrors_transaction_id on both legs, so which two rows are the
	 * pair is a recorded fact. Joining on it finds them every time, whatever the rows look like
	 * afterwards.
	 *
	 * They used to be re-derived on every load from the amounts, the dates and the two bank
	 * account names, which found a settled pair back only while it still resembled one. A card
	 * payment leaving the chequing account three days before it reached the card fell outside the
	 * date tolerance, so the pair was never rebuilt: both legs left the Transfers tab, and because
	 * approving had given them a real account they were not in Unclassified either. The transfer
	 * looked like it had disappeared.
	 *
	 * Every settled leg is claimed here even when its counterpart is not in the file — an archived
	 * counterpart, say — so that the guesswork below can never pair a settled leg with a stranger.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Object} { pairs, legsAlreadySettled }
	 */
	readTransferPairsTheServerRecorded({ transactions }) {
		let transactionsById = new Map();
		for (let transaction of transactions) {
			if (transaction.id !== undefined && transaction.id !== null) transactionsById.set(String(transaction.id), transaction);
		}

		let pairs = [];
		let legsAlreadySettled = new Set();

		for (let transaction of transactions) {
			if (!transaction.mirrors_transaction_id) continue;
			if (legsAlreadySettled.has(transaction)) continue;
			legsAlreadySettled.add(transaction);

			let counterpart = transactionsById.get(String(transaction.mirrors_transaction_id));
			if (!counterpart || counterpart === transaction) continue;
			if (legsAlreadySettled.has(counterpart) && counterpart.mirrors_transaction_id) {
				let counterpartIsAlreadyInAPair = pairs.some(pair => pair.outLeg === counterpart || pair.inLeg === counterpart);
				if (counterpartIsAlreadyInAPair) continue;
			}

			legsAlreadySettled.add(counterpart);
			pairs.push(buildTransferPairSummary({ transactionA: transaction, transactionB: counterpart }));
		}

		return { pairs, legsAlreadySettled };
	},

	/**
	 * Finds every inter-account transfer pair for the Transfers review — the pairs already
	 * approved, read from the server's own record, followed by the pairs still being proposed.
	 * Each transaction is paired at most once.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @returns {Array} pairs of { outLeg, inLeg, cadValue, isCrossCurrency, status }
	 */
	getTransferPairs({ transactions }) {
		let { pairs, legsAlreadySettled } = this.readTransferPairsTheServerRecorded({ transactions });
		let usedTransactions = legsAlreadySettled;

		for (let sourceIndex = 0; sourceIndex < transactions.length; sourceIndex++) {
			let transactionA = transactions[sourceIndex];
			if (usedTransactions.has(transactionA)) continue;
			if (!isTransferPairCandidate({ transaction: transactionA })) continue;

			for (let candidateIndex = sourceIndex + 1; candidateIndex < transactions.length; candidateIndex++) {
				let transactionB = transactions[candidateIndex];
				if (usedTransactions.has(transactionB)) continue;
				if (!isTransferPairCandidate({ transaction: transactionB })) continue;
				if (transferAccountName({ transaction: transactionA }) === transferAccountName({ transaction: transactionB })) continue;
				if (!transferLegsMatchInCad({ transactionA, transactionB, allowCrossCurrency: true })) continue;
				if (!datesAreWithinTolerance(transactionA.date, transactionB.date)) continue;

				usedTransactions.add(transactionA);
				usedTransactions.add(transactionB);
				pairs.push(buildTransferPairSummary({ transactionA, transactionB }));
				break;
			}
		}

		return pairs;
	},

	/**
	 * Reverses each transfer pair back to an unclassified transaction, so it flows through
	 * normal classification again. Returns a new transactions array with the legs cleared.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Array} params.pairs - pairs from getTransferPairs
	 * @returns {Array}
	 */
	reverseTransferPairs({ transactions, pairs }) {
		let legsToReverse = new Set();
		for (let pair of pairs) {
			legsToReverse.add(pair.outLeg);
			legsToReverse.add(pair.inLeg);
		}

		let updatedTransactions = transactions.map(transaction => {
			if (!legsToReverse.has(transaction)) return transaction;
			return { ...transaction, classification: "Unclassified", isTransfer: false, canonical_code: null, canonical_label: null, classification_method: null, confidence: null };
		});

		return updatedTransactions;
	},

	/**
	 * Validates whether two transactions can be manually marked as a transfer pair.
	 * Requires two different accounts and opposite signs. A same-currency pair must
	 * match to the cent — a difference there is a fee or a data error, so it is
	 * surfaced (invalid) rather than silently absorbed. A cross-currency pair is
	 * allowed regardless of the difference, which is the realized FX.
	 *
	 * @param {Object} params
	 * @param {Object} params.transactionA
	 * @param {Object} params.transactionB
	 * @returns {Object} { isValid, error, isCrossCurrency, differenceCad }
	 */
	getManualTransferValidation({ transactionA, transactionB }) {
		const accountA = transferAccountName({ transaction: transactionA });
		const accountB = transferAccountName({ transaction: transactionB });
		if (!accountA || !accountB) return { isValid: false, error: "Both transactions need a bank account." };
		if (accountA === accountB) return { isValid: false, error: "A transfer must be between two different accounts." };

		const cadA = cadEquivalentAmount({ transaction: transactionA });
		const cadB = cadEquivalentAmount({ transaction: transactionB });
		if (Math.sign(cadA) === Math.sign(cadB)) return { isValid: false, error: "A transfer needs one outgoing and one incoming transaction (opposite signs)." };

		const isCrossCurrency = (transactionA.currency || "CAD") !== (transactionB.currency || "CAD");
		const differenceCad = Math.abs(cadA + cadB);

		if (!isCrossCurrency && differenceCad > AMOUNT_TOLERANCE) {
			const differenceText = differenceCad.toFixed(2);
			return { isValid: false, error: `The amounts differ by CAD ${differenceText}. A same-currency transfer should match to the cent — if that difference is a bank fee, record it as its own Bank Charges transaction; if a leg is wrong, fix it first.` };
		}

		if (isCrossCurrency) {
			const largerCadValue = Math.max(Math.abs(cadA), Math.abs(cadB));
			const allowedDifference = largerCadValue * CROSS_CURRENCY_TOLERANCE_RATE;
			if (differenceCad > allowedDifference) {
				const differenceText = differenceCad.toFixed(2);
				return { isValid: false, error: `The CAD-equivalent amounts differ by CAD ${differenceText}, more than the ~3% an exchange-rate spread explains. Check the amounts and that each account's currency is set correctly — this may not be a matching transfer.` };
			}
		}

		return { isValid: true, error: null, isCrossCurrency, differenceCad };
	},

	/**
	 * Whether a pair can be recorded, and why not when it cannot.
	 *
	 * Recording names each leg by the other's transaction id, so a row the server has never seen
	 * cannot be half of a pair. The Transfers tab and the hand-marking modal both ask this, so a
	 * preparer gets one answer in one wording wherever they happen to be standing.
	 *
	 * @param {Object} params
	 * @param {Object} params.pair
	 * @returns {Object} { isRecordable, error }
	 */
	checkTransferPairIsRecordable({ pair }) {
		const outLegIsSaved = !!(pair && pair.outLeg && pair.outLeg.id);
		const inLegIsSaved = !!(pair && pair.inLeg && pair.inLeg.id);
		if (!outLegIsSaved || !inLegIsSaved) return { isRecordable: false, error: "A leg has not been saved yet — save the import first." };

		return { isRecordable: true, error: null };
	},

	/**
	 * The pair a preparer picked by hand, ordered and ready to record, or the reason it cannot be.
	 *
	 * Which leg is the outgoing one follows from the sign of the amount, and it is decided here by
	 * the same function that orders a detected pair — a hand-marked transfer and a detected one are
	 * the same thing once recorded, so they cannot be allowed to order themselves differently. That
	 * decision briefly lived in the table instead, where it had already drifted into a second
	 * spelling of the same test.
	 *
	 * @param {Object} params
	 * @param {Object} params.transactionA
	 * @param {Object} params.transactionB
	 * @returns {Object} { pair, error }
	 */
	buildManualTransferPair({ transactionA, transactionB }) {
		const validation = this.getManualTransferValidation({ transactionA, transactionB });
		if (!validation.isValid) return { pair: null, error: validation.error };

		const pair = buildTransferPairSummary({ transactionA, transactionB });
		const recordable = this.checkTransferPairIsRecordable({ pair });
		if (!recordable.isRecordable) return { pair: null, error: recordable.error };

		return { pair, error: null };
	},

	/**
	 * Normalizes a canonical account code to its top statement category. INCOME folds
	 * into REVENUE; anything unrecognized returns OTHER.
	 *
	 * @param {Object} params
	 * @param {String} params.canonicalCode - dotted canonical code (may be empty)
	 * @returns {String} REVENUE | COGS | EXPENSE | ASSET | LIABILITY | EQUITY | OTHER
	 */
	canonicalTopCategory({ canonicalCode }) {
		const topSegment = (canonicalCode || "").split(".")[0].toUpperCase();
		if (topSegment === "REVENUE" || topSegment === "INCOME") return "REVENUE";
		if (topSegment === "COGS") return "COGS";
		if (topSegment === "EXPENSE") return "EXPENSE";
		if (topSegment === "ASSET") return "ASSET";
		if (topSegment === "LIABILITY") return "LIABILITY";
		if (topSegment === "EQUITY") return "EQUITY";
		return "OTHER";
	},

	/**
	 * Infers an account's nature from its name when no canonical mapping exists yet.
	 * A display-only fallback (never books anything): expense phrases are tested first
	 * because some contain "sales" (e.g. "cost of sales") and must not read as revenue.
	 *
	 * @param {Object} params
	 * @param {String} params.accountName
	 * @returns {String|null} "revenue" | "expense" | null
	 */
	accountNatureFromName({ accountName }) {
		const lowerName = (accountName || "").toLowerCase();
		const expensePhrases = ["cost of sales", "cost of goods", "cogs", "expense", "wage", "salar", "payroll", "rent", "utilit", "subscription", "insurance", "depreciat", "amortiz", "supplies", "advertis", "bank charge", "interest expense", "meals", "repairs"];
		const matchesExpense = expensePhrases.some(phrase => lowerName.includes(phrase));
		if (matchesExpense) return "expense";
		const revenuePhrases = ["revenue", "sales", "income", "turnover", "fees earned"];
		const matchesRevenue = revenuePhrases.some(phrase => lowerName.includes(phrase));
		if (matchesRevenue) return "revenue";
		return null;
	},

	/**
	 * Coarse account nature for the year-over-year favourability colouring: revenue vs
	 * expense vs balance-sheet. Uses the canonical mapping when present, else falls back
	 * to the account name so the review still colours income-statement lines pre-mapping.
	 *
	 * @param {Object} params
	 * @param {String} params.canonicalCode
	 * @param {String} params.accountName
	 * @returns {String|null} "revenue" | "expense" | "balance" | null
	 */
	getAccountNature({ canonicalCode, accountName }) {
		const category = this.canonicalTopCategory({ canonicalCode });
		if (category === "REVENUE") return "revenue";
		if (category === "COGS" || category === "EXPENSE") return "expense";
		if (category === "ASSET" || category === "LIABILITY" || category === "EQUITY") return "balance";
		return this.accountNatureFromName({ accountName });
	},

	/**
	 * Ranks the transactions that could be the other leg of a manual transfer for a
	 * given leg: opposite sign, a different account, best amount match first, then
	 * nearest date. Used to suggest the counterpart in the manual-transfer modal.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Object} params.leg - the already-chosen leg
	 * @returns {Array} candidate transactions, best match first
	 */
	getManualTransferCandidates({ transactions, leg }) {
		if (!leg) return [];
		const legCad = cadEquivalentAmount({ transaction: leg });
		const legAccount = transferAccountName({ transaction: leg });

		const eligibleCandidates = (transactions || []).filter(candidate => {
			if (candidate === leg) return false;
			const candidateCad = cadEquivalentAmount({ transaction: candidate });
			if (!candidateCad) return false;
			if (Math.sign(candidateCad) === Math.sign(legCad)) return false;
			if (transferAccountName({ transaction: candidate }) === legAccount) return false;
			return true;
		});

		const scoredCandidates = eligibleCandidates.map(candidate => {
			const candidateCad = cadEquivalentAmount({ transaction: candidate });
			const amountDifference = Math.abs(Math.abs(candidateCad) - Math.abs(legCad));
			const dayDifference = Math.abs((new Date(candidate.date) - new Date(leg.date)) / 86400000);
			return { candidate, amountDifference, dayDifference };
		});

		scoredCandidates.sort((a, b) => {
			if (a.amountDifference !== b.amountDifference) return a.amountDifference - b.amountDifference;
			return a.dayDifference - b.dayDifference;
		});

		return scoredCandidates.map(entry => entry.candidate);
	},

	/**
	 * Dismisses each proposed pair as "not a transfer", so both legs drop out of the
	 * Transfers review and are never proposed again. The legs stay unclassified and flow
	 * through normal classification. Returns a new transactions array with the legs flagged.
	 *
	 * @param {Object} params
	 * @param {Array} params.transactions
	 * @param {Array} params.pairs - pairs from getTransferPairs
	 * @returns {Array}
	 */
	rejectTransferPairs({ transactions, pairs }) {
		let legsToReject = new Set();
		for (let pair of pairs) {
			legsToReject.add(pair.outLeg);
			legsToReject.add(pair.inLeg);
		}

		let updatedTransactions = transactions.map(transaction => {
			if (!legsToReject.has(transaction)) return transaction;
			return { ...transaction, notATransfer: true, isTransfer: false };
		});

		return updatedTransactions;
	},

	/**
	 * Gets the unique bank account names from transactions, sorted. Reads whichever
	 * field the transaction shape carries: bankAccountName (import rows) or
	 * accountName (table rows).
	 *
	 * @param {Array} transactions
	 * @returns {Array} Unique bank account names
	 */
	getUniqueBankAccountNames({ transactions }) {
		let names = new Set();

		for (let transaction of transactions || []) {
			let bankName = transaction.bankAccountName || transaction.accountName;
			if (bankName) {
				names.add(bankName);
			}
		}

		const uniqueNames = Array.from(names);
		const sortedNames = uniqueNames.sort();
		return sortedNames;
	},



};

export default BookkeepingService;
